export interface FundamentalsConcept {
  title: string;
  description: string;
  tags?: string[];
}

export interface CodeExample {
  title: string;
  description: string;
  code: string;
  language: string;
}

export interface InterviewQA {
  question: string;
  answer: string;
  difficulty: 'easy' | 'medium' | 'hard';
}

export interface QuizQuestion {
  question: string;
  options: string[];
  correctIndex: number;
  explanation: string;
}

export interface InterviewExercise {
  title: string;
  difficulty: 'easy' | 'medium' | 'hard';
  description: string;
  hints: string[];
  tags: string[];
}

export type FundamentalCategory = 'frontend' | 'backend' | 'languages' | 'cloud' | 'architecture' | 'ai';

export interface Fundamental {
  slug: string;
  title: string;
  shortDescription: string;
  icon: string;
  color: string;
  category: FundamentalCategory;
  concepts: FundamentalsConcept[];
  useCases: FundamentalsConcept[];
  interviewQA: InterviewQA[];
  codeExamples: CodeExample[];
  quiz: QuizQuestion[];
  exercises: InterviewExercise[];
}

export const FUNDAMENTALS: Fundamental[] = [
  {
    slug: 'react',
    title: 'React',
    shortDescription: 'Librería de UI declarativa basada en componentes y Virtual DOM.',
    icon: 'hub',
    color: 'text-sky-400',
    category: 'frontend',
    concepts: [
      {
        title: 'JSX',
        description:
          'JSX es una extensión de sintaxis de JavaScript que permite escribir markup similar a HTML dentro de código JS. React transforma JSX a llamadas de React.createElement en tiempo de compilación (Babel/SWC). JSX no es HTML: los atributos usan camelCase (className, onClick), las expresiones van entre llaves {}.',
        tags: ['Sintaxis', 'Compilación'],
      },
      {
        title: 'Componentes y Props',
        description:
          'Los componentes son funciones (o clases) que reciben props como argumento y retornan JSX. Las props son de solo lectura: un componente nunca debe mutar sus propias props. Puedes tipar props con TypeScript usando interfaces o type aliases. Los componentes siguen el principio de responsabilidad única.',
        tags: ['Arquitectura', 'TypeScript'],
      },
      {
        title: 'Estado con useState',
        description:
          'useState es el hook fundamental para estado local. Retorna un par [valor, setter]. El setter puede recibir un nuevo valor o una función actualizadora (prev => newVal). Las actualizaciones de estado son asíncronas y se agrupan (batching). Nunca mutes el estado directamente.',
        tags: ['Hooks', 'Estado'],
      },
      {
        title: 'Efectos con useEffect',
        description:
          'useEffect sincroniza un componente con sistemas externos (APIs, DOM, subscripciones). El array de dependencias controla cuándo se ejecuta el efecto. Sin array: cada render. Array vacío []: solo al montar. Con deps: cuando alguna cambia. La función de cleanup se ejecuta antes del siguiente efecto y al desmontar.',
        tags: ['Hooks', 'Ciclo de vida'],
      },
      {
        title: 'Contexto con useContext',
        description:
          'useContext permite consumir valores de un Context sin prop drilling. Se crea con React.createContext(defaultValue), se provee con <Context.Provider value={...}> y se consume con useContext(Context). No es un reemplazo de estado global (no optimiza re-renders por defecto).',
        tags: ['Hooks', 'Estado Global'],
      },
      {
        title: 'useRef',
        description:
          'useRef retorna un objeto mutable {current: value} que persiste entre renders sin causar re-renders. Úsalo para: 1) referencias al DOM (autofocus, medición), 2) guardar valores mutables sin re-render (timers, IDs de animación), 3) almacenar el valor anterior de una prop/state.',
        tags: ['Hooks', 'DOM'],
      },
      {
        title: 'useMemo y useCallback',
        description:
          'useMemo memoiza el resultado de un cálculo costoso: const result = useMemo(() => heavyCalc(a, b), [a, b]). useCallback memoiza una función: const fn = useCallback(() => doSomething(x), [x]). Solo úsalos cuando hay un problema real de performance medido; agregan complejidad cognitiva.',
        tags: ['Performance', 'Optimización'],
      },
      {
        title: 'Virtual DOM y Reconciliation',
        description:
          'React mantiene una representación en memoria del DOM (Virtual DOM). Al cambiar el estado, genera un nuevo árbol virtual, lo compara con el anterior (diffing/reconciliation usando el algoritmo de Fiber) y solo actualiza los nodos reales que cambiaron. La prop key en listas ayuda a React a identificar qué elementos cambiaron.',
        tags: ['Internals', 'Performance'],
      },
      {
        title: 'Custom Hooks',
        description:
          'Un custom hook es una función que empieza con "use" y puede llamar otros hooks. Permiten extraer lógica stateful reutilizable. Ejemplos comunes: useFetch, useLocalStorage, useDebounce, useIntersectionObserver. Cada llamada al hook tiene su propio estado aislado.',
        tags: ['Patrones', 'Reutilización'],
      },
      {
        title: 'React.memo y Keys',
        description:
          'React.memo es un HOC que evita re-renders si las props no cambiaron (comparación superficial). La prop key en listas ayuda al algoritmo de diffing: usa IDs únicos estables, nunca índices de array cuando los items pueden reordenarse o eliminarse.',
        tags: ['Performance', 'Optimización'],
      },
    ],
    useCases: [
      {
        title: 'Single Page Applications (SPAs)',
        description:
          'React es ideal para SPAs donde la navegación ocurre en el cliente sin recargas de página. React Router maneja el enrutamiento del lado del cliente. Combinado con React Query o Redux para el estado del servidor.',
      },
      {
        title: 'Dashboards y paneles de administración',
        description:
          'La composición de componentes y el ecosistema de librerías (Recharts, AG Grid, Tanstack Table) hacen de React la opción default para dashboards complejos con visualizaciones de datos en tiempo real.',
      },
      {
        title: 'E-commerce y tiendas online',
        description:
          'Plataformas como Shopify (Hydrogen) usan React. El Server Side Rendering con Next.js mejora el SEO y el tiempo de carga inicial, críticos para conversiones en e-commerce.',
      },
      {
        title: 'Aplicaciones con estado complejo',
        description:
          'Para apps con lógica de negocio compleja (flujos de formularios, estados interdependientes, datos en tiempo real), el modelo de componentes de React con herramientas como Zustand o Redux Toolkit escala bien.',
      },
      {
        title: 'Sistemas de diseño y librerías de componentes',
        description:
          'La mayoría de sistemas de diseño modernos (MUI, Ant Design, Radix UI, Shadcn) están construidos en React, aprovechando su modelo de composición y el soporte de TypeScript.',
      },
    ],
    interviewQA: [
      {
        question: '¿Cuál es la diferencia entre estado (state) y props?',
        answer:
          'Las props son datos que un componente recibe de su padre y son de solo lectura. El estado es datos internos del componente que puede cambiar con el tiempo usando useState. Las props fluyen hacia abajo (unidirectional data flow); el estado es privado al componente que lo define.',
        difficulty: 'easy',
      },
      {
        question: '¿Qué es el Virtual DOM y por qué React lo usa?',
        answer:
          'El Virtual DOM es una representación ligera en memoria del DOM real. Cuando el estado cambia, React crea un nuevo árbol virtual, lo compara con el anterior (diffing) y solo actualiza las partes del DOM real que cambiaron. Esto es más eficiente que manipular el DOM directamente en cada cambio.',
        difficulty: 'easy',
      },
      {
        question: '¿Cuándo usarías useCallback vs useMemo?',
        answer:
          'useCallback memoiza una función (retorna la misma referencia si las deps no cambian). useMemo memoiza el resultado de llamar una función. Usa useCallback cuando pasas funciones a componentes hijos memoizados (React.memo) para evitar re-renders innecesarios. Usa useMemo para cálculos costosos. Ambos son optimizaciones de performance, no deben usarse por defecto.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es el prop drilling y cómo se soluciona?',
        answer:
          'Prop drilling ocurre cuando pasas props a través de múltiples niveles de componentes intermedios que no las usan, solo para que lleguen a un componente profundo. Se soluciona con: Context API (para datos globales poco frecuentes), state management (Zustand, Redux) para estado global complejo, o Component Composition pasando componentes como children.',
        difficulty: 'medium',
      },
      {
        question: '¿Cuál es la diferencia entre useEffect y useLayoutEffect?',
        answer:
          'useEffect se ejecuta de forma asíncrona después de que el navegador pinta el DOM. useLayoutEffect se ejecuta de forma síncrona después de todas las mutaciones del DOM pero antes de que el navegador pinte. Usa useLayoutEffect cuando necesitas medir el DOM o hacer mutaciones sincrónicas (ej: calcular la posición de un tooltip). En la mayoría de casos, useEffect es la opción correcta.',
        difficulty: 'hard',
      },
      {
        question: '¿Cómo funciona el algoritmo de reconciliación de React?',
        answer:
          'React usa el algoritmo Fiber para comparar árboles de componentes. Tiene dos asunciones de optimización: 1) Dos elementos de distinto tipo producirán árboles distintos (desmonta y recrea), 2) El desarrollador puede dar pistas de estabilidad con la prop key. Al comparar listas, usa key para identificar qué elementos se movieron, añadieron o eliminaron eficientemente.',
        difficulty: 'hard',
      },
      {
        question: '¿Qué son los React Server Components?',
        answer:
          'Los Server Components (RSC) son componentes que se renderizan solo en el servidor, sin enviar JavaScript al cliente. Pueden hacer fetch de datos directamente, leer bases de datos, y usar dependencias pesadas sin afectar el bundle del cliente. Se complementan con Client Components (marcados con "use client") que mantienen la interactividad.',
        difficulty: 'hard',
      },
      {
        question: '¿Cuándo usarías useReducer en lugar de useState?',
        answer:
          'useReducer es preferible cuando: el estado tiene múltiples sub-valores relacionados, el siguiente estado depende del anterior de forma compleja, o la lógica de actualización es suficientemente compleja para beneficiarse de tener un reducer puro testeable. useState es suficiente para estado simple o independiente.',
        difficulty: 'medium',
      },
    ],
    codeExamples: [
      {
        title: 'Custom Hook: useFetch',
        description: 'Hook reutilizable para fetching de datos con estado de loading y error.',
        language: 'typescript',
        code: `import { useState, useEffect } from 'react';

interface FetchState<T> {
  data: T | null;
  loading: boolean;
  error: Error | null;
}

function useFetch<T>(url: string): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({
    data: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;

    setState(prev => ({ ...prev, loading: true }));

    fetch(url)
      .then(res => {
        if (!res.ok) throw new Error(\`HTTP \${res.status}\`);
        return res.json() as Promise<T>;
      })
      .then(data => {
        if (!cancelled) setState({ data, loading: false, error: null });
      })
      .catch(error => {
        if (!cancelled) setState({ data: null, loading: false, error });
      });

    return () => { cancelled = true; };
  }, [url]);

  return state;
}

// Uso
function UserProfile({ userId }: { userId: string }) {
  const { data, loading, error } = useFetch<User>(\`/api/users/\${userId}\`);

  if (loading) return <Spinner />;
  if (error) return <ErrorMessage error={error} />;
  return <div>{data?.name}</div>;
}`,
      },
      {
        title: 'Context + Reducer pattern',
        description: 'Patrón para manejo de estado global con Context API y useReducer.',
        language: 'typescript',
        code: `import { createContext, useContext, useReducer, ReactNode } from 'react';

// 1. Tipos
interface CartItem { id: string; name: string; price: number; qty: number; }
interface CartState { items: CartItem[]; }
type CartAction =
  | { type: 'ADD'; item: CartItem }
  | { type: 'REMOVE'; id: string }
  | { type: 'CLEAR' };

// 2. Reducer puro y testeable
function cartReducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'ADD': {
      const exists = state.items.find(i => i.id === action.item.id);
      if (exists) {
        return {
          items: state.items.map(i =>
            i.id === action.item.id ? { ...i, qty: i.qty + 1 } : i
          ),
        };
      }
      return { items: [...state.items, { ...action.item, qty: 1 }] };
    }
    case 'REMOVE':
      return { items: state.items.filter(i => i.id !== action.id) };
    case 'CLEAR':
      return { items: [] };
    default:
      return state;
  }
}

// 3. Context
const CartContext = createContext<{
  state: CartState;
  dispatch: React.Dispatch<CartAction>;
} | null>(null);

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(cartReducer, { items: [] });
  return (
    <CartContext.Provider value={{ state, dispatch }}>
      {children}
    </CartContext.Provider>
  );
}

// 4. Hook de acceso
export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}`,
      },
      {
        title: 'Componente optimizado con React.memo',
        description: 'Ejemplo de memoización de componentes para evitar re-renders innecesarios.',
        language: 'typescript',
        code: `import { memo, useCallback, useState } from 'react';

// Componente hijo memoizado — solo re-renderiza si props cambian
const TodoItem = memo(function TodoItem({
  todo,
  onToggle,
}: {
  todo: { id: string; text: string; done: boolean };
  onToggle: (id: string) => void;
}) {
  console.log('Render TodoItem:', todo.id); // Para verificar optimización
  return (
    <li
      onClick={() => onToggle(todo.id)}
      style={{ textDecoration: todo.done ? 'line-through' : 'none' }}
    >
      {todo.text}
    </li>
  );
});

// Componente padre
export function TodoList() {
  const [todos, setTodos] = useState([
    { id: '1', text: 'Aprender React', done: false },
    { id: '2', text: 'Practicar hooks', done: false },
  ]);
  const [filter, setFilter] = useState('');

  // useCallback: misma referencia si setTodos no cambia
  // Sin esto, TodoItem re-renderizaría en cada keystroke del filtro
  const handleToggle = useCallback((id: string) => {
    setTodos(prev =>
      prev.map(t => (t.id === id ? { ...t, done: !t.done } : t))
    );
  }, []);

  const filtered = todos.filter(t => t.text.includes(filter));

  return (
    <div>
      <input value={filter} onChange={e => setFilter(e.target.value)} />
      <ul>
        {filtered.map(todo => (
          <TodoItem key={todo.id} todo={todo} onToggle={handleToggle} />
        ))}
      </ul>
    </div>
  );
}`,
      },
    ],
    quiz: [
      {
        question: '¿Qué retorna useState("hello")?',
        options: [
          '"hello"',
          'Un array con el valor y una función setter',
          'Un objeto con value y setValue',
          'Un Promise con el valor inicial',
        ],
        correctIndex: 1,
        explanation:
          'useState retorna un array con dos elementos: [currentValue, setterFunction]. Por eso se usa destructuring: const [value, setValue] = useState("hello").',
      },
      {
        question: '¿Cuándo se ejecuta el cleanup de useEffect?',
        options: [
          'Solo cuando el componente se desmonta',
          'Antes de ejecutar el próximo efecto y al desmontar',
          'Después de cada render',
          'Solo cuando las dependencias cambian',
        ],
        correctIndex: 1,
        explanation:
          'El cleanup de useEffect se ejecuta en dos momentos: justo antes de ejecutar el efecto nuevamente (si las deps cambiaron) y cuando el componente se desmonta. Esto previene memory leaks en subscripciones o timers.',
      },
      {
        question: '¿Cuál es el problema de usar índices de array como key en listas?',
        options: [
          'Los índices son lentos de computar',
          'React no acepta números como key',
          'Causa bugs en la reconciliación cuando los items se reordenan o eliminan',
          'Los índices no son únicos',
        ],
        correctIndex: 2,
        explanation:
          'Los índices son estables entre renders solo si la lista no cambia de orden ni se eliminan items. Si un item se elimina del medio, todos los índices posteriores cambian y React puede mostrar estados incorrectos en el componente incorrecto.',
      },
      {
        question: '¿Qué diferencia hay entre useEffect con [] y sin array de dependencias?',
        options: [
          'No hay diferencia',
          'Con [] se ejecuta en cada render; sin array solo al montar',
          'Con [] se ejecuta solo al montar; sin array se ejecuta en cada render',
          'Con [] se ejecuta al desmontar; sin array al montar',
        ],
        correctIndex: 2,
        explanation:
          'useEffect(() => {}, []) con array vacío solo se ejecuta una vez al montar el componente. useEffect(() => {}) sin array se ejecuta después de CADA render, lo que puede causar loops infinitos si actualiza estado.',
      },
      {
        question: '¿Qué hace React.memo?',
        options: [
          'Memoiza el resultado de una función costosa',
          'Evita re-renders de un componente si sus props no cambian',
          'Cachea las peticiones de red del componente',
          'Guarda el estado del componente entre renders',
        ],
        correctIndex: 1,
        explanation:
          'React.memo es un Higher Order Component que envuelve un componente y lo re-renderiza solo si sus props cambian (comparación superficial). Es útil cuando un componente hijo se re-renderiza frecuentemente con las mismas props.',
      },
      {
        question: '¿Cuál es la forma correcta de actualizar el estado basándose en el valor anterior?',
        options: [
          'setCount(count + 1)',
          'setCount(prev => prev + 1)',
          'count = count + 1',
          'setState({ count: count + 1 })',
        ],
        correctIndex: 1,
        explanation:
          'Usar la forma funcional setCount(prev => prev + 1) garantiza que trabajas con el valor más reciente del estado, especialmente importante cuando múltiples actualizaciones se agrupan (batching) o en handlers asíncronos.',
      },
      {
        question: '¿Qué hook usarías para acceder directamente a un elemento del DOM?',
        options: ['useState', 'useEffect', 'useRef', 'useContext'],
        correctIndex: 2,
        explanation:
          'useRef retorna un objeto {current: null} que puedes pasar como ref={myRef} a un elemento JSX. Después de montar, myRef.current apunta al nodo DOM real. Cambiar current no causa re-renders.',
      },
      {
        question: '¿Qué ocurre si no incluyes una dependencia necesaria en el array de useEffect?',
        options: [
          'React lanza un error en tiempo de ejecución',
          'El efecto nunca se ejecuta',
          'El efecto usa un valor stale (obsoleto) de la dependencia',
          'El efecto se ejecuta en cada render de todas formas',
        ],
        correctIndex: 2,
        explanation:
          'Si omites una dependencia, el efecto captura el valor de esa variable en el closure del primer render (o el último render en que se re-ejecutó). Esto se llama "stale closure" y es una fuente común de bugs sutiles.',
      },
      {
        question: '¿Cuál es el propósito principal del hook useReducer?',
        options: [
          'Reducir el tamaño del bundle de React',
          'Manejar estado complejo con múltiples sub-valores o transiciones complejas',
          'Reemplazar Context API',
          'Mejorar el performance de useState',
        ],
        correctIndex: 1,
        explanation:
          'useReducer es útil cuando el estado tiene múltiples sub-valores relacionados o cuando la lógica de actualización es compleja. El reducer es una función pura fácil de testear: (state, action) => newState.',
      },
      {
        question: '¿Qué hace el operador "key" en listas de React?',
        options: [
          'Define el orden de renderizado',
          'Aplica estilos únicos a cada elemento',
          'Ayuda a React a identificar qué elementos cambiaron, se añadieron o eliminaron',
          'Previene re-renders del componente de lista',
        ],
        correctIndex: 2,
        explanation:
          'La prop key es usada por el algoritmo de reconciliación para trackear la identidad de los elementos entre renders. Con keys estables y únicas, React puede reutilizar nodos del DOM existentes en lugar de recrearlos.',
      },
    ],
    exercises: [
      {
        title: 'Implementar un hook useDebounce',
        difficulty: 'medium',
        description:
          'Crea un custom hook useDebounce<T>(value: T, delay: number): T que retorna un valor debounceado. El valor solo debe actualizarse si no ha habido cambios durante el tiempo especificado en delay (en ms). Úsalo en un buscador que filtra una lista de usuarios.',
        hints: [
          'Usa useState para guardar el valor debounceado',
          'Usa useEffect con un setTimeout y clearTimeout en el cleanup',
          'El array de dependencias debe incluir value y delay',
        ],
        tags: ['Hooks', 'Performance', 'Custom Hook'],
      },
      {
        title: 'Formulario controlado con validación',
        difficulty: 'easy',
        description:
          'Construye un formulario de registro con campos: nombre, email, contraseña y confirmación de contraseña. Valida en tiempo real (onBlur) y muestra mensajes de error. El botón Submit debe estar deshabilitado si hay errores o campos vacíos. No uses librerías de formularios.',
        hints: [
          'Usa un objeto de estado para los valores y otro para los errores',
          'Crea funciones de validación puras que retornen string | null',
          'Usa el evento onBlur para marcar un campo como "touched"',
        ],
        tags: ['Formularios', 'Validación', 'Estado'],
      },
      {
        title: 'Infinite Scroll con IntersectionObserver',
        difficulty: 'hard',
        description:
          'Implementa un feed de posts con carga infinita. Cuando el usuario llega al último elemento visible, carga más posts. Usa IntersectionObserver (no scroll events) para detectar cuando el sentinel element entra en el viewport. Maneja estados de loading, error y "no más posts".',
        hints: [
          'Crea un ref para el elemento sentinel (div vacío al final de la lista)',
          'Usa useEffect para crear y limpiar el IntersectionObserver',
          'Mantén un estado de page/cursor para la paginación',
          'Usa useCallback para el callback del Observer',
        ],
        tags: ['Performance', 'Async', 'UX'],
      },
      {
        title: 'Kanban Board con drag and drop',
        difficulty: 'hard',
        description:
          'Crea un tablero Kanban con columnas (Todo, In Progress, Done) y tarjetas arrastrables entre columnas. Usa el HTML Drag and Drop API nativo (no librerías). El estado debe reflejar la posición actual de cada tarjeta.',
        hints: [
          'Usa los eventos onDragStart, onDragOver, onDrop',
          'Guarda el id del elemento que se está arrastrando en estado o ref',
          'Cada tarjeta y columna necesitan handlers apropiados',
          'Llama preventDefault() en onDragOver para habilitar el drop',
        ],
        tags: ['DOM', 'Interactividad', 'Estado Complejo'],
      },
      {
        title: 'Context de autenticación con persistencia',
        difficulty: 'medium',
        description:
          'Implementa un AuthContext que maneje login/logout. El token JWT debe persistirse en localStorage. Al recargar la página, el contexto debe leer el token, verificarlo (simula una llamada a /api/me) y restaurar la sesión del usuario. Protege rutas privadas redirigiendo a /login si no hay sesión.',
        hints: [
          'Usa useEffect con [] para verificar el token al montar',
          'Maneja tres estados: loading, authenticated, unauthenticated',
          'No renderices rutas protegidas hasta terminar el loading inicial',
          'Limpia el localStorage en el logout',
        ],
        tags: ['Auth', 'Context', 'Persistencia'],
      },
    ],
  },
  {
    slug: 'angular',
    title: 'Angular',
    shortDescription: 'Framework full-featured de Google con TypeScript, DI y RxJS.',
    category: 'frontend',
    icon: 'change_history',
    color: 'text-red-400',
    concepts: [
      {
        title: 'Módulos (NgModule)',
        description:
          'Un NgModule es un mecanismo para agrupar componentes, directivas, pipes y servicios relacionados. Cada app tiene al menos AppModule. Los módulos declaran (declarations), importan otros módulos (imports), exportan elementos (exports) y proveen servicios (providers). Con Standalone Components (Angular 14+) los módulos son opcionales.',
        tags: ['Arquitectura', 'Módulos'],
      },
      {
        title: 'Componentes',
        description:
          'Un componente Angular es una clase TypeScript decorada con @Component que define: selector (etiqueta HTML), template (HTML) y estilos. El ciclo de vida incluye hooks: ngOnInit, ngOnChanges, ngOnDestroy, ngAfterViewInit, etc. Los componentes se comunican con @Input() y @Output() / EventEmitter.',
        tags: ['Componentes', 'Decoradores'],
      },
      {
        title: 'Dependency Injection (DI)',
        description:
          'Angular tiene un sistema de DI jerárquico y potente. Los servicios se marcan con @Injectable({ providedIn: "root" }) para un singleton global, o se proveen en un módulo/componente para scope limitado. Angular instancia y gestiona las dependencias automáticamente. Facilita el testing con mocks.',
        tags: ['DI', 'Servicios', 'Testing'],
      },
      {
        title: 'RxJS y Observables',
        description:
          'Angular usa RxJS extensivamente: el HttpClient retorna Observables, el Router tiene eventos como Observables, los FormControls tienen valueChanges. Los Observables son lazy y cancelables. Operadores clave: map, filter, switchMap, takeUntil, combineLatest, debounceTime. El async pipe (| async) maneja suscripción y cancelación automáticamente.',
        tags: ['RxJS', 'Async', 'Reactivo'],
      },
      {
        title: 'Directivas',
        description:
          'Hay tres tipos: 1) Directivas de atributo: modifican el aspecto de un elemento (NgClass, NgStyle, o custom con @Directive). 2) Directivas estructurales: modifican el DOM (*ngIf, *ngFor, *ngSwitch). 3) Componentes: directivas con template. Las directivas estructurales son syntactic sugar para <ng-template>.',
        tags: ['Template', 'DOM'],
      },
      {
        title: 'Change Detection',
        description:
          'Angular detecta cambios comparando el estado antes y después de eventos (clicks, HTTP, timers). La estrategia Default verifica todo el árbol. ChangeDetectionStrategy.OnPush solo verifica cuando: las @Input() cambian por referencia, un evento en el componente ocurre, o async pipe emite. OnPush mejora el performance significativamente.',
        tags: ['Performance', 'Internals'],
      },
      {
        title: 'Reactive Forms',
        description:
          'Los Reactive Forms se crean con FormBuilder y FormGroup/FormControl programáticamente. Son más predecibles y testeables que Template-driven forms. Soportan validadores síncronos y asíncronos. FormArray para listas dinámicas. Con valueChanges puedes subscribirte a cambios con RxJS.',
        tags: ['Formularios', 'RxJS'],
      },
      {
        title: 'Routing y Lazy Loading',
        description:
          'El Router de Angular permite lazy loading de módulos: { path: "admin", loadChildren: () => import("./admin/admin.module").then(m => m.AdminModule) }. Con Standalone Components: loadComponent. Guards (CanActivate, CanDeactivate) protegen rutas. Resolvers precargan datos antes de activar una ruta.',
        tags: ['Routing', 'Performance'],
      },
      {
        title: 'Pipes',
        description:
          'Los pipes transforman datos en templates: {{ date | date:"dd/MM/yyyy" }}, {{ price | currency:"EUR" }}, {{ text | uppercase }}. Puedes crear pipes custom con @Pipe({ name: "myPipe" }). Los pipes son pure por defecto (solo ejecutan si el input cambia), o impure (ejecutan en cada CD).',
        tags: ['Template', 'Transformaciones'],
      },
    ],
    useCases: [
      {
        title: 'Aplicaciones empresariales complejas',
        description:
          'Angular es la opción preferred para proyectos grandes con múltiples equipos. Su estructura opinionada (módulos, DI, TypeScript nativo) facilita el onboarding de nuevos devs y mantiene consistencia entre equipos. Usado por Google, Microsoft, y grandes bancos.',
      },
      {
        title: 'Apps con formularios complejos',
        description:
          'Los Reactive Forms de Angular con validación síncrona/asíncrona, FormArrays dinámicos y el pipe async son superiores para aplicaciones con formularios de datos complejos (solicitudes bancarias, CRMs, ERPs).',
      },
      {
        title: 'Dashboards con datos en tiempo real',
        description:
          'La integración nativa con RxJS hace que Angular sea natural para apps con WebSockets, SSE o polling. Los Observables y operadores como switchMap, mergeMap permiten componer flujos de datos complejos de forma declarativa.',
      },
      {
        title: 'Progressive Web Apps (PWA)',
        description:
          'Angular tiene soporte nativo para PWA via @angular/pwa. Añade service workers, manifest y estrategias de caching automáticamente. El Angular CLI facilita la configuración.',
      },
    ],
    interviewQA: [
      {
        question: '¿Qué diferencia hay entre Observable y Promise?',
        answer:
          'Un Promise maneja un único valor asíncrono y se ejecuta inmediatamente (eager). Un Observable es lazy (no se ejecuta hasta suscripción), puede emitir múltiples valores a lo largo del tiempo, y es cancelable (unsubscribe). Los Observables soportan operadores de composición (map, filter, switchMap) para manipular flujos de datos.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es ChangeDetectionStrategy.OnPush y cuándo usarlo?',
        answer:
          'OnPush hace que Angular solo ejecute Change Detection en el componente cuando: 1) Una referencia de @Input() cambia, 2) Un evento dentro del componente ocurre, 3) Un Observable suscrito con async pipe emite, 4) Se llama manualmente ChangeDetectorRef.markForCheck(). Se usa en componentes "puros" o de presentación para mejorar performance.',
        difficulty: 'medium',
      },
      {
        question: '¿Cuál es la diferencia entre switchMap, mergeMap, concatMap y exhaustMap?',
        answer:
          'Todos reciben un Observable y retornan un Observable "aplanado". switchMap: cancela la suscripción anterior al llegar un nuevo valor (ideal para búsquedas). mergeMap: mantiene todas las suscripciones concurrentes. concatMap: espera que complete antes de suscribirse al siguiente (ideal para secuencias ordenadas). exhaustMap: ignora nuevos valores mientras hay una suscripción activa (ideal para prevenir doble submit).',
        difficulty: 'hard',
      },
      {
        question: '¿Qué diferencia hay entre Reactive Forms y Template-driven Forms?',
        answer:
          'Los Reactive Forms se crean en la clase TypeScript (más explícitos, testeables, escalables). Los Template-driven dependen de ngModel en el template (más simples para formularios pequeños). Para formularios complejos con validación dinámica o pruebas unitarias, Reactive Forms es la opción correcta.',
        difficulty: 'easy',
      },
      {
        question: '¿Qué es providedIn: "root" en @Injectable?',
        answer:
          'Hace que el servicio sea un singleton a nivel de la aplicación, disponible en toda la inyección de dependencias sin necesidad de declararlo en providers de ningún módulo. Angular lo incluye en el bundle solo si es usado (tree-shakeable). También puedes usar providedIn: MyModule para limitarlo al scope de ese módulo.',
        difficulty: 'easy',
      },
      {
        question: '¿Cómo evitarías memory leaks con Observables en Angular?',
        answer:
          'Varias estrategias: 1) Usar el async pipe en templates (se cancela automáticamente al destruir el componente). 2) Usar takeUntil(this.destroy$) con un Subject que emite en ngOnDestroy. 3) Usar take(1) si solo necesitas un valor. 4) Con Angular 16+: usar toSignal() que gestiona la suscripción automáticamente.',
        difficulty: 'medium',
      },
    ],
    codeExamples: [
      {
        title: 'Servicio con HttpClient y RxJS',
        description: 'Servicio Angular que consume una API REST con manejo de errores usando RxJS.',
        language: 'typescript',
        code: `import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map, retry } from 'rxjs/operators';

export interface User {
  id: string;
  name: string;
  email: string;
}

@Injectable({ providedIn: 'root' })
export class UserService {
  private readonly API = '/api/users';

  constructor(private http: HttpClient) {}

  getUsers(page = 1, limit = 20): Observable<User[]> {
    const params = new HttpParams()
      .set('page', page)
      .set('limit', limit);

    return this.http.get<{ data: User[] }>(this.API, { params }).pipe(
      map(response => response.data),
      retry(2),
      catchError(err => {
        console.error('Error fetching users:', err);
        return throwError(() => new Error('No se pudo cargar usuarios'));
      })
    );
  }

  createUser(payload: Omit<User, 'id'>): Observable<User> {
    return this.http.post<User>(this.API, payload).pipe(
      catchError(err => throwError(() => err))
    );
  }
}`,
      },
      {
        title: 'Reactive Form con validación asíncrona',
        description: 'Formulario reactivo con validadores síncronos y asíncronos.',
        language: 'typescript',
        code: `import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { Observable, of } from 'rxjs';
import { debounceTime, map, switchMap } from 'rxjs/operators';
import { UserService } from './user.service';

// Validador asíncrono personalizado
function emailAvailable(userService: UserService) {
  return (control: AbstractControl): Observable<{ taken: true } | null> => {
    if (!control.value) return of(null);
    return of(control.value).pipe(
      debounceTime(400),
      switchMap(email => userService.checkEmailAvailable(email)),
      map(available => (available ? null : { taken: true }))
    );
  };
}

@Component({
  selector: 'app-register',
  template: \`
    <form [formGroup]="form" (ngSubmit)="submit()">
      <input formControlName="name" placeholder="Nombre" />
      <span *ngIf="form.get('name')?.errors?.['required'] && form.get('name')?.touched">
        El nombre es obligatorio
      </span>

      <input formControlName="email" placeholder="Email" />
      <span *ngIf="form.get('email')?.errors?.['email']">Email inválido</span>
      <span *ngIf="form.get('email')?.errors?.['taken']">Email ya registrado</span>
      <span *ngIf="form.get('email')?.pending">Verificando...</span>

      <button type="submit" [disabled]="form.invalid || form.pending">
        Registrar
      </button>
    </form>
  \`,
})
export class RegisterComponent implements OnInit {
  form = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    email: [
      '',
      [Validators.required, Validators.email],
      [emailAvailable(this.userService)],
    ],
  });

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
  ) {}

  submit() {
    if (this.form.valid) {
      console.log(this.form.value);
    }
  }
}`,
      },
      {
        title: 'OnPush con async pipe y signals',
        description: 'Componente optimizado con OnPush y el nuevo sistema de Signals de Angular 17+.',
        language: 'typescript',
        code: `import { Component, ChangeDetectionStrategy, inject, signal, computed } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { UserService } from './user.service';

@Component({
  selector: 'app-user-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: \`
    <div>
      <input (input)="search.set($any($event.target).value)" placeholder="Buscar..." />

      @if (loading()) {
        <p>Cargando...</p>
      } @else {
        <ul>
          @for (user of filtered(); track user.id) {
            <li>{{ user.name }} — {{ user.email }}</li>
          } @empty {
            <li>Sin resultados</li>
          }
        </ul>
      }
    </div>
  \`,
})
export class UserListComponent {
  private userService = inject(UserService);

  // Convierte Observable a Signal (gestiona suscripción automáticamente)
  users = toSignal(this.userService.getUsers(), { initialValue: [] });
  loading = signal(false);
  search = signal('');

  // Signal derivada (computed) — se recalcula cuando users o search cambian
  filtered = computed(() =>
    this.users().filter(u =>
      u.name.toLowerCase().includes(this.search().toLowerCase())
    )
  );
}`,
      },
    ],
    quiz: [
      {
        question: '¿Qué hace el decorador @Injectable({ providedIn: "root" })?',
        options: [
          'Inyecta el servicio en el root component',
          'Hace el servicio un singleton disponible en toda la app y tree-shakeable',
          'Importa el servicio en el módulo raíz',
          'Hace el servicio disponible solo en el módulo donde se declara',
        ],
        correctIndex: 1,
        explanation:
          'providedIn: "root" crea un singleton del servicio accesible en toda la app. Angular lo incluye en el bundle solo si es inyectado en algún componente/servicio (tree-shakeable), sin necesidad de añadirlo a providers[] de ningún módulo.',
      },
      {
        question: '¿Qué operador RxJS usarías para una búsqueda en vivo (cancelar peticiones anteriores)?',
        options: ['mergeMap', 'concatMap', 'switchMap', 'exhaustMap'],
        correctIndex: 2,
        explanation:
          'switchMap cancela la suscripción/petición anterior cuando llega un nuevo valor. Es ideal para búsquedas en vivo donde solo nos interesa el resultado de la última query y queremos cancelar peticiones obsoletas.',
      },
      {
        question: '¿Cuál es la diferencia entre *ngIf y la nueva sintaxis @if de Angular 17+?',
        options: [
          'No hay diferencia, son equivalentes',
          '@if es más rápido porque no crea un ng-template',
          '@if usa la nueva sintaxis de control flow nativa, más performante y sin necesidad de importar CommonModule',
          '*ngIf soporta else, @if no',
        ],
        correctIndex: 2,
        explanation:
          'La nueva sintaxis @if (Angular 17+) es nativa del compilador de Angular, no requiere importar CommonModule, y es más performante. También soporta @else y @else if. *ngIf sigue funcionando pero es legacy.',
      },
      {
        question: '¿Qué ocurre si no haces unsubscribe de un Observable en ngOnDestroy?',
        options: [
          'Angular lo limpia automáticamente',
          'El Observable deja de emitir al destruir el componente',
          'Puede causar memory leaks y comportamiento inesperado',
          'Solo es un problema con Observables HTTP',
        ],
        correctIndex: 2,
        explanation:
          'Si no cancelas suscripciones (especialmente a Subjects, WebSockets, o intervalos infinitos), el callback seguirá ejecutándose incluso después de destruir el componente, causando memory leaks y posibles errores al intentar actualizar un componente destruido.',
      },
      {
        question: '¿Cuándo se ejecuta el Change Detection con OnPush?',
        options: [
          'En cada evento del browser',
          'Solo cuando se llama manualmente',
          'Cuando cambia una referencia de @Input, hay un evento en el componente, o el async pipe emite',
          'Nunca, OnPush desactiva el change detection',
        ],
        correctIndex: 2,
        explanation:
          'Con OnPush, Angular ejecuta CD solo en 4 casos: 1) Referencia de @Input cambia, 2) Evento en el propio componente (click, input), 3) Observable suscrito con async pipe emite, 4) Se llama ChangeDetectorRef.markForCheck() manualmente.',
      },
    ],
    exercises: [
      {
        title: 'Servicio de autenticación con JWT',
        difficulty: 'medium',
        description:
          'Implementa un AuthService en Angular que maneje login/logout con JWT. El token debe guardarse en localStorage. Crea un HttpInterceptor que añada el Bearer token a cada petición. Implementa un CanActivate guard que redirija a /login si el token expiró. Usa BehaviorSubject para el estado del usuario actual.',
        hints: [
          'Usa BehaviorSubject<User | null> para el usuario actual',
          'El interceptor recibe el token de AuthService y clona la request con HttpRequest.clone()',
          'Verifica la expiración del JWT decodificando el payload (base64)',
          'El guard puede inyectar AuthService y retornar un Observable<boolean>',
        ],
        tags: ['Auth', 'RxJS', 'Interceptors', 'Guards'],
      },
      {
        title: 'Tabla con paginación y filtros reactivos',
        difficulty: 'medium',
        description:
          'Crea un componente de tabla que cargue datos de una API con paginación server-side. Los filtros (búsqueda por texto, dropdown de categoría, rango de fechas) deben combinarse con RxJS (combineLatest o merge) y hacer la petición solo después de un debounce de 300ms. Muestra estado de loading entre peticiones.',
        hints: [
          'Crea FormControls para cada filtro',
          'Usa combineLatest([searchControl.valueChanges, categoryControl.valueChanges, pageSubject])',
          'Aplica debounceTime(300) y distinctUntilChanged()',
          'Usa switchMap para cancelar peticiones anteriores',
          'El async pipe en el template gestiona la suscripción',
        ],
        tags: ['RxJS', 'Forms', 'HTTP', 'OnPush'],
      },
      {
        title: 'Componente de autocompletar con accesibilidad',
        difficulty: 'hard',
        description:
          'Implementa un componente de autocompletar (Autocomplete) reutilizable que busque en una API con debounce, soporte navegación por teclado (ArrowUp/Down/Enter/Escape), sea accesible (ARIA roles), y funcione como ControlValueAccessor para integrarse con Reactive Forms.',
        hints: [
          'Implementa la interfaz ControlValueAccessor para integración con formularios',
          'Usa @HostListener para los eventos de teclado',
          'Mantén el índice activo en estado y aplica clases CSS al ítem activo',
          'Añade role="combobox", aria-expanded, aria-activedescendant',
        ],
        tags: ['Accesibilidad', 'ControlValueAccessor', 'UX'],
      },
    ],
  },
  {
    slug: 'ionic',
    title: 'Ionic',
    shortDescription: 'Framework para apps móviles multiplataforma con web technologies.',
    icon: 'phone_android',
    color: 'text-violet-400',
    category: 'frontend',
    concepts: [
      {
        title: 'Ionic y Capacitor',
        description:
          'Ionic es un framework de UI que proporciona componentes nativos (IonButton, IonCard, IonList) adaptados a iOS y Android. Capacitor es el runtime nativo que envuelve la app web en un contenedor nativo y provee acceso a APIs del dispositivo (cámara, GPS, notificaciones). Juntos permiten construir apps móviles multiplataforma con TypeScript/HTML/CSS.',
        tags: ['Arquitectura', 'Capacitor'],
      },
      {
        title: 'Componentes de UI adaptativos',
        description:
          'Los componentes Ionic se adaptan automáticamente a la plataforma: en iOS siguen las guías de diseño de Apple, en Android el Material Design de Google. Esto se llama "adaptive styling". Puedes forzar una plataforma con mode="ios" o mode="md". Los componentes incluyen IonModal, IonActionSheet, IonAlert, IonToast, IonInfiniteScroll.',
        tags: ['UI', 'Diseño'],
      },
      {
        title: 'Capacitor Plugins y APIs nativas',
        description:
          'Capacitor provee plugins oficiales: Camera, Geolocation, Filesystem, Push Notifications, Network, Storage, etc. Se instalan como npm packages y se llaman con APIs web-friendly. También puedes crear plugins nativos personalizados en Swift/Kotlin. Ionic Native/Capacitor Community extiende el ecosistema.',
        tags: ['Capacitor', 'Nativo'],
      },
      {
        title: 'Navegación y Routing',
        description:
          'Con Angular: usa IonRouterOutlet en lugar del router-outlet estándar, que añade animaciones de transición nativas y manejo del stack de navegación. IonBackButton y NavController manejan la navegación backward. Con el parámetro de ruta puedes pasar datos.',
        tags: ['Routing', 'Navegación'],
      },
      {
        title: 'PWA y Web',
        description:
          'Ionic apps funcionan también como PWAs. Con @ionic/pwa-elements puedes usar APIs nativas (cámara con fallback a input file) en el navegador. El CLI de Ionic genera el manifest y service workers automáticamente. Puedes deployar la misma codebase como PWA, iOS y Android.',
        tags: ['PWA', 'Web'],
      },
      {
        title: 'Rendimiento y optimización',
        description:
          'Para apps móviles el rendimiento es crítico. Ionic usa virtual scrolling (IonVirtualScroll o IonInfiniteScroll) para listas largas. Lazy loading de páginas/módulos reduce el tiempo de inicio. Las animaciones de transición usan CSS animations (GPU) o Web Animations API, no JavaScript.',
        tags: ['Performance', 'Optimización'],
      },
      {
        title: 'Storage y datos locales',
        description:
          'Capacitor Preferences (antes Storage) guarda pares clave-valor de forma nativa. Para datos estructurados usa SQLite con capacitor-sqlite o IndexedDB. Ionic/Storage es un wrapper que selecciona automáticamente el mejor motor disponible (SQLite nativo, IndexedDB en web).',
        tags: ['Storage', 'Offline'],
      },
      {
        title: 'Testing en Ionic',
        description:
          'Para unit testing: Jest o Jasmine con TestBed de Angular. Para end-to-end en dispositivos reales: Detox o Appium. Para web E2E: Cypress o Playwright. El reto es mockear Capacitor plugins (que no tienen implementación en JSDOM). La convención es crear wrappers inyectables de los plugins para facilitar el mock.',
        tags: ['Testing', 'QA'],
      },
    ],
    useCases: [
      {
        title: 'Apps B2B con equipos web',
        description:
          'Ionic es ideal cuando un equipo web (Angular/React) necesita entregar una app móvil sin contratar iOS/Android developers. La misma codebase se compila para iOS, Android y web. Es la elección típica para herramientas internas, fieldwork apps, o CRMs móviles.',
      },
      {
        title: 'Apps de e-commerce y catálogos',
        description:
          'Para apps de compras donde el catálogo y la UI son más importantes que animaciones 60fps complejas. Ionic tiene todos los componentes necesarios (listas, cards, modales de producto, carrito). Funciona bien para Shopify/WooCommerce frontends.',
      },
      {
        title: 'Apps con funcionalidades nativas moderadas',
        description:
          'Cuando necesitas cámara, geolocalización, notificaciones push, pero no características muy específicas de la plataforma. Capacitor cubre el 90% de casos de uso de apps de negocio típicas.',
      },
      {
        title: 'Progressive Web Apps',
        description:
          'Ionic es excelente para PWAs que deben verse y sentirse como apps nativas. El sistema de componentes y las animaciones de Ionic dan una experiencia cercana a nativa en el navegador móvil, sin necesidad de publicar en tiendas de apps.',
      },
    ],
    interviewQA: [
      {
        question: '¿Cuál es la diferencia entre Ionic con Cordova y con Capacitor?',
        answer:
          'Cordova (legacy) usa plugins basados en callbacks con APIs más antigas. Capacitor es el sucesor moderno creado por el equipo de Ionic: usa Promises/async-await, tiene APIs web-first que funcionan en el browser, genera proyectos Xcode/Android Studio estándar (más fácil de personalizar), y está diseñado para funcionar también como PWA sin cambios de código.',
        difficulty: 'medium',
      },
      {
        question: '¿Cómo funciona el "adaptive styling" de Ionic?',
        answer:
          'Los componentes de Ionic detectan la plataforma en runtime (iOS o Android) y aplican estilos y comportamientos acordes: en iOS usan las guías HIG de Apple (colores, tipografía, botones back), en Android usan Material Design. Puedes sobrescribir esto con mode="ios" o mode="md" en cualquier componente o globalmente en IonApp.',
        difficulty: 'easy',
      },
      {
        question: '¿Cómo harías para que tu app Ionic funcione offline?',
        answer:
          'Estrategia en capas: 1) Service Worker (PWA) para cachear assets y respuestas de red con estrategia Cache First o Stale While Revalidate. 2) Capacitor Preferences o SQLite para persistir datos localmente. 3) Una cola de peticiones pendientes (sync queue) que se envía cuando hay conexión usando el plugin Network para detectar el estado. 4) Indicadores visuales con IonToast para informar al usuario del estado offline.',
        difficulty: 'hard',
      },
      {
        question: '¿Qué es IonRouterOutlet y por qué se usa en vez de router-outlet?',
        answer:
          'IonRouterOutlet extiende router-outlet de Angular con: 1) Animaciones de transición nativas (slide, fade) entre páginas. 2) Gestión del stack de páginas en memoria (mantiene componentes vivos para la navegación back sin recargar). 3) Integración con IonBackButton. Usar router-outlet estándar pierde estas características.',
        difficulty: 'medium',
      },
      {
        question: '¿Cómo mockeas Capacitor plugins en tests unitarios?',
        answer:
          'La práctica recomendada es crear un servicio wrapper Angular que inyecta el plugin: @Injectable class CameraService { async takePhoto() { return Camera.getPhoto(options); } }. En tests, provees un mock del CameraService con TestBed: providers: [{ provide: CameraService, useValue: mockCameraService }]. Esto desacopla los componentes de la API nativa y hace los tests independientes del dispositivo.',
        difficulty: 'hard',
      },
    ],
    codeExamples: [
      {
        title: 'Página con lista infinita',
        description: 'Lista con carga infinita usando IonInfiniteScroll y datos de una API.',
        language: 'typescript',
        code: `import { Component, OnInit } from '@angular/core';
import { InfiniteScrollCustomEvent } from '@ionic/angular';
import { ProductService } from '../services/product.service';

interface Product { id: string; name: string; price: number; image: string; }

@Component({
  selector: 'app-products',
  template: \`
    <ion-header>
      <ion-toolbar>
        <ion-title>Productos</ion-title>
      </ion-toolbar>
      <ion-toolbar>
        <ion-searchbar
          [(ngModel)]="searchTerm"
          (ionInput)="onSearch()"
          placeholder="Buscar...">
        </ion-searchbar>
      </ion-toolbar>
    </ion-header>

    <ion-content>
      <ion-list>
        <ion-item *ngFor="let product of products; trackBy: trackById">
          <ion-thumbnail slot="start">
            <img [src]="product.image" [alt]="product.name" />
          </ion-thumbnail>
          <ion-label>
            <h2>{{ product.name }}</h2>
            <p>{{ product.price | currency }}</p>
          </ion-label>
          <ion-button slot="end" fill="outline" (click)="addToCart(product)">
            Añadir
          </ion-button>
        </ion-item>
      </ion-list>

      <ion-infinite-scroll (ionInfinite)="loadMore($event)">
        <ion-infinite-scroll-content loadingText="Cargando más...">
        </ion-infinite-scroll-content>
      </ion-infinite-scroll>
    </ion-content>
  \`,
})
export class ProductsPage implements OnInit {
  products: Product[] = [];
  searchTerm = '';
  private page = 1;

  constructor(private productService: ProductService) {}

  ngOnInit() { this.loadProducts(); }

  async loadProducts(reset = false) {
    if (reset) { this.page = 1; this.products = []; }
    const newProducts = await this.productService.getProducts({
      page: this.page,
      search: this.searchTerm,
    });
    this.products = [...this.products, ...newProducts];
  }

  async loadMore(event: InfiniteScrollCustomEvent) {
    this.page++;
    await this.loadProducts();
    event.target.complete();
  }

  onSearch() { this.loadProducts(true); }
  trackById(_: number, item: Product) { return item.id; }
  addToCart(product: Product) { console.log('Adding to cart:', product); }
}`,
      },
      {
        title: 'Acceso a la cámara con Capacitor',
        description: 'Usa el plugin Camera de Capacitor para tomar fotos y mostrarlas en la app.',
        language: 'typescript',
        code: `import { Component } from '@angular/core';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { ToastController, LoadingController } from '@ionic/angular';

@Component({
  selector: 'app-photo',
  template: \`
    <ion-content class="ion-padding">
      <div class="photo-container">
        <img
          *ngIf="photoUrl"
          [src]="photoUrl"
          alt="Foto capturada"
          class="preview"
        />
        <div *ngIf="!photoUrl" class="placeholder">
          <ion-icon name="camera-outline" size="large"></ion-icon>
          <p>Sin foto</p>
        </div>
      </div>

      <ion-button expand="block" (click)="takePhoto()">
        <ion-icon name="camera" slot="start"></ion-icon>
        Tomar foto
      </ion-button>

      <ion-button expand="block" fill="outline" (click)="pickFromGallery()">
        <ion-icon name="images" slot="start"></ion-icon>
        Galería
      </ion-button>
    </ion-content>
  \`,
})
export class PhotoPage {
  photoUrl: string | null = null;

  constructor(
    private toastCtrl: ToastController,
    private loadingCtrl: LoadingController,
  ) {}

  async takePhoto() {
    try {
      const image = await Camera.getPhoto({
        quality: 90,
        allowEditing: false,
        resultType: CameraResultType.DataUrl,
        source: CameraSource.Camera,
      });
      this.photoUrl = image.dataUrl ?? null;
    } catch (err) {
      if ((err as Error).message !== 'User cancelled photos app') {
        this.showToast('Error al acceder a la cámara');
      }
    }
  }

  async pickFromGallery() {
    const image = await Camera.getPhoto({
      quality: 90,
      resultType: CameraResultType.DataUrl,
      source: CameraSource.Photos,
    });
    this.photoUrl = image.dataUrl ?? null;
  }

  private async showToast(message: string) {
    const toast = await this.toastCtrl.create({
      message,
      duration: 2000,
      position: 'bottom',
      color: 'danger',
    });
    await toast.present();
  }
}`,
      },
      {
        title: 'Storage local con Capacitor Preferences',
        description: 'Servicio para persistir datos clave-valor de forma nativa en el dispositivo.',
        language: 'typescript',
        code: `import { Injectable } from '@angular/core';
import { Preferences } from '@capacitor/preferences';

@Injectable({ providedIn: 'root' })
export class StorageService {
  async set<T>(key: string, value: T): Promise<void> {
    await Preferences.set({
      key,
      value: JSON.stringify(value),
    });
  }

  async get<T>(key: string): Promise<T | null> {
    const { value } = await Preferences.get({ key });
    if (value === null) return null;
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }

  async remove(key: string): Promise<void> {
    await Preferences.remove({ key });
  }

  async clear(): Promise<void> {
    await Preferences.clear();
  }
}

// Uso en un componente
@Injectable({ providedIn: 'root' })
export class AuthStorageService {
  private readonly TOKEN_KEY = 'auth_token';
  private readonly USER_KEY = 'current_user';

  constructor(private storage: StorageService) {}

  async saveSession(token: string, user: User) {
    await Promise.all([
      this.storage.set(this.TOKEN_KEY, token),
      this.storage.set(this.USER_KEY, user),
    ]);
  }

  async getToken(): Promise<string | null> {
    return this.storage.get<string>(this.TOKEN_KEY);
  }

  async clearSession(): Promise<void> {
    await Promise.all([
      this.storage.remove(this.TOKEN_KEY),
      this.storage.remove(this.USER_KEY),
    ]);
  }
}`,
      },
    ],
    quiz: [
      {
        question: '¿Cuál es la principal ventaja de Capacitor sobre Cordova?',
        options: [
          'Tiene más plugins disponibles',
          'APIs modernas con async/await, proyectos nativos estándar y soporte PWA nativo',
          'Compila el código TypeScript a nativo (Swift/Kotlin)',
          'No requiere XCode ni Android Studio',
        ],
        correctIndex: 1,
        explanation:
          'Capacitor usa APIs modernas (async/await en lugar de callbacks), genera proyectos Xcode/Android Studio estándar que puedes editar directamente, y funciona como PWA sin cambios. Es el sucesor moderno de Cordova mantenido por el equipo de Ionic.',
      },
      {
        question: '¿Qué es IonRouterOutlet y por qué se prefiere sobre router-outlet?',
        options: [
          'Es más rápido que router-outlet',
          'Añade animaciones de transición nativas y gestión del stack de navegación',
          'Soporta rutas lazy loading',
          'Permite rutas anidadas',
        ],
        correctIndex: 1,
        explanation:
          'IonRouterOutlet extiende router-outlet con animaciones de transición nativas (slide/fade), mantiene componentes en memoria para navegación back instantánea, e integra correctamente con IonBackButton.',
      },
      {
        question: '¿Cómo accedes a APIs nativas del dispositivo en Ionic?',
        options: [
          'Directamente desde JavaScript usando window.native',
          'Mediante Capacitor plugins o Capacitor Community plugins instalados como npm packages',
          'Solo a través de Angular Services especiales',
          'Usando WebAssembly',
        ],
        correctIndex: 1,
        explanation:
          'Capacitor provee plugins oficiales (@capacitor/camera, @capacitor/geolocation, etc.) que se instalan como paquetes npm y exponen APIs modernas con Promises. La comunidad también mantiene plugins adicionales en @capacitor-community.',
      },
      {
        question: '¿Qué almacenamiento usarías para datos estructurados grandes en una app Ionic offline?',
        options: [
          'localStorage del navegador',
          'Capacitor Preferences (Preferences.set/get)',
          'SQLite con capacitor-community/sqlite o ionic/storage',
          'IndexedDB directamente',
        ],
        correctIndex: 2,
        explanation:
          'Capacitor Preferences es para pares clave-valor simples (settings, token). Para datos estructurados grandes en offline, SQLite (via capacitor-community/sqlite) o Ionic Storage (que usa SQLite nativo en dispositivo, IndexedDB en web) es la solución correcta.',
      },
      {
        question: '¿Qué es el "adaptive styling" en Ionic?',
        options: [
          'Los estilos se adaptan al tamaño de pantalla (responsive)',
          'Los componentes aplican automáticamente el estilo visual de iOS o Android según la plataforma',
          'Los estilos se generan dinámicamente según el tema del usuario',
          'Es el sistema de variables CSS de Ionic',
        ],
        correctIndex: 1,
        explanation:
          'Los componentes Ionic detectan la plataforma en runtime y aplican el look and feel correspondiente: HIG de Apple en iOS, Material Design en Android. Puedes forzar un modo con mode="ios" o mode="md".',
      },
    ],
    exercises: [
      {
        title: 'App de lista de tareas con persistencia offline',
        difficulty: 'easy',
        description:
          'Crea una app de To-Do list en Ionic/Angular con IonList, IonItem y IonCheckbox. Las tareas deben persistirse con Capacitor Preferences para sobrevivir cierres de la app. Añade swipe-to-delete con IonItemSliding. Muestra un IonToast al completar o eliminar una tarea.',
        hints: [
          'Usa IonItemSliding con IonItemOptions para el swipe delete',
          'Guarda la lista completa serializada como JSON en Preferences',
          'El IonCheckbox puede tener [(ngModel)] para el estado checked',
          'IonToast.create() retorna una Promise, recuerda hacer await present()',
        ],
        tags: ['Storage', 'UI', 'Ionic Components'],
      },
      {
        title: 'Scanner de QR con cámara nativa',
        difficulty: 'medium',
        description:
          'Implementa un escáner de códigos QR usando @capacitor-community/barcode-scanner. La app debe: solicitar permiso de cámara antes de escanear, mostrar una UI de "scanning" mientras escanea, y al detectar un código mostrar el resultado en un IonAlert con opción de abrir si es una URL.',
        hints: [
          'Usa BarcodeScanner.checkPermission({ force: true }) antes de escanear',
          'Añade la clase "barcode-scanner-active" al body para hacer transparente el fondo',
          'IonAlert puede tener buttons con handlers para abrir URLs con Browser.open()',
          'Limpia el escáner en ngOnDestroy con BarcodeScanner.stopScan()',
        ],
        tags: ['Capacitor', 'Cámara', 'Permisos'],
      },
      {
        title: 'App de geolocalización con mapa',
        difficulty: 'hard',
        description:
          'Crea una pantalla que muestre la ubicación actual del usuario en un mapa usando Leaflet.js (no requiere API key). Usa @capacitor/geolocation para obtener coordenadas con alta precisión. Actualiza la posición cada 5 segundos con watchPosition. Muestra la velocidad y precisión en una IonCard superpuesta sobre el mapa. Funciona en web y en dispositivo.',
        hints: [
          'Inicializa el mapa Leaflet en ngAfterViewInit después de que el IonContent haya renderizado',
          'Usa Geolocation.watchPosition() y guarda el callbackId para cancelar en ngOnDestroy',
          'Leaflet necesita ser importado dinámicamente para evitar SSR issues: const L = await import("leaflet")',
          'Maneja el caso web con la API de Geolocation del navegador como fallback',
        ],
        tags: ['Geolocation', 'Maps', 'Capacitor', 'Native'],
      },
    ],
  },
  {
    slug: 'nodejs',
    title: 'Node.js',
    shortDescription: 'Runtime de JavaScript en el servidor basado en el motor V8 y arquitectura event-driven.',
    icon: 'dns',
    color: 'text-green-500',
    category: 'backend',
    concepts: [
      {
        title: 'Event Loop',
        description:
          'El Event Loop es el corazón de Node.js. Permite ejecutar operaciones no bloqueantes aunque JavaScript sea single-threaded. Fases: timers (setTimeout/setInterval), pending callbacks, idle/prepare, poll (I/O), check (setImmediate), close callbacks. Entender el orden de ejecución evita bugs sutiles con operaciones asíncronas.',
        tags: ['Arquitectura', 'Async'],
      },
      {
        title: 'Módulos CommonJS y ESM',
        description:
          'Node.js soporta dos sistemas de módulos: CommonJS (require/module.exports, sincrónico, .cjs) y ES Modules (import/export, estático, .mjs o "type": "module" en package.json). ESM permite tree-shaking y top-level await. En proyectos TypeScript modernos se usa ESM compilado. Los módulos se cachean tras la primera carga.',
        tags: ['Módulos', 'TypeScript'],
      },
      {
        title: 'Streams',
        description:
          'Los Streams permiten procesar datos en chunks sin cargarlos enteros en memoria. Tipos: Readable, Writable, Duplex, Transform. Se conectan con pipe(). Esenciales para procesamiento de archivos grandes, respuestas HTTP de gran tamaño, compresión en tiempo real. Implementan la interfaz EventEmitter.',
        tags: ['I/O', 'Performance'],
      },
      {
        title: 'Buffer y manejo de binarios',
        description:
          'Buffer es una clase global para trabajar con datos binarios (imágenes, archivos, protobuf). A diferencia de strings, los Buffers tienen tamaño fijo y acceso directo a memoria. Buffer.from(), Buffer.alloc(), buf.toString("base64"). Importante en parsing de protocolos de red, procesamiento de imágenes y criptografía.',
        tags: ['Binarios', 'I/O'],
      },
      {
        title: 'Child Processes y Worker Threads',
        description:
          'Para tareas CPU-intensivas que bloquearían el event loop: Worker Threads (hilo separado, memoria compartida con SharedArrayBuffer, mismo proceso) o Child Processes (proceso separado, comunicación via IPC). cluster module crea múltiples procesos que comparten el mismo puerto para aprovechar múltiples núcleos.',
        tags: ['Concurrencia', 'Performance'],
      },
      {
        title: 'EventEmitter',
        description:
          'La clase EventEmitter es la base de gran parte de Node.js (streams, http, fs). Permite suscribirse a eventos con on/once y emitirlos con emit. Patrón pub/sub dentro del proceso. Importante: manejar el evento "error" siempre (si no hay listener, lanza una excepción). Usar once() para evitar memory leaks en listeners de un solo uso.',
        tags: ['Patrones', 'Eventos'],
      },
      {
        title: 'Async/Await y manejo de errores',
        description:
          'Node.js moderno usa async/await sobre callbacks y Promises. Las funciones async siempre retornan una Promise. Usa try/catch para errores síncronos y de Promises rechazadas. Maneja el evento process.on("unhandledRejection") para Promises no capturadas. Promise.all() para paralelismo; Promise.allSettled() cuando todas deben completarse independientemente.',
        tags: ['Async', 'Error Handling'],
      },
      {
        title: 'HTTP nativo y frameworks',
        description:
          'El módulo http nativo permite crear servidores, pero es verboso. Express es el framework minimalista de facto. Frameworks modernos: Fastify (más rápido, schema validation), Hapi, Koa. Para APIs a gran escala, NestJS añade estructura. Para edge/serverless: Hono. Elegir según el nivel de estructura necesario.',
        tags: ['HTTP', 'Frameworks'],
      },
      {
        title: 'npm y gestión de dependencias',
        description:
          'npm es el package manager por defecto. package.json define dependencias (dependencies vs devDependencies), scripts y metadatos. package-lock.json garantiza instalaciones reproducibles. Alternativas: pnpm (eficiente con workspaces/monorepos), yarn, bun. Semantic versioning: major.minor.patch. ^ permite minor updates, ~ solo patch.',
        tags: ['Tooling', 'Ecosistema'],
      },
      {
        title: 'Variables de entorno y configuración',
        description:
          'Usa process.env para acceder a variables de entorno. En desarrollo, dotenv carga el archivo .env. Nunca commitees .env con secretos. Valida variables de entorno al arrancar la app (con zod o envalid) para fallar rápido si falta configuración crítica. En producción, usa secrets managers (AWS Secrets Manager, Vault).',
        tags: ['Configuración', 'Seguridad'],
      },
    ],
    useCases: [
      {
        title: 'APIs REST y GraphQL',
        description:
          'Node.js es el entorno más popular para construir APIs. Su modelo non-blocking lo hace ideal para APIs con alta concurrencia y muchas operaciones I/O (base de datos, servicios externos). Frameworks como Express, Fastify y NestJS simplifican la creación de endpoints REST o schemas GraphQL.',
      },
      {
        title: 'Microservicios y arquitecturas distribuidas',
        description:
          'El bajo consumo de memoria y el arranque rápido de Node.js lo hacen ideal para microservicios en contenedores. Se integra bien con RabbitMQ, Kafka y Redis Pub/Sub para comunicación entre servicios. NestJS tiene soporte nativo de transportes de microservicios.',
      },
      {
        title: 'Real-time con WebSockets',
        description:
          'La arquitectura event-driven de Node.js es perfecta para aplicaciones en tiempo real: chats, notificaciones push, dashboards en vivo, juegos multijugador. Socket.IO es la librería más popular, con fallback automático a polling cuando WebSockets no está disponible.',
      },
      {
        title: 'Herramientas de build y CLI',
        description:
          'Webpack, Vite, ESBuild, Rollup, TypeScript compiler, ESLint, Prettier: todo el tooling moderno de frontend está construido sobre Node.js. Es el entorno natural para scripts de automatización, generadores de código, y CLIs con commander o yargs.',
      },
      {
        title: 'Serverless y Edge Functions',
        description:
          'Node.js es el runtime más soportado en plataformas serverless (AWS Lambda, Vercel, Cloudflare Workers con limitaciones). El arranque rápido y el bajo footprint lo hacen ideal para funciones que escalan a cero. Compatible con el modelo de ejecución efímera de serverless.',
      },
    ],
    interviewQA: [
      {
        question: '¿Qué es el Event Loop y cómo funciona en Node.js?',
        answer:
          'El Event Loop permite a Node.js realizar operaciones I/O no bloqueantes aunque sea single-threaded. Delega operaciones a libuv (que usa threads del SO), y cuando terminan, encola los callbacks en la fase correspondiente del loop. Las fases son: timers → pending callbacks → poll → check → close callbacks. El loop continúa mientras haya trabajo pendiente.',
        difficulty: 'medium',
      },
      {
        question: '¿Cuál es la diferencia entre process.nextTick() y setImmediate()?',
        answer:
          'process.nextTick() se ejecuta al final de la fase actual del event loop, antes de pasar a la siguiente fase (incluso antes que las Promises microtasks en algunos contextos). setImmediate() se ejecuta en la fase "check", después del poll de I/O. nextTick tiene mayor prioridad. Usar nextTick excesivamente puede bloquear el event loop (starvation).',
        difficulty: 'hard',
      },
      {
        question: '¿Qué diferencia hay entre require() y import en Node.js?',
        answer:
          'require() es CommonJS: síncrono, carga el módulo completo, resuelve en runtime. import es ESM: estático (analizable en parse time), permite tree-shaking, soporta top-level await, asíncrono. No puedes usar require() en módulos .mjs o con "type":"module". En proyectos modernos con TypeScript, ESM es preferido.',
        difficulty: 'medium',
      },
      {
        question: '¿Cómo harías CPU-intensive work sin bloquear el event loop?',
        answer:
          'Opciones: 1) Worker Threads: hilo separado en el mismo proceso, comparte memoria con SharedArrayBuffer, ideal para cálculos que necesitan acceso a datos. 2) Child Process: proceso separado, más aislado, comunicación IPC. 3) Cluster: múltiples instancias del proceso principal para aprovechar múltiples núcleos. Para operaciones muy frecuentes, considera offload a un servicio dedicado.',
        difficulty: 'hard',
      },
      {
        question: '¿Qué son los Streams y por qué son importantes?',
        answer:
          'Los Streams procesan datos en chunks, permitiendo trabajar con datasets que no caben en memoria. Son especialmente útiles para: leer/escribir archivos grandes, comprimir/descomprimir datos, proxy de respuestas HTTP. Se conectan con pipe(). Manejan backpressure automáticamente: si el consumidor es más lento que el productor, el productor se pausa.',
        difficulty: 'medium',
      },
      {
        question: '¿Cómo manejarías errores en código asíncrono en Node.js?',
        answer:
          'Para Promises/async-await: try/catch en funciones async. Para callbacks: convención error-first (cb(err, data)). Para errores no capturados: process.on("uncaughtException") y process.on("unhandledRejection") — registra el error y reinicia el proceso con un process manager (PM2). En Express/Fastify: middleware de error centralizado. Siempre validar y sanitizar inputs en el límite del sistema.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es backpressure en Streams?',
        answer:
          'Backpressure ocurre cuando el escritor produce datos más rápido de lo que el lector puede consumir, causando que se acumulen en memoria. Node.js Streams manejan esto: write() retorna false cuando el buffer interno está lleno, y emite el evento "drain" cuando se puede escribir de nuevo. pipe() maneja backpressure automáticamente. Ignorarlo puede causar memory leaks en transferencias de archivos grandes.',
        difficulty: 'hard',
      },
      {
        question: '¿Cuál es la diferencia entre dependencies y devDependencies?',
        answer:
          'dependencies son paquetes necesarios en runtime (express, lodash, prisma). devDependencies son solo necesarios durante el desarrollo (typescript, eslint, jest, ts-node). En producción (npm install --production) no se instalan devDependencies, reduciendo el tamaño del node_modules. peerDependencies indica compatibilidad esperada (plugins que requieren una versión del host).',
        difficulty: 'easy',
      },
    ],
    codeExamples: [
      {
        title: 'Servidor HTTP con manejo de rutas',
        description: 'Servidor HTTP nativo con routing básico y manejo de errores.',
        language: 'typescript',
        code: `import { createServer, IncomingMessage, ServerResponse } from 'http';

type Handler = (req: IncomingMessage, res: ServerResponse) => void;

const routes: Record<string, Handler> = {
  'GET /': (_, res) => {
    res.writeHead(200, { 'Content-Type': 'application/json' });
    res.end(JSON.stringify({ message: 'Hello World' }));
  },
  'GET /health': (_, res) => {
    res.writeHead(200, { 'Content-Type': 'application/json' });
    res.end(JSON.stringify({ status: 'ok', uptime: process.uptime() }));
  },
};

const server = createServer((req, res) => {
  const key = \`\${req.method} \${req.url}\`;
  const handler = routes[key];

  if (handler) {
    handler(req, res);
  } else {
    res.writeHead(404, { 'Content-Type': 'application/json' });
    res.end(JSON.stringify({ error: 'Not found' }));
  }
});

server.listen(3000, () => {
  console.log('Server running on http://localhost:3000');
});`,
      },
      {
        title: 'Leer archivo grande con Streams',
        description: 'Procesar un CSV grande línea a línea sin cargar todo en memoria.',
        language: 'typescript',
        code: `import { createReadStream } from 'fs';
import { createInterface } from 'readline';
import { Transform } from 'stream';

async function processLargeCSV(filePath: string): Promise<void> {
  const fileStream = createReadStream(filePath, { encoding: 'utf8' });
  const rl = createInterface({ input: fileStream, crlfDelay: Infinity });

  let lineCount = 0;
  let isHeader = true;

  for await (const line of rl) {
    if (isHeader) {
      isHeader = false;
      continue; // Skip header row
    }

    const [id, name, email] = line.split(',');
    // Procesar cada línea sin acumular en memoria
    await processRow({ id, name, email });
    lineCount++;

    if (lineCount % 10_000 === 0) {
      console.log(\`Processed \${lineCount} rows...\`);
    }
  }

  console.log(\`Done. Total rows: \${lineCount}\`);
}

async function processRow(row: { id: string; name: string; email: string }) {
  // Lógica de negocio por fila
}

processLargeCSV('./data/users.csv').catch(console.error);`,
      },
      {
        title: 'Worker Threads para CPU-intensive work',
        description: 'Ejecutar cálculos pesados en un hilo separado sin bloquear el event loop.',
        language: 'typescript',
        code: `// worker.ts
import { workerData, parentPort } from 'worker_threads';

function heavyCalculation(n: number): number {
  // Simulación de trabajo CPU-intensivo
  let result = 0;
  for (let i = 0; i < n; i++) {
    result += Math.sqrt(i) * Math.sin(i);
  }
  return result;
}

const result = heavyCalculation(workerData.n);
parentPort?.postMessage({ result });

// main.ts
import { Worker } from 'worker_threads';
import { resolve } from 'path';

function runWorker(n: number): Promise<number> {
  return new Promise((resolve, reject) => {
    const worker = new Worker('./worker.js', { workerData: { n } });
    worker.on('message', ({ result }) => resolve(result));
    worker.on('error', reject);
    worker.on('exit', (code) => {
      if (code !== 0) reject(new Error(\`Worker stopped with exit code \${code}\`));
    });
  });
}

async function main() {
  console.log('Starting heavy calculation...');
  // No bloquea el event loop
  const [r1, r2] = await Promise.all([
    runWorker(10_000_000),
    runWorker(10_000_000),
  ]);
  console.log('Results:', r1, r2);
}

main();`,
      },
    ],
    quiz: [
      {
        question: '¿En qué fase del Event Loop se ejecutan los callbacks de setTimeout?',
        options: ['Poll', 'Check', 'Timers', 'Pending callbacks'],
        correctIndex: 2,
        explanation:
          'Los callbacks de setTimeout y setInterval se ejecutan en la fase "timers" del Event Loop. Sin embargo, solo se ejecutan si el delay mínimo ha transcurrido; si no, el loop continúa a la siguiente fase.',
      },
      {
        question: '¿Qué ocurre si lanzas un error dentro de un EventEmitter sin listener de "error"?',
        options: [
          'El error se ignora silenciosamente',
          'Se convierte en una Promise rechazada',
          'Node.js lanza una excepción no capturada y puede crashear el proceso',
          'El error se loguea automáticamente en stderr',
        ],
        correctIndex: 2,
        explanation:
          'Si un EventEmitter emite el evento "error" y no hay listeners registrados para ese evento, Node.js lanza una excepción de tipo Error. Si no hay un handler de uncaughtException, el proceso termina. Siempre registra un listener de "error" en tus EventEmitters.',
      },
      {
        question: '¿Cuál es la diferencia principal entre process.nextTick() y Promise.resolve().then()?',
        options: [
          'No hay diferencia, son equivalentes',
          'nextTick se ejecuta antes que los microtasks de Promises',
          'Promise.resolve().then() se ejecuta antes que nextTick',
          'nextTick es más lento que Promises',
        ],
        correctIndex: 1,
        explanation:
          'process.nextTick() tiene mayor prioridad que la cola de microtasks (Promises). nextTick callbacks se procesan al finalizar la operación actual, antes de que el event loop continúe y antes de los microtasks de Promises.',
      },
      {
        question: '¿Qué módulo usarías para aprovechar todos los núcleos de CPU en Node.js?',
        options: ['os', 'worker_threads', 'cluster', 'child_process'],
        correctIndex: 2,
        explanation:
          'El módulo cluster permite crear múltiples procesos hijos (workers) que comparten el mismo puerto del servidor. Cada proceso corre en su propio núcleo de CPU. Es la forma tradicional de escalar Node.js horizontalmente en una sola máquina.',
      },
      {
        question: '¿Qué significa que Node.js sea "non-blocking I/O"?',
        options: [
          'Node.js usa múltiples threads para I/O',
          'Las operaciones I/O no detienen la ejecución del thread principal; usan callbacks cuando terminan',
          'Node.js no puede leer archivos del sistema',
          'El I/O se ejecuta en el mismo thread pero más rápido',
        ],
        correctIndex: 1,
        explanation:
          'Non-blocking I/O significa que cuando Node.js inicia una operación I/O (leer archivo, query DB, llamada HTTP), no espera a que termine. En cambio, registra un callback y continúa ejecutando otro código. Cuando el I/O termina, el callback se encola para ejecutarse en el event loop.',
      },
      {
        question: '¿Qué es el operador "??" (nullish coalescing) y en qué difiere de "||"?',
        options: [
          'Son idénticos en comportamiento',
          '?? solo activa el fallback para null/undefined; || activa para cualquier valor falsy',
          '|| solo activa el fallback para null/undefined; ?? activa para cualquier valor falsy',
          '?? es más lento que ||',
        ],
        correctIndex: 1,
        explanation:
          '?? solo usa el valor derecho si el izquierdo es null o undefined. || usa el valor derecho si el izquierdo es cualquier valor falsy (0, "", false, NaN). Entonces: 0 ?? 5 = 0, pero 0 || 5 = 5. Esto hace ?? más seguro para valores numéricos o booleanos que pueden ser falsy legítimamente.',
      },
      {
        question: '¿Qué hace el flag --inspect en Node.js?',
        options: [
          'Activa el modo strict de JavaScript',
          'Habilita el debugging remoto con Chrome DevTools o VSCode',
          'Muestra el AST del módulo cargado',
          'Activa la inspección de memoria automática',
        ],
        correctIndex: 1,
        explanation:
          'node --inspect (o --inspect-brk para pausar en el inicio) habilita el protocolo de debugging de V8. Puedes conectarte desde Chrome (chrome://inspect) o VSCode para poner breakpoints, inspeccionar variables y hacer profiling de CPU y memoria en tiempo real.',
      },
      {
        question: '¿Cuándo usarías Buffer.alloc() en lugar de Buffer.from()?',
        options: [
          'Buffer.alloc() es siempre preferible',
          'Buffer.from() para crear desde datos existentes; Buffer.alloc() para crear un buffer vacío de tamaño fijo',
          'Buffer.alloc() es para strings; Buffer.from() para números',
          'No hay diferencia funcional',
        ],
        correctIndex: 1,
        explanation:
          'Buffer.from(data) crea un Buffer a partir de datos existentes (string, array, ArrayBuffer). Buffer.alloc(size) crea un Buffer de tamaño fijo lleno de ceros (seguro). Existe Buffer.allocUnsafe(size) que es más rápido pero puede contener datos de memoria anterior — solo úsalo si inmediatamente sobrescribes todo el buffer.',
      },
    ],
    exercises: [
      {
        title: 'Servidor de archivos estáticos',
        difficulty: 'easy',
        description:
          'Crea un servidor HTTP nativo (sin Express) que sirva archivos estáticos desde una carpeta "public". Debe manejar correctamente los Content-Type según la extensión del archivo (.html, .css, .js, .json, .png). Retorna 404 si el archivo no existe y 500 si hay un error de lectura. No uses path traversal (../../etc/passwd debe retornar 403).',
        hints: [
          'Usa path.join() y path.resolve() para construir rutas seguras',
          'Verifica que la ruta resuelta empieza con el directorio "public"',
          'Usa fs.createReadStream() para servir archivos (stream, no readFileSync)',
          'Mapea extensiones a MIME types con un objeto simple',
        ],
        tags: ['HTTP', 'Streams', 'Seguridad'],
      },
      {
        title: 'Rate limiter en memoria',
        difficulty: 'medium',
        description:
          'Implementa un middleware de rate limiting para Express que limite las requests por IP: máximo N requests por ventana de tiempo (sliding window). Usa un Map en memoria para trackear los contadores. Incluye los headers: X-RateLimit-Limit, X-RateLimit-Remaining, Retry-After. Limpia automáticamente las entradas expiradas.',
        hints: [
          'Guarda en el Map: { count, resetTime } por IP',
          'Sliding window: mantén un array de timestamps de las últimas N requests',
          'Usa setInterval para limpiar entradas expiradas (evita memory leak)',
          'req.ip puede ser un proxy; considera X-Forwarded-For con trust proxy',
        ],
        tags: ['Middleware', 'Express', 'Seguridad'],
      },
      {
        title: 'Pipeline de transformación de datos con Streams',
        difficulty: 'hard',
        description:
          'Lee un archivo JSON Lines (un objeto JSON por línea) de varios GB, transforma cada objeto (normaliza campos, filtra registros inválidos), y escribe el resultado en un nuevo archivo. Usa Transform streams. Mide el uso de memoria (debe mantenerse constante independientemente del tamaño del archivo) y el throughput (MB/s). Maneja correctamente los errores en el pipeline.',
        hints: [
          'Usa stream.pipeline() en lugar de pipe() para manejo automático de errores',
          'Implementa una clase que extiende Transform y override _transform(chunk, encoding, callback)',
          'Acumula fragmentos de línea entre chunks usando un buffer interno de string',
          'Usa process.memoryUsage().heapUsed para medir memoria',
        ],
        tags: ['Streams', 'Performance', 'I/O'],
      },
      {
        title: 'CLI con comandos y flags',
        difficulty: 'easy',
        description:
          'Crea una herramienta CLI que genere archivos de componentes TypeScript/React a partir de templates. Comandos: generate component <name> [--dir <path>] [--style <css|scss|none>]. Usa process.argv manualmente (sin librerías). Muestra ayuda si el comando es inválido. El template debe incluir el nombre del componente en camelCase y PascalCase.',
        hints: [
          'process.argv[0] es node, [1] es el script, [2] en adelante son los argumentos',
          'Implementa un parser simple de flags: busca "--" y toma el siguiente argumento',
          'Usa fs.mkdirSync con recursive: true para crear directorios anidados',
          'Usa template literals para el contenido del archivo generado',
        ],
        tags: ['CLI', 'fs', 'Tooling'],
      },
      {
        title: 'Event Bus tipado con TypeScript',
        difficulty: 'medium',
        description:
          'Implementa un EventEmitter tipado con TypeScript que garantice en tiempo de compilación que los listeners reciben los tipos correctos para cada evento. El bus debe soportar on, off, once, y emit. Bonus: implementa wildcards y namespacing (user:created, user:deleted, user:*).',
        hints: [
          'Usa un tipo genérico: EventMap extends Record<string, unknown>',
          'El método on<K extends keyof EventMap>(event: K, listener: (data: EventMap[K]) => void)',
          'Internamente usa un Map<string, Set<Function>> para los listeners',
          'Para wildcards, itera sobre todas las keys y compara con startsWith',
        ],
        tags: ['TypeScript', 'Patrones', 'EventEmitter'],
      },
    ],
  },
  {
    slug: 'nestjs',
    title: 'NestJS',
    shortDescription: 'Framework Node.js progresivo con arquitectura modular, DI y soporte nativo de TypeScript.',
    icon: 'account_tree',
    color: 'text-red-500',
    category: 'backend',
    concepts: [
      {
        title: 'Módulos',
        description:
          'Los módulos son la unidad de organización fundamental en NestJS. Cada módulo agrupa providers, controllers y otros módulos relacionados. El AppModule es el módulo raíz. Los módulos son singletons por defecto (a menos que sean dinámicos). Los módulos globales (@Global()) hacen sus providers disponibles sin importarlos explícitamente. Los módulos dinámicos permiten configuración en tiempo de carga (forRoot/forFeature pattern).',
        tags: ['Arquitectura', 'Organización'],
      },
      {
        title: 'Inyección de Dependencias (DI)',
        description:
          'NestJS usa un contenedor IoC (Inversion of Control) para DI. Los providers se registran en el módulo y se inyectan via constructor con @Injectable(). El decorador @Inject(TOKEN) permite inyectar por token personalizado. Los scopes controlan el ciclo de vida: DEFAULT (singleton), REQUEST (nueva instancia por request), TRANSIENT (nueva instancia por inyección). La DI hace el código testeable y desacoplado.',
        tags: ['DI', 'IoC', 'Arquitectura'],
      },
      {
        title: 'Controllers',
        description:
          'Los Controllers manejan las requests HTTP entrantes. Se decoran con @Controller("path"). Los métodos usan decoradores HTTP: @Get(), @Post(), @Put(), @Patch(), @Delete(). Para acceder a la request: @Body(), @Param(), @Query(), @Headers(), @Req(). Los controllers deben ser delgados: delegan la lógica de negocio a Services. El router de NestJS soporta wildcards, parámetros opcionales y expresiones regulares.',
        tags: ['HTTP', 'Routing'],
      },
      {
        title: 'Providers y Services',
        description:
          'Un provider es cualquier clase decorada con @Injectable(). Los Services son el lugar para la lógica de negocio, acceso a datos e integraciones. Los Repositories (patrón con TypeORM/Prisma) son providers especializados. También son providers: Guards, Interceptors, Pipes, y Filters. Los custom providers (useValue, useFactory, useClass) permiten registrar cualquier valor, factory o clase alternativa.',
        tags: ['DI', 'Lógica de Negocio'],
      },
      {
        title: 'Pipes y Validación',
        description:
          'Los Pipes transforman o validan datos de entrada. ValidationPipe (con class-validator + class-transformer) valida DTOs automáticamente: @IsEmail(), @IsString(), @MinLength(8). Configurar globalmente en main.ts con app.useGlobalPipes(new ValidationPipe({ whitelist: true, forbidNonWhitelisted: true, transform: true })). ParseIntPipe, ParseUUIDPipe, ParseEnumPipe para transformación de tipos. Los Pipes personalizados implementan la interfaz PipeTransform.',
        tags: ['Validación', 'DTOs', 'Seguridad'],
      },
      {
        title: 'Guards y Autorización',
        description:
          'Los Guards determinan si una request puede continuar (autenticación/autorización). Implementan CanActivate con el método canActivate(context: ExecutionContext). Se aplican con @UseGuards() a nivel de controller, método o globalmente. JwtAuthGuard verifica tokens JWT. RolesGuard combina con el decorador @Roles() para RBAC. El ExecutionContext permite acceder a la request y switchear entre HTTP, WebSocket y microservicios.',
        tags: ['Seguridad', 'Auth', 'RBAC'],
      },
      {
        title: 'Interceptors',
        description:
          'Los Interceptors envuelven la ejecución de handlers con lógica adicional. Usan RxJS Observables. Casos de uso: logging de requests/responses, transformación de respuestas, caching, timeout, manejo de errores. El método intercept(context, next) recibe el ExecutionContext y el CallHandler. next.handle() retorna un Observable; puedes transformarlo con operadores RxJS (map, tap, catchError).',
        tags: ['Middleware', 'AOP', 'RxJS'],
      },
      {
        title: 'Exception Filters',
        description:
          'Los Exception Filters capturan excepciones lanzadas durante el manejo de una request y formatean la respuesta de error. @Catch(HttpException) captura excepciones específicas. @Catch() (sin argumentos) captura todo. HttpException y sus subclases (BadRequestException, UnauthorizedException, NotFoundException) generan respuestas con el status correcto. Implementa ExceptionFilter con el método catch(exception, host: ArgumentsHost).',
        tags: ['Error Handling', 'HTTP'],
      },
      {
        title: 'Microservicios y Transportes',
        description:
          'NestJS soporta múltiples transportes para microservicios: TCP, Redis, NATS, RabbitMQ, Kafka, gRPC. Los mensajes usan el patrón message/event: @MessagePattern() para request-response, @EventPattern() para fire-and-forget. ClientProxy permite enviar mensajes desde un microservicio a otro. Hybrid applications pueden ser tanto HTTP server como microservice al mismo tiempo.',
        tags: ['Microservicios', 'Mensajería'],
      },
      {
        title: 'Testing en NestJS',
        description:
          'NestJS tiene soporte de testing integrado con @nestjs/testing. Test.createTestingModule() crea un módulo de prueba con DI. Puedes reemplazar providers con mocks: { provide: UserService, useValue: mockUserService }. Para testing de integración, compila el módulo completo. Para e2e, usa supertest con el servidor HTTP de NestJS. Los Guards e Interceptors se pueden mockear fácilmente.',
        tags: ['Testing', 'Jest', 'DI'],
      },
    ],
    useCases: [
      {
        title: 'APIs empresariales con estructura sólida',
        description:
          'NestJS impone una arquitectura modular y escalable desde el inicio. Ideal para equipos medianos-grandes donde la consistencia y las convenciones son importantes. La estructura de módulos facilita la división del trabajo y el onboarding de nuevos desarrolladores.',
      },
      {
        title: 'Microservicios con múltiples transportes',
        description:
          'NestJS incluye soporte nativo para microservicios con TCP, Redis, RabbitMQ, Kafka y gRPC sin configuración adicional. El mismo código puede funcionar como servidor HTTP y como microservicio simultáneamente (hybrid application).',
      },
      {
        title: 'APIs GraphQL',
        description:
          'El módulo @nestjs/graphql integra Apollo Server o Mercurius. Soporta dos enfoques: code-first (decoradores TypeScript generan el schema) y schema-first (el schema SDL define los tipos). La DI de NestJS se integra perfectamente con resolvers, dataloaders y guards de GraphQL.',
      },
      {
        title: 'Backend for Frontend (BFF)',
        description:
          'NestJS es ideal para BFFs que agregan múltiples microservicios y adaptan los datos para cada cliente (web, mobile). Los Interceptors permiten transformar respuestas fácilmente; los Guards centralizan la autenticación; los módulos de caché mejoran el performance.',
      },
      {
        title: 'Aplicaciones con WebSockets',
        description:
          'El módulo @nestjs/websockets provee Gateways que funcionan igual que los Controllers pero para WebSocket events. @WebSocketGateway() con @SubscribeMessage() maneja eventos. Se puede usar con Socket.IO o WebSockets nativo. Los Guards y Pipes funcionan igual que en HTTP.',
      },
    ],
    interviewQA: [
      {
        question: '¿Cuál es la diferencia entre un Module, un Controller y un Provider en NestJS?',
        answer:
          'Module: unidad de organización que agrupa Controllers y Providers relacionados. Define qué exporta (disponible para otros módulos) y qué importa. Controller: maneja las requests HTTP, mapea rutas a métodos. Solo debe orquestar, no contener lógica de negocio. Provider: cualquier clase inyectable (@Injectable). Los Services son providers con lógica de negocio. También son providers los Guards, Pipes, Interceptors y Filters.',
        difficulty: 'easy',
      },
      {
        question: '¿Cómo funciona la Inyección de Dependencias en NestJS?',
        answer:
          'NestJS usa un contenedor IoC que resuelve dependencias automáticamente. Al registrar un provider en un módulo, el contenedor lo instancia (singleton por defecto) y lo inyecta donde sea requerido via constructor. Los tokens son las clases mismas o strings/symbols para providers customizados. El contenedor maneja el ciclo de vida: crea instancias al iniciar el módulo y las destruye al finalizar la app (onModuleDestroy).',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es el ciclo de vida de una request en NestJS?',
        answer:
          'El orden es: Middleware → Guards → Interceptors (before) → Pipes → Controller Handler → Interceptors (after) → Exception Filters (si hay error). Middleware: modifican req/res sin acceso al contexto de NestJS. Guards: deciden si continuar (autenticación/autorización). Interceptors: envuelven el handler con RxJS. Pipes: transforman/validan parámetros. Filters: capturan excepciones no manejadas.',
        difficulty: 'hard',
      },
      {
        question: '¿Cuándo usarías un Interceptor vs un Middleware en NestJS?',
        answer:
          'Middleware: para lógica genérica que no necesita contexto de NestJS (cors, helmet, body-parser, logging básico). Es Express/Fastify nativo. Interceptor: cuando necesitas acceso al ExecutionContext (saber qué handler se ejecuta, qué clase es, metadata de decoradores), acceso a la respuesta del handler (para transformarla o cachearla), o cuando necesitas usar RxJS operators como timeout(), catchError().',
        difficulty: 'medium',
      },
      {
        question: '¿Cómo implementarías autenticación JWT en NestJS?',
        answer:
          'Pasos: 1) @nestjs/passport + passport-jwt. 2) JwtStrategy extends PassportStrategy: valida el token en validate(), retorna el payload. 3) JwtAuthGuard extends AuthGuard("jwt"): aplica la estrategia. 4) AuthModule registra JwtModule.registerAsync() con la secretKey. 5) @UseGuards(JwtAuthGuard) en controllers o globalmente. 6) @Public() custom decorator para rutas públicas con reflector en el guard. 7) El payload validado queda en req.user.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué hace whitelist: true en ValidationPipe?',
        answer:
          'whitelist: true elimina automáticamente de los DTOs cualquier propiedad que no tenga un decorador de class-validator. Si el cliente envía campos extra no declarados en el DTO, son removidos antes de llegar al handler. Combinado con forbidNonWhitelisted: true, lanza una BadRequestException si el cliente envía propiedades no declaradas en lugar de simplemente ignorarlas. Esto previene mass assignment vulnerabilities.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué diferencia hay entre @Module imports, exports y providers?',
        answer:
          'providers: clases @Injectable() disponibles dentro de este módulo (DI local). imports: otros módulos cuyos providers exportados estarán disponibles en este módulo. exports: subset de providers de este módulo que estarán disponibles cuando otros módulos importen este módulo. Para hacer un provider global sin importarlo en cada módulo, decora el módulo con @Global() y exporta el provider.',
        difficulty: 'medium',
      },
      {
        question: '¿Cómo testearías un Service que tiene dependencias en NestJS?',
        answer:
          'Con Test.createTestingModule({ providers: [UserService, { provide: UserRepository, useValue: mockRepo }] }). Crea mocks de las dependencias con jest.fn() o jest.createMockFromModule(). compile() construye el módulo y resuelve DI. module.get(UserService) obtiene la instancia con las dependencias mockeadas inyectadas. Para HTTP, usa supertest con app.getHttpServer() en tests e2e.',
        difficulty: 'hard',
      },
    ],
    codeExamples: [
      {
        title: 'CRUD Module completo con Prisma',
        description: 'Módulo NestJS con Controller, Service, DTOs y validación usando Prisma.',
        language: 'typescript',
        code: `// users.module.ts
import { Module } from '@nestjs/common';
import { UsersController } from './users.controller';
import { UsersService } from './users.service';
import { PrismaModule } from '../prisma/prisma.module';

@Module({
  imports: [PrismaModule],
  controllers: [UsersController],
  providers: [UsersService],
  exports: [UsersService],
})
export class UsersModule {}

// create-user.dto.ts
import { IsEmail, IsString, MinLength, IsOptional } from 'class-validator';

export class CreateUserDto {
  @IsString()
  @MinLength(2)
  name: string;

  @IsEmail()
  email: string;

  @IsString()
  @MinLength(8)
  password: string;

  @IsOptional()
  @IsString()
  avatar?: string;
}

// users.service.ts
import { Injectable, NotFoundException, ConflictException } from '@nestjs/common';
import { PrismaService } from '../prisma/prisma.service';
import { CreateUserDto } from './dto/create-user.dto';
import * as bcrypt from 'bcrypt';

@Injectable()
export class UsersService {
  constructor(private prisma: PrismaService) {}

  async create(dto: CreateUserDto) {
    const exists = await this.prisma.user.findUnique({ where: { email: dto.email } });
    if (exists) throw new ConflictException('Email already in use');

    const hash = await bcrypt.hash(dto.password, 10);
    return this.prisma.user.create({
      data: { ...dto, password: hash },
      select: { id: true, name: true, email: true, createdAt: true },
    });
  }

  async findAll() {
    return this.prisma.user.findMany({
      select: { id: true, name: true, email: true, createdAt: true },
    });
  }

  async findOne(id: string) {
    const user = await this.prisma.user.findUnique({ where: { id } });
    if (!user) throw new NotFoundException(\`User \${id} not found\`);
    return user;
  }
}

// users.controller.ts
import { Controller, Get, Post, Body, Param, ParseUUIDPipe, UseGuards } from '@nestjs/common';
import { UsersService } from './users.service';
import { CreateUserDto } from './dto/create-user.dto';
import { JwtAuthGuard } from '../auth/guards/jwt-auth.guard';

@Controller('users')
@UseGuards(JwtAuthGuard)
export class UsersController {
  constructor(private usersService: UsersService) {}

  @Post()
  create(@Body() dto: CreateUserDto) {
    return this.usersService.create(dto);
  }

  @Get()
  findAll() {
    return this.usersService.findAll();
  }

  @Get(':id')
  findOne(@Param('id', ParseUUIDPipe) id: string) {
    return this.usersService.findOne(id);
  }
}`,
      },
      {
        title: 'Guard JWT con roles (RBAC)',
        description: 'Implementación de autenticación JWT y autorización basada en roles.',
        language: 'typescript',
        code: `// roles.decorator.ts
import { SetMetadata } from '@nestjs/common';
export const ROLES_KEY = 'roles';
export const Roles = (...roles: string[]) => SetMetadata(ROLES_KEY, roles);

// public.decorator.ts
import { SetMetadata } from '@nestjs/common';
export const IS_PUBLIC_KEY = 'isPublic';
export const Public = () => SetMetadata(IS_PUBLIC_KEY, true);

// jwt-auth.guard.ts
import { Injectable, ExecutionContext } from '@nestjs/common';
import { Reflector } from '@nestjs/core';
import { AuthGuard } from '@nestjs/passport';
import { IS_PUBLIC_KEY } from '../decorators/public.decorator';

@Injectable()
export class JwtAuthGuard extends AuthGuard('jwt') {
  constructor(private reflector: Reflector) {
    super();
  }

  canActivate(context: ExecutionContext) {
    const isPublic = this.reflector.getAllAndOverride<boolean>(IS_PUBLIC_KEY, [
      context.getHandler(),
      context.getClass(),
    ]);
    if (isPublic) return true;
    return super.canActivate(context);
  }
}

// roles.guard.ts
import { Injectable, CanActivate, ExecutionContext, ForbiddenException } from '@nestjs/common';
import { Reflector } from '@nestjs/core';
import { ROLES_KEY } from '../decorators/roles.decorator';

@Injectable()
export class RolesGuard implements CanActivate {
  constructor(private reflector: Reflector) {}

  canActivate(context: ExecutionContext): boolean {
    const requiredRoles = this.reflector.getAllAndOverride<string[]>(ROLES_KEY, [
      context.getHandler(),
      context.getClass(),
    ]);
    if (!requiredRoles) return true;

    const { user } = context.switchToHttp().getRequest();
    const hasRole = requiredRoles.some(role => user.roles?.includes(role));
    if (!hasRole) throw new ForbiddenException('Insufficient permissions');
    return true;
  }
}

// uso en controller
@Get('admin/stats')
@Roles('admin')
@UseGuards(RolesGuard)
getAdminStats() {
  return this.statsService.getAll();
}

@Get('profile')
@Public()
getPublicProfile(@Param('id') id: string) {
  return this.usersService.getPublicProfile(id);
}`,
      },
      {
        title: 'Interceptor de logging y transformación',
        description: 'Interceptor que loguea requests y transforma respuestas al formato { data, meta }.',
        language: 'typescript',
        code: `import {
  Injectable,
  NestInterceptor,
  ExecutionContext,
  CallHandler,
  Logger,
} from '@nestjs/common';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

export interface ApiResponse<T> {
  data: T;
  meta: {
    timestamp: string;
    path: string;
    duration: number;
  };
}

@Injectable()
export class TransformInterceptor<T>
  implements NestInterceptor<T, ApiResponse<T>>
{
  private logger = new Logger(TransformInterceptor.name);

  intercept(
    context: ExecutionContext,
    next: CallHandler,
  ): Observable<ApiResponse<T>> {
    const start = Date.now();
    const request = context.switchToHttp().getRequest();
    const { method, url } = request;

    return next.handle().pipe(
      tap(() => {
        const duration = Date.now() - start;
        this.logger.log(\`\${method} \${url} — \${duration}ms\`);
      }),
      map((data) => ({
        data,
        meta: {
          timestamp: new Date().toISOString(),
          path: url,
          duration: Date.now() - start,
        },
      })),
    );
  }
}

// Registrar globalmente en main.ts:
// app.useGlobalInterceptors(new TransformInterceptor());`,
      },
    ],
    quiz: [
      {
        question: '¿Cuál es el orden correcto del ciclo de vida de una request en NestJS?',
        options: [
          'Pipes → Guards → Middleware → Interceptors → Handler',
          'Middleware → Guards → Interceptors → Pipes → Handler',
          'Guards → Middleware → Pipes → Handler → Interceptors',
          'Interceptors → Middleware → Guards → Handler → Pipes',
        ],
        correctIndex: 1,
        explanation:
          'El orden es: Middleware (Express-level) → Guards (¿puede pasar?) → Interceptors (before) → Pipes (transformación/validación) → Handler (controller method) → Interceptors (after) → Exception Filters (si error). Este orden es crítico para entender cuándo aplicar cada pieza.',
      },
      {
        question: '¿Qué hace @Injectable({ scope: Scope.REQUEST })?',
        options: [
          'El provider solo está disponible dentro del módulo que lo declara',
          'Se crea una nueva instancia del provider por cada request HTTP',
          'El provider se destruye al terminar la aplicación',
          'El provider es compartido entre todos los módulos',
        ],
        correctIndex: 1,
        explanation:
          'Scope.REQUEST crea una nueva instancia del provider por cada request entrante. Útil cuando necesitas que el provider tenga acceso al contexto de la request (ej: el usuario autenticado). El scope DEFAULT (singleton) es el más eficiente y debe preferirse cuando no se necesita estado por request.',
      },
      {
        question: '¿Cómo hace NestJS para que un módulo sea accesible globalmente sin importarlo en cada módulo?',
        options: [
          'Usando @Controller({ global: true })',
          'Exportando el módulo desde AppModule',
          'Decorando el módulo con @Global()',
          'Usando providers: [{ provide: X, useGlobal: true }]',
        ],
        correctIndex: 2,
        explanation:
          '@Global() hace que los providers exportados por el módulo estén disponibles en toda la aplicación sin necesidad de importar el módulo. Úsalo con moderación (DatabaseModule, ConfigModule) para no perder el beneficio de la modularidad explícita.',
      },
      {
        question: '¿Para qué sirve whitelist: true en ValidationPipe?',
        options: [
          'Solo permite requests de IPs en whitelist',
          'Elimina propiedades del body que no están declaradas en el DTO',
          'Valida que todos los campos requeridos estén presentes',
          'Loguea todos los campos recibidos para auditoría',
        ],
        correctIndex: 1,
        explanation:
          'whitelist: true stripea automáticamente propiedades del objeto recibido que no tienen decoradores de class-validator en el DTO. Previene mass assignment: si el cliente envía un campo "isAdmin" que no está en el DTO, es removido antes de llegar al handler.',
      },
      {
        question: '¿Cuál es la diferencia entre un Guard y un Middleware en NestJS?',
        options: [
          'No hay diferencia práctica',
          'Middleware tiene acceso al ExecutionContext de NestJS; Guard no',
          'Guard tiene acceso al ExecutionContext de NestJS; Middleware no',
          'Guard solo funciona en WebSockets; Middleware solo en HTTP',
        ],
        correctIndex: 2,
        explanation:
          'Los Guards tienen acceso al ExecutionContext de NestJS, que sabe qué handler se va a ejecutar, qué clase, y la metadata de decoradores (como @Roles()). Los Middleware son Express/Fastify puros: solo tienen (req, res, next) sin acceso al contexto de NestJS. Los Guards son la herramienta correcta para autorización.',
      },
      {
        question: '¿Cómo se inyecta un provider usando un token personalizado (string)?',
        options: [
          'Con @Injectable("MY_TOKEN") en la clase',
          'Con @Inject("MY_TOKEN") en el parámetro del constructor',
          'Con @UseProvider("MY_TOKEN") en el controller',
          'Con providers: [{ provide: "MY_TOKEN", inject: true }]',
        ],
        correctIndex: 1,
        explanation:
          '@Inject("MY_TOKEN") en el parámetro del constructor le dice al contenedor de NestJS qué provider buscar por ese token. Los custom providers se registran como: { provide: "MY_TOKEN", useValue: algo } o useFactory/useClass. Los tokens symbols son preferibles a strings para evitar colisiones.',
      },
      {
        question: '¿Qué hace app.useGlobalPipes() en main.ts?',
        options: [
          'Registra pipes que solo aplican a las rutas del AppModule',
          'Aplica el pipe a todas las routes de toda la aplicación',
          'Registra pipes para WebSocket gateways únicamente',
          'Aplica pipes solo a los DTOs que tienen @IsGlobal()',
        ],
        correctIndex: 1,
        explanation:
          'useGlobalPipes() (y useGlobalGuards, useGlobalInterceptors, useGlobalFilters) aplican la pieza globalmente a toda la aplicación. La diferencia con registrar en providers del AppModule es que los pipes globales registrados así no tienen acceso a DI (no pueden inyectar Services). Para DI en globales, usar APP_PIPE token en providers.',
      },
      {
        question: '¿Qué retorna el método handle() del CallHandler en un Interceptor?',
        options: [
          'Una Promise con el resultado del handler',
          'Un Observable con el resultado del handler',
          'El objeto Response de Express/Fastify',
          'Un array con [error, result]',
        ],
        correctIndex: 1,
        explanation:
          'CallHandler.handle() retorna un Observable. Esto es clave: puedes usar operadores RxJS para transformar la respuesta (map), loguear (tap), manejar errores (catchError), agregar timeouts (timeout), o reintentar (retry). Es la integración de RxJS en el pipeline de NestJS.',
      },
    ],
    exercises: [
      {
        title: 'Auth module con JWT y refresh tokens',
        difficulty: 'hard',
        description:
          'Implementa un módulo de autenticación completo: registro, login, refresh token y logout. Access token con 15min de expiración, refresh token con 7 días guardado en base de datos (con hash). El endpoint /auth/refresh valida el refresh token, lo invalida y emite un par nuevo. Implementa JwtStrategy, LocalStrategy, y los guards correspondientes. El logout invalida el refresh token activo.',
        hints: [
          'Usa dos JwtModule.register(): uno para access token, otro para refresh token con diferente secret',
          'Guarda el hash del refresh token en la DB (bcrypt), no el token en claro',
          'JwtRefreshStrategy valida el token y verifica que coincida con el hash en DB',
          'Al hacer logout, pone a null el refreshTokenHash del usuario en DB',
        ],
        tags: ['Auth', 'JWT', 'Seguridad'],
      },
      {
        title: 'Módulo de caché con Redis',
        difficulty: 'medium',
        description:
          'Crea un CacheModule que use Redis vía @nestjs/cache-manager. Implementa un CacheInterceptor personalizado que cachee las respuestas GET basándose en la URL + query params. El TTL debe ser configurable por ruta via un decorador @CacheTime(seconds). Las rutas POST/PUT/DELETE deben invalidar el caché relacionado. El caché debe funcionar en modo cluster con Redis.',
        hints: [
          'Usa @CacheKey() y @CacheTTL() de @nestjs/cache-manager o implementa decoradores propios',
          'En el interceptor, genera la cache key con req.url y serializa con JSON.stringify',
          'Usa Reflector para leer el metadata del decorador @CacheTime',
          'Para invalidación, usa un patrón de keys: cache:GET:/users:* y usa SCAN + DEL en Redis',
        ],
        tags: ['Redis', 'Performance', 'Interceptors'],
      },
      {
        title: 'Microservicio con RabbitMQ',
        difficulty: 'hard',
        description:
          'Crea dos aplicaciones NestJS: un API Gateway (HTTP) y un microservicio de notificaciones (RabbitMQ). El gateway emite eventos via ClientProxy cuando se crea un usuario. El microservicio escucha el evento, procesa la notificación (simula envío de email con un delay) y responde con el resultado. Implementa dead letter queues para mensajes que fallan 3 veces.',
        hints: [
          'Usa ClientsModule.register([{ name: "NOTIFICATIONS", transport: Transport.RMQ }])',
          'En el microservicio: @EventPattern("user.created") para fire-and-forget',
          'Para request-response: @MessagePattern("notification.send") + client.send()',
          'Configura DLQ en RabbitMQ con x-dead-letter-exchange y x-max-retry headers',
        ],
        tags: ['Microservicios', 'RabbitMQ', 'Mensajería'],
      },
      {
        title: 'API con paginación, filtros y ordenamiento',
        difficulty: 'medium',
        description:
          'Implementa un endpoint GET /products que soporte: paginación (page, limit con defaults y máximos), filtros (price_min, price_max, category, in_stock), búsqueda por texto (q, con full-text search), y ordenamiento (sort=price&order=asc). Valida todos los query params con DTOs y ValidationPipe. La respuesta debe incluir { data, total, page, totalPages }.',
        hints: [
          'Crea un PaginationDto con @Type(() => Number) para convertir strings a números',
          'Usa @Transform para sanitizar el parámetro q (trim, max length)',
          'En el service, construye el where de Prisma dinámicamente con los filtros presentes',
          'Usa Promise.all([findMany, count]) para obtener datos y total en paralelo',
        ],
        tags: ['REST', 'Prisma', 'DTOs', 'Validación'],
      },
      {
        title: 'WebSocket Gateway con rooms',
        difficulty: 'medium',
        description:
          'Implementa un Gateway de WebSockets para un chat en tiempo real. Los usuarios se autentican con JWT al conectar. Soporta: join-room, leave-room, send-message (a una sala específica), y typing-indicator. Los mensajes se persisten en PostgreSQL. Al conectar, el usuario recibe los últimos 50 mensajes de sus salas activas.',
        hints: [
          '@WebSocketGateway({ cors: true }) y usar handleConnection para autenticar el token',
          'this.server.to(roomId).emit() para broadcast a una sala',
          'socket.join(roomId) y socket.leave(roomId) para manejar las salas',
          'Inyecta el MessagesService para persistir y consultar mensajes',
        ],
        tags: ['WebSockets', 'Real-time', 'Auth'],
      },
    ],
  },
  {
    slug: 'python',
    title: 'Python',
    shortDescription: 'Lenguaje interpretado de alto nivel, legible y versátil, dominante en data science, AI y scripting.',
    icon: 'code',
    color: 'text-yellow-400',
    category: 'languages',
    concepts: [
      {
        title: 'Tipos de datos y mutabilidad',
        description:
          'Python tiene tipos inmutables (int, float, str, tuple, frozenset) y mutables (list, dict, set). La mutabilidad importa: pasar una lista a una función permite modificarla in-place; pasar un string no. Los integers pequeños y strings se internan (cached) en CPython. isinstance() es preferible a type() para chequeos de tipo porque respeta la herencia.',
        tags: ['Tipos', 'Fundamentos'],
      },
      {
        title: 'Comprehensions y generators',
        description:
          'List comprehensions [x*2 for x in range(10) if x % 2 == 0] son más idiomáticas y generalmente más rápidas que loops equivalentes. Generator expressions (x*2 for x in range(10)) son lazy: evalúan elemento por elemento sin cargar todo en memoria. yield convierte una función en un generator. Úsalos para grandes datasets o pipelines de transformación.',
        tags: ['Idioms', 'Performance'],
      },
      {
        title: 'Decorators',
        description:
          'Un decorator es una función que recibe una función y retorna otra. @wraps(func) preserva el __name__ y __doc__ de la función original. Casos de uso: caching (@functools.lru_cache), timing, retries, autenticación, logging. Los decorators de clase (@classmethod, @staticmethod, @property) modifican el comportamiento de métodos. Puedes encadenar múltiples decorators (se aplican de abajo a arriba).',
        tags: ['Metaprogramming', 'Patrones'],
      },
      {
        title: 'Context Managers',
        description:
          'Los context managers (with statement) garantizan limpieza de recursos aunque ocurra una excepción. Implementación: __enter__ / __exit__, o usando @contextlib.contextmanager con yield. Esenciales para: archivos (with open()), conexiones de DB, locks, transacciones. contextlib.suppress() ignora excepciones específicas. contextlib.ExitStack maneja múltiples context managers dinámicamente.',
        tags: ['Resources', 'Seguridad'],
      },
      {
        title: 'async/await y asyncio',
        description:
          'asyncio permite concurrencia cooperativa en un solo thread. async def define una coroutine; await suspende la ejecución hasta que el awaitable completa. asyncio.gather() ejecuta múltiples coroutines en paralelo. asyncio.TaskGroup (Python 3.11+) es la forma moderna con mejor manejo de errores. Ideal para I/O concurrente (HTTP, DB); no ayuda con CPU-bound work (usa multiprocessing para eso).',
        tags: ['Async', 'Concurrencia'],
      },
      {
        title: 'Type Hints y mypy',
        description:
          'Python soporta type hints desde 3.5 (PEP 484). No son verificados en runtime por defecto, pero mypy/pyright los validan estáticamente. Tipos útiles: Optional[T] (= T | None), Union[A, B], List[T], Dict[K, V], Callable[[Args], Return], TypeVar para genéricos. Python 3.10+ permite X | Y en lugar de Union[X, Y]. Protocols definen duck typing estructural.',
        tags: ['TypeScript', 'Type Safety'],
      },
      {
        title: 'Manejo de excepciones',
        description:
          'try/except/else/finally: else se ejecuta si no hubo excepción; finally siempre. Captura excepciones específicas (except ValueError) antes que genéricas (except Exception). Nunca uses except: sin tipo (captura SystemExit y KeyboardInterrupt). raise from original_exception preserva el traceback original. Las excepciones custom extienden Exception con mensajes descriptivos.',
        tags: ['Error Handling', 'Fundamentos'],
      },
      {
        title: 'Dataclasses y Pydantic',
        description:
          '@dataclass genera __init__, __repr__, __eq__ automáticamente. frozen=True lo hace inmutable. Pydantic (v2) añade validación de tipos en runtime: ideal para configuración, APIs y parsing de datos externos. BaseModel de Pydantic valida al instanciar, convierte tipos, y genera JSON schemas. FastAPI usa Pydantic para validación de request/response automática.',
        tags: ['Data Modeling', 'Validación'],
      },
    ],
    useCases: [
      {
        title: 'Data Science y Machine Learning',
        description:
          'El ecosistema de Python (NumPy, Pandas, Scikit-learn, PyTorch, TensorFlow) lo hace el lenguaje dominante en ML/DS. Jupyter Notebooks permiten exploración iterativa. La sintaxis limpia facilita expresar algoritmos matemáticos. La mayoría de papers de investigación en AI publican código en Python.',
      },
      {
        title: 'APIs y backends con FastAPI/Django',
        description:
          'FastAPI es el framework moderno para APIs REST: async nativo, validación con Pydantic, documentación OpenAPI automática, performance comparable a Node.js. Django es el framework batteries-included para aplicaciones web completas con ORM, admin, auth y migrations incluidos.',
      },
      {
        title: 'Scripting y automatización',
        description:
          'Python es el lenguaje de facto para scripts de automatización: procesamiento de archivos, web scraping (BeautifulSoup, Playwright), administración de sistemas, pipelines de CI/CD, y herramientas DevOps. pathlib, subprocess, y os son módulos clave.',
      },
      {
        title: 'CLI tools y utilities',
        description:
          'Click y Typer (basado en type hints) simplifican la creación de CLIs robustas con subcomandos, opciones y validación. Rich añade tablas, progress bars y colores a la terminal. Herramientas como black, ruff, y pipenv están construidas en Python.',
      },
    ],
    interviewQA: [
      {
        question: '¿Cuál es la diferencia entre una lista y una tupla en Python?',
        answer:
          'Las listas son mutables (pueden cambiar después de crearse) y las tuplas son inmutables. Las tuplas son más rápidas para iteración y pueden usarse como keys de diccionario. Semánticamente: usa listas para colecciones homogéneas que pueden crecer/cambiar; usa tuplas para grupos de valores heterogéneos con significado fijo (ej: coordenadas, registros de DB).',
        difficulty: 'easy',
      },
      {
        question: '¿Qué es el GIL y cómo afecta la concurrencia en Python?',
        answer:
          'El GIL (Global Interpreter Lock) en CPython permite que solo un thread ejecute bytecode Python a la vez. Esto significa que el threading de Python no logra paralelismo real para tareas CPU-bound. Soluciones: multiprocessing (procesos separados, cada uno con su GIL), ProcessPoolExecutor, o usar Numba/C extensions que liberan el GIL. Para I/O-bound, threading y asyncio funcionan bien porque el GIL se libera durante operaciones I/O.',
        difficulty: 'hard',
      },
      {
        question: '¿Qué son los *args y **kwargs?',
        answer:
          '*args captura argumentos posicionales adicionales como una tupla. **kwargs captura argumentos de keyword adicionales como un diccionario. Se usan para funciones que aceptan número variable de argumentos. También se usan para unpacking: func(*lista) expande la lista como args; func(**dict) expande el dict como kwargs. El orden en la firma es: params normales, *args, keyword-only params, **kwargs.',
        difficulty: 'easy',
      },
      {
        question: '¿Cuál es la diferencia entre shallow copy y deep copy?',
        answer:
          'Shallow copy (copy.copy() o list[:]) crea un nuevo objeto pero los elementos internos siguen siendo referencias al mismo objeto. Deep copy (copy.deepcopy()) crea copias recursivas de todos los objetos anidados. Si tienes una lista de listas y haces shallow copy, modificar un sublist afecta ambas copias. Para objetos simples (listas de números), shallow copy es suficiente.',
        difficulty: 'medium',
      },
      {
        question: '¿Cómo funciona la gestión de memoria en Python?',
        answer:
          'Python usa reference counting: cada objeto tiene un contador de referencias; cuando llega a 0, el objeto se libera. Para romper ciclos de referencias, tiene un garbage collector cíclico. El memory allocator de CPython tiene un pool de objetos pequeños para reutilización. id() retorna la dirección de memoria del objeto. Los objetos inmutables pueden ser internados (strings cortos, integers pequeños comparten la misma dirección).',
        difficulty: 'hard',
      },
      {
        question: '¿Qué es un generator y cuándo lo usarías?',
        answer:
          'Un generator es una función que usa yield para retornar valores uno a uno, manteniendo su estado entre llamadas. Son lazy: no calculan el siguiente valor hasta que se pide. Úsalos cuando: el dataset es grande (no cabe en memoria), solo necesitas iterar una vez, o procesas un stream de datos. range() es un ejemplo built-in. Ventaja: O(1) memoria vs O(n) de una lista.',
        difficulty: 'medium',
      },
    ],
    codeExamples: [
      {
        title: 'Decorator con argumentos y wraps',
        description: 'Decorator configurable para retry automático con backoff exponencial.',
        language: 'python',
        code: `import time
import functools
from typing import Type, Tuple

def retry(
    max_attempts: int = 3,
    delay: float = 1.0,
    exceptions: Tuple[Type[Exception], ...] = (Exception,),
):
    """Retry decorator con backoff exponencial."""
    def decorator(func):
        @functools.wraps(func)  # Preserva __name__, __doc__, etc.
        def wrapper(*args, **kwargs):
            last_exception = None
            for attempt in range(max_attempts):
                try:
                    return func(*args, **kwargs)
                except exceptions as e:
                    last_exception = e
                    if attempt < max_attempts - 1:
                        wait = delay * (2 ** attempt)
                        print(f"Attempt {attempt + 1} failed: {e}. Retrying in {wait}s...")
                        time.sleep(wait)
            raise last_exception
        return wrapper
    return decorator

@retry(max_attempts=3, delay=0.5, exceptions=(ConnectionError, TimeoutError))
def fetch_data(url: str) -> dict:
    # Simula una llamada HTTP que puede fallar
    import requests
    response = requests.get(url, timeout=5)
    response.raise_for_status()
    return response.json()`,
      },
      {
        title: 'Dataclass + Pydantic para validación',
        description: 'Modelado de datos con Pydantic v2 para validación en runtime.',
        language: 'python',
        code: `from pydantic import BaseModel, EmailStr, field_validator, model_validator
from typing import Optional
from datetime import datetime

class CreateUserRequest(BaseModel):
    name: str
    email: EmailStr
    password: str
    age: Optional[int] = None

    @field_validator('name')
    @classmethod
    def name_must_not_be_empty(cls, v: str) -> str:
        v = v.strip()
        if len(v) < 2:
            raise ValueError('Name must be at least 2 characters')
        return v.title()  # Capitaliza

    @field_validator('password')
    @classmethod
    def password_strength(cls, v: str) -> str:
        if len(v) < 8:
            raise ValueError('Password must be at least 8 characters')
        if not any(c.isdigit() for c in v):
            raise ValueError('Password must contain at least one digit')
        return v

    @field_validator('age')
    @classmethod
    def age_range(cls, v: Optional[int]) -> Optional[int]:
        if v is not None and not (0 < v < 150):
            raise ValueError('Age must be between 1 and 149')
        return v

# Uso
try:
    user = CreateUserRequest(name="  john  ", email="john@example.com", password="secret123", age=25)
    print(user)  # name='John' email='john@example.com' ...
except ValueError as e:
    print(e)`,
      },
    ],
    quiz: [
      {
        question: '¿Cuál es el output de: x = [1, 2, 3]; y = x; y.append(4); print(x)?',
        options: ['[1, 2, 3]', '[1, 2, 3, 4]', 'Error', '[4, 1, 2, 3]'],
        correctIndex: 1,
        explanation:
          'y = x no crea una copia: tanto x como y apuntan al mismo objeto lista en memoria. Al hacer y.append(4), modificas el objeto compartido, por lo que x también refleja el cambio. Para una copia independiente usa y = x.copy() o y = x[:].',
      },
      {
        question: '¿Qué retorna type(None) en Python?',
        options: ['None', 'NoneType', 'null', 'object'],
        correctIndex: 1,
        explanation:
          'None es el único objeto de tipo NoneType. type(None) retorna <class NoneType>. Para verificar si algo es None, usa "is None" (identidad) en lugar de "== None" (igualdad), ya que algunos objetos pueden sobreescribir __eq__.',
      },
      {
        question: '¿Cuál es la diferencia entre "is" y "==" en Python?',
        options: [
          'Son idénticos en comportamiento',
          '"==" compara identidad de objeto; "is" compara valor',
          '"is" compara identidad de objeto (misma dirección de memoria); "==" compara valor',
          '"is" solo funciona con None',
        ],
        correctIndex: 2,
        explanation:
          '"is" verifica que dos variables apuntan al mismo objeto en memoria (misma identidad). "==" verifica que los valores son iguales (puede ser sobreescrito con __eq__). Usa "is" solo para comparar con None, True, y False. Para todo lo demás, usa "==".',
      },
      {
        question: '¿Qué hace @staticmethod en Python?',
        options: [
          'Hace el método solo accesible desde la clase, no desde instancias',
          'Define un método que no recibe ni self ni cls como primer argumento',
          'Hace el método inmutable',
          'Es equivalente a @classmethod',
        ],
        correctIndex: 1,
        explanation:
          '@staticmethod define un método que no recibe ni la instancia (self) ni la clase (cls). Es básicamente una función regular dentro del namespace de la clase. Úsalo para funciones utilitarias relacionadas semánticamente con la clase pero que no necesitan acceso a su estado.',
      },
      {
        question: '¿Cuándo es más apropiado usar asyncio que threading en Python?',
        options: [
          'Siempre, asyncio es superior',
          'Para tareas CPU-bound que necesitan paralelismo real',
          'Para I/O-bound con muchas operaciones concurrentes (HTTP, DB, archivos)',
          'Solo cuando se usa FastAPI',
        ],
        correctIndex: 2,
        explanation:
          'asyncio brilla en I/O-bound work: cuando tienes muchas operaciones que esperan (HTTP requests, queries de DB, lectura de archivos), un solo thread puede manejarlas concurrentemente con async/await. Para CPU-bound (cálculos, procesamiento de imágenes), necesitas multiprocessing para evadir el GIL.',
      },
    ],
    exercises: [
      {
        title: 'Pipeline de datos lazy con generators',
        difficulty: 'medium',
        description:
          'Implementa un pipeline de procesamiento de logs que lea un archivo de varios GB línea a línea (lazy), filtre líneas que contengan errores, extraiga timestamp y mensaje con regex, agrupe por hora, y retorne las 10 horas con más errores. El uso de memoria debe ser O(1) respecto al tamaño del archivo.',
        hints: [
          'Cada paso del pipeline es un generator function que yield resultados',
          'Usa re.compile() fuera del generator para compilar el regex una sola vez',
          'collections.Counter para contar errores por hora',
          'Encadena: read_lines | filter_errors | parse_log | count_by_hour',
        ],
        tags: ['Generators', 'I/O', 'Performance'],
      },
      {
        title: 'API REST con FastAPI y autenticación',
        difficulty: 'hard',
        description:
          'Crea una API con FastAPI para gestión de tareas (CRUD). Incluye autenticación JWT con registro/login, endpoints protegidos con Depends(), paginación en listados, y validación con Pydantic. Usa SQLAlchemy async con SQLite. Los usuarios solo deben ver sus propias tareas.',
        hints: [
          'Crea una dependency get_current_user que decodifica el JWT del header Authorization',
          'Usa async with AsyncSession como dependency via Depends(get_db)',
          'El modelo Task tiene un FK a User; filtra siempre por current_user.id',
          'HTTPException con 401 para token inválido, 403 para recurso de otro usuario',
        ],
        tags: ['FastAPI', 'JWT', 'SQLAlchemy'],
      },
    ],
  },
  {
    slug: 'typescript',
    title: 'TypeScript',
    shortDescription: 'Superset tipado de JavaScript que detecta errores en tiempo de compilación y mejora la productividad.',
    icon: 'data_object',
    color: 'text-blue-500',
    category: 'languages',
    concepts: [
      {
        title: 'Sistema de tipos estructural',
        description:
          'TypeScript usa tipado estructural (duck typing): dos tipos son compatibles si tienen la misma estructura, independientemente de su nombre. Diferente al tipado nominal (Java, C#). Esto significa que un objeto con {name: string, age: number} es asignable a una interfaz Person aunque no declare explícitamente que la implementa. Importante para entender por qué algunos assignments que parecen incorrectos son válidos.',
        tags: ['Type System', 'Fundamentos'],
      },
      {
        title: 'Union e Intersection types',
        description:
          'Union (|): el valor puede ser uno de varios tipos. string | number. Usa type guards (typeof, instanceof, discriminated unions) para narrowing. Intersection (&): combina múltiples tipos en uno. Admin & User tiene todas las propiedades de ambos. Los discriminated unions (con una propiedad "kind" o "type" literal) son el patrón más poderoso para modelar estados mutuamente excluyentes.',
        tags: ['Types', 'Patrones'],
      },
      {
        title: 'Generics',
        description:
          'Los generics permiten escribir código reutilizable que funciona con múltiples tipos. function identity<T>(arg: T): T. Las constraints (T extends SomeType) limitan qué tipos acepta el genérico. Los generics en interfaces y clases crean estructuras de datos tipo-seguras (Stack<T>, Repository<T>). Los conditional types (T extends U ? X : Y) permiten lógica compleja en el type system.',
        tags: ['Generics', 'Reutilización'],
      },
      {
        title: 'Utility Types',
        description:
          'TypeScript incluye utility types built-in: Partial<T> hace todos los campos opcionales, Required<T> los hace obligatorios, Readonly<T> los hace inmutables, Pick<T, K> selecciona un subset de campos, Omit<T, K> excluye campos, Record<K, V> crea un mapa tipado, Exclude<T, U> excluye tipos de una union, Extract<T, U> extrae tipos de una union, ReturnType<F> obtiene el tipo de retorno de una función.',
        tags: ['Utility Types', 'DRY'],
      },
      {
        title: 'Type Guards y Narrowing',
        description:
          'El narrowing reduce el tipo en un bloque de código basándose en checks. typeof, instanceof, in operator, truthy checks. Los type guards personalizados son funciones que retornan "arg is SomeType": function isString(x: unknown): x is string { return typeof x === "string"; }. Los assertion functions (assert condition) también narrowean el tipo. El patrón exhaustive check con never detecta casos no manejados.',
        tags: ['Type Safety', 'Narrowing'],
      },
      {
        title: 'Mapped Types y Template Literal Types',
        description:
          'Mapped types transforman las propiedades de un tipo: { [K in keyof T]: SomeTransform<T[K]> }. Template literal types (TS 4.1+) permiten manipular strings en el type system: type EventName = `on${Capitalize<string>}`. Combinados, permiten generar tipos complejos automáticamente, como un tipo que transforma todas las propiedades en setters o crea tipos de eventos a partir de nombres de métodos.',
        tags: ['Advanced Types', 'Metaprogramming'],
      },
      {
        title: 'Decorators y Metadata',
        description:
          'Los decorators (experimental en TS, estabilizados en TS 5 con la propuesta de Stage 3) permiten metaprogramación: @Injectable(), @Component(), @Column(). reflect-metadata permite guardar y leer metadata en tiempo de runtime. Son la base de frameworks como NestJS y Angular. Tipos: class decorators, method decorators, property decorators, parameter decorators.',
        tags: ['Decorators', 'Metaprogramming'],
      },
      {
        title: 'tsconfig y opciones clave',
        description:
          'strict: true habilita un conjunto de checks: strictNullChecks, noImplicitAny, strictFunctionTypes. target define a qué versión de JS se compila. module define el sistema de módulos (ESNext, CommonJS). moduleResolution controla cómo se resuelven los imports. paths permite path aliases (@/utils). outDir define el directorio de salida. include/exclude controlan qué archivos se compilan.',
        tags: ['Tooling', 'Configuración'],
      },
    ],
    useCases: [
      {
        title: 'Aplicaciones frontend a gran escala',
        description:
          'TypeScript es el estándar en proyectos React, Angular y Vue a gran escala. El sistema de tipos previene bugs de regresión, mejora el autocompletado del IDE, y hace el refactoring seguro. Angular lo requiere; React y Vue lo recomiendan fuertemente.',
      },
      {
        title: 'APIs y backends con Node.js',
        description:
          'NestJS está escrito en TypeScript y lo requiere. Express con tipos via @types/express es el punto de partida. TypeScript en backend permite compartir tipos entre cliente y servidor (monorepos), garantizando que los contratos de API sean consistentes.',
      },
      {
        title: 'Librerías y packages npm',
        description:
          'Publicar una librería en TypeScript genera automáticamente los .d.ts (declaration files) que dan autocompletado y type safety a los consumidores. Es el estándar para librerías modernas.',
      },
    ],
    interviewQA: [
      {
        question: '¿Cuál es la diferencia entre interface y type en TypeScript?',
        answer:
          'Las interfaces son extensibles (declaration merging: puedes declarar la misma interfaz dos veces y se fusionan) y solo describen la forma de objetos/clases. Los type aliases pueden ser cualquier tipo: primitivos, uniones, intersecciones, tuplas. En la práctica, para objetos usa interface (más legible en errores, soporta extends). Para uniones, tuplas o tipos complejos usa type. Con strict mode, la diferencia es mínima.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es "unknown" y por qué es mejor que "any"?',
        answer:
          'unknown es el tipo type-safe de any. A una variable unknown puedes asignar cualquier valor (como any), pero no puedes hacer operaciones en ella sin primero narrowear el tipo con un type guard. Con any, puedes hacer lo que quieras sin verificación. unknown te fuerza a validar el tipo antes de usarlo, haciendo el código más seguro. Úsalo para valores de origen externo (API responses, JSON.parse, catch clauses).',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es el "never" type y cuándo aparece?',
        answer:
          'never representa valores que nunca pueden existir. Aparece en: 1) El tipo de retorno de funciones que siempre lanzan o tienen loop infinito. 2) El resultado de narrowing que es imposible: if (typeof x === "string" && typeof x === "number") { x } — x es never. Se usa en exhaustive checks: si un switch no cubre todos los casos de una union, el default puede asignar a never y TypeScript reporta error.',
        difficulty: 'hard',
      },
      {
        question: '¿Cómo funciona la inferencia de tipos en TypeScript?',
        answer:
          'TypeScript infiere tipos automáticamente: const x = 5 infiere number; las funciones infieren el tipo de retorno desde el return statement; los generics se infieren de los argumentos. La inferencia contextual funciona al revés: el tipo esperado en cierto contexto informa el tipo del valor (ej: pasar un array literal donde se espera string[] infiere string[]). No necesitas anotar tipos cuando TypeScript puede inferirlos correctamente.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué hace el operador "satisfies" introducido en TS 4.9?',
        answer:
          'satisfies valida que un valor satisface un tipo sin cambiar el tipo inferido del valor. Ejemplo: const palette = { red: [255, 0, 0], green: "#00ff00" } satisfies Record<string, string | number[]>. TypeScript verifica que palette cumple el tipo, pero mantiene el tipo más específico de palette (sabiendo que red es un array y green es string). Con una anotación de tipo normal perderías esa especificidad.',
        difficulty: 'hard',
      },
      {
        question: '¿Qué son las conditional types y para qué se usan?',
        answer:
          'T extends U ? X : Y — si T es asignable a U, el tipo es X, si no, es Y. Se distribuyen sobre uniones: si T = A | B, el resultado es (A extends U ? X : Y) | (B extends U ? X : Y). Úsalos para: extraer tipos de estructuras (infer keyword), crear tipos que varían según el tipo de entrada (ReturnType<F> es T extends (...args: any) => infer R ? R : never), o filtrar tipos de una union.',
        difficulty: 'hard',
      },
    ],
    codeExamples: [
      {
        title: 'Discriminated Union y exhaustive check',
        description: 'Patrón para modelar estados con type safety completa.',
        language: 'typescript',
        code: `// Estado de una operación asíncrona
type AsyncState<T, E = Error> =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: T }
  | { status: 'error'; error: E };

// Exhaustive check: TypeScript falla si no se cubren todos los casos
function assertNever(x: never): never {
  throw new Error('Unexpected value: ' + x);
}

function renderState<T>(state: AsyncState<T>, render: (data: T) => string): string {
  switch (state.status) {
    case 'idle':
      return 'Waiting...';
    case 'loading':
      return 'Loading...';
    case 'success':
      return render(state.data); // TypeScript sabe que state.data: T aquí
    case 'error':
      return \`Error: \${state.error.message}\`;
    default:
      return assertNever(state); // Error de compilación si falta algún case
  }
}

// Uso en React
function UserProfile({ state }: { state: AsyncState<User> }) {
  return <div>{renderState(state, user => user.name)}</div>;
}`,
      },
      {
        title: 'Generic Repository con Prisma',
        description: 'Abstracción type-safe de acceso a datos con generics.',
        language: 'typescript',
        code: `import { PrismaClient } from '@prisma/client';

type PrismaModelName = keyof Omit<PrismaClient, '$connect' | '$disconnect' | '$on' | '$transaction' | '$use' | '$extends'>;

interface PaginationOptions {
  page: number;
  limit: number;
}

interface PaginatedResult<T> {
  data: T[];
  total: number;
  page: number;
  totalPages: number;
}

// Inferir el tipo de retorno de findMany para un modelo dado
type FindManyResult<T extends PrismaModelName> = Awaited<
  ReturnType<PrismaClient[T]['findMany']>
>[number];

class BaseRepository<T extends PrismaModelName> {
  constructor(
    protected prisma: PrismaClient,
    protected model: T,
  ) {}

  async paginate(
    options: PaginationOptions,
    where?: Record<string, unknown>,
  ): Promise<PaginatedResult<FindManyResult<T>>> {
    const { page, limit } = options;
    const skip = (page - 1) * limit;

    const [data, total] = await Promise.all([
      (this.prisma[this.model] as any).findMany({ where, skip, take: limit }),
      (this.prisma[this.model] as any).count({ where }),
    ]);

    return { data, total, page, totalPages: Math.ceil(total / limit) };
  }
}

class UserRepository extends BaseRepository<'user'> {
  constructor(prisma: PrismaClient) {
    super(prisma, 'user');
  }

  async findByEmail(email: string) {
    return this.prisma.user.findUnique({ where: { email } });
  }
}`,
      },
    ],
    quiz: [
      {
        question: '¿Cuál es el resultado de: type T = string extends unknown ? "yes" : "no"?',
        options: ['"no"', '"yes"', 'never', 'unknown'],
        correctIndex: 1,
        explanation:
          'string extends unknown es true porque unknown es el supertipo de todos los tipos en TypeScript (todo es asignable a unknown). Por lo tanto el conditional type resuelve a "yes".',
      },
      {
        question: '¿Qué hace Readonly<T> en TypeScript?',
        options: [
          'Hace que el objeto no pueda ser asignado a una variable',
          'Hace todas las propiedades de T de solo lectura (no pueden ser reasignadas)',
          'Convierte el objeto en inmutable en runtime',
          'Solo aplica al nivel superior, no a objetos anidados',
        ],
        correctIndex: 1,
        explanation:
          'Readonly<T> es un mapped type que añade readonly a cada propiedad de T. Esto previene reasignación en compile time: obj.prop = "new value" da error. Importante: es shallow — los objetos anidados siguen siendo mutables. Para deep readonly necesitas un tipo recursivo custom.',
      },
      {
        question: '¿Cuándo usarías "as const" en TypeScript?',
        options: [
          'Para convertir un tipo mutable en inmutable en runtime',
          'Para que TypeScript infiera tipos literales estrechos en lugar de tipos amplios',
          'Para marcar una variable como constante en JavaScript',
          'Solo funciona con arrays',
        ],
        correctIndex: 1,
        explanation:
          'as const hace que TypeScript infiera tipos literales: const obj = { status: "active" } infiere { status: string }, pero const obj = { status: "active" } as const infiere { readonly status: "active" }. Útil para objetos de configuración, arrays de opciones fijas, y cualquier dato que no debe cambiar y donde quieres el tipo más estricto posible.',
      },
      {
        question: '¿Qué es el "infer" keyword en TypeScript?',
        options: [
          'Fuerza a TypeScript a inferir el tipo de una variable',
          'Extrae un tipo desde dentro de un conditional type para usarlo en la rama true',
          'Es un alias de typeof',
          'Desactiva la inferencia de tipos',
        ],
        correctIndex: 1,
        explanation:
          'infer se usa dentro de conditional types para capturar un tipo: type ReturnType<T> = T extends (...args: any[]) => infer R ? R : never. Aquí infer R "captura" el tipo de retorno de la función T y lo hace disponible en la rama true. Permite descomponer tipos complejos.',
      },
      {
        question: '¿Qué hace el operador "keyof" en TypeScript?',
        options: [
          'Retorna los valores de un objeto en runtime',
          'Crea un union type con todos los nombres de propiedades de un tipo',
          'Verifica si un objeto tiene una propiedad específica',
          'Convierte un objeto en un array de sus keys',
        ],
        correctIndex: 1,
        explanation:
          'keyof T produce un union type de todos los nombres de propiedades de T. keyof { name: string; age: number } = "name" | "age". Se usa frecuentemente con generics: function get<T, K extends keyof T>(obj: T, key: K): T[K] — tipo de retorno es el tipo de la propiedad accedida, no any.',
      },
    ],
    exercises: [
      {
        title: 'Type-safe Event Emitter',
        difficulty: 'hard',
        description:
          'Implementa un EventEmitter genérico completamente tipado. Define un EventMap como Record<string, unknown>. Los métodos on<K>(event: K, listener) deben inferir el tipo del argumento del listener desde el EventMap. emit<K>(event: K, data) debe verificar que data es del tipo correcto. El método off debe también estar tipado.',
        hints: [
          'type EventEmitter<T extends Record<string, unknown>>',
          'on<K extends keyof T>(event: K, listener: (data: T[K]) => void): this',
          'Internamente usa Map<keyof T, Set<Function>>',
          'Usa TypeScript playground para iterar rápido',
        ],
        tags: ['Generics', 'Advanced Types', 'Patrones'],
      },
      {
        title: 'Utility types personalizados',
        difficulty: 'medium',
        description:
          'Implementa desde cero los siguientes utility types sin usar los built-in: MyPartial<T>, MyRequired<T>, MyReadonly<T>, MyPick<T, K>, MyOmit<T, K>, MyReturnType<F>, MyParameters<F>. Para cada uno, escribe tests de tipos usando type assertions.',
        hints: [
          'MyPartial<T> = { [K in keyof T]?: T[K] }',
          'MyRequired<T> = { [K in keyof T]-?: T[K] } (el - remueve el modificador optional)',
          'MyOmit<T, K> = MyPick<T, Exclude<keyof T, K>>',
          'Para functions: T extends (...args: infer P) => infer R',
        ],
        tags: ['Utility Types', 'Mapped Types', 'Advanced'],
      },
    ],
  },
  {
    slug: 'nextjs',
    title: 'Next.js',
    shortDescription: 'Framework React fullstack con SSR, SSG, App Router y Server Components para apps de producción.',
    icon: 'web',
    color: 'text-slate-300',
    category: 'frontend',
    concepts: [
      {
        title: 'App Router y Server Components',
        description:
          'El App Router (Next.js 13+) usa React Server Components por defecto: los componentes se renderizan en el servidor sin enviar JavaScript al cliente. Esto reduce el bundle size y permite fetch de datos directamente en el componente. Para interactividad (hooks, eventos), agrega "use client" al top del archivo. La regla: empieza todo como Server Component y agrega "use client" solo cuando sea necesario.',
        tags: ['App Router', 'RSC'],
      },
      {
        title: 'Estrategias de rendering: SSR, SSG, ISR',
        description:
          'SSR (Server-Side Rendering): HTML generado en cada request. Datos siempre frescos pero más lento. SSG (Static Site Generation): HTML generado en build time. Máxima performance, ideal para contenido que no cambia frecuentemente. ISR (Incremental Static Regeneration): regenera páginas estáticas en background cada X segundos (revalidate). Con App Router, el caching es granular a nivel de fetch(), no de página.',
        tags: ['Rendering', 'Performance'],
      },
      {
        title: 'Data Fetching en App Router',
        description:
          'En Server Components, fetch() de Next.js es extendido con opciones de cache: fetch(url, { cache: "no-store" }) para SSR, { next: { revalidate: 60 } } para ISR. Para datos que no cambian, el default es cached. unstable_cache() cachea funciones arbitrarias (queries de Prisma). revalidatePath() y revalidateTag() para invalidar el cache manualmente en Server Actions.',
        tags: ['Data Fetching', 'Caching'],
      },
      {
        title: 'Server Actions',
        description:
          'Las Server Actions son funciones async marcadas con "use server" que se ejecutan en el servidor pero se llaman desde el cliente. Permiten mutaciones de datos sin crear un endpoint API explícito. Se integran con formularios HTML (<form action={myAction}>). Manejan automáticamente la serialización/deserialización. Pueden usarse con useFormState y useFormStatus para feedback al usuario.',
        tags: ['Server Actions', 'Mutaciones'],
      },
      {
        title: 'Layouts y estructura de archivos',
        description:
          'El App Router usa convenciones de nombres de archivo: page.tsx (ruta), layout.tsx (layout compartido que persiste entre navegaciones), loading.tsx (Suspense automático), error.tsx (Error Boundary), not-found.tsx. Los layouts anidados crean una jerarquía. Los route groups (carpetas entre paréntesis) organizan rutas sin afectar la URL. Los segmentos dinámicos usan [slug] y los catch-all [...slug].',
        tags: ['Routing', 'File System'],
      },
      {
        title: 'Middleware',
        description:
          'middleware.ts en la raíz del proyecto intercepta requests antes de que lleguen a la página. Se ejecuta en el Edge Runtime (V8 isolates, ultra-rápido). Casos de uso: autenticación/redirección, A/B testing, geolocalización, rewriting de URLs. Usa NextResponse.redirect(), rewrite() o next(). El matcher controla qué rutas activan el middleware.',
        tags: ['Middleware', 'Edge'],
      },
      {
        title: 'Optimizaciones built-in',
        description:
          'next/image: optimización automática de imágenes (WebP, lazy loading, CLS prevention con width/height). next/font: fuentes zero-layout-shift, auto-hosted, sin requests a Google Fonts. next/link: prefetching automático de rutas. Script: control de carga de scripts externos (beforeInteractive, afterInteractive, lazyOnload). Estas optimizaciones mejoran Core Web Vitals sin configuración adicional.',
        tags: ['Performance', 'Core Web Vitals'],
      },
      {
        title: 'API Routes y Route Handlers',
        description:
          'Los Route Handlers (app/api/route.ts) reemplazan a las API Routes del Pages Router. Exporta funciones GET, POST, PUT, DELETE, etc. Reciben un Request y retornan un Response. Pueden ser Edge o Node.js runtime. Úsalos cuando necesitas un endpoint HTTP real (webhooks, OAuth callbacks). Para la mayoría de mutaciones de datos, Server Actions son más simples.',
        tags: ['API', 'Backend'],
      },
    ],
    useCases: [
      {
        title: 'E-commerce y sitios con SEO crítico',
        description:
          'Next.js es la elección default para e-commerce donde SEO y performance son críticos. SSG para páginas de producto (ISR para actualizar precios/stock), SSR para búsqueda con filtros dinámicos. next/image optimiza las imágenes de producto. El App Router con Server Components reduce el JavaScript enviado al cliente.',
      },
      {
        title: 'Dashboards y aplicaciones SaaS',
        description:
          'La combinación de Server Components (para fetching eficiente) y Client Components (para interactividad) es ideal para dashboards complejos. Los layouts anidados permiten sidebars y navegación persistente. Las Server Actions simplifican formularios de configuración sin necesitar una API separada.',
      },
      {
        title: 'Blogs y sitios de contenido',
        description:
          'SSG con MDX o un CMS headless (Contentful, Sanity) genera sitios estáticos ultra-rápidos. ISR permite publicar nuevo contenido sin rebuild completo. next/font y la optimización de imágenes mejoran los Core Web Vitals automáticamente.',
      },
      {
        title: 'Aplicaciones fullstack con un solo repo',
        description:
          'Next.js permite construir el frontend y el backend en el mismo proyecto. Server Actions para mutaciones, Route Handlers para webhooks y APIs externas, Server Components para fetching. Ideal para startups que quieren moverse rápido con un stack unificado.',
      },
    ],
    interviewQA: [
      {
        question: '¿Cuándo usarías un Server Component vs un Client Component?',
        answer:
          'Server Component (default): fetching de datos, acceso a recursos del servidor (DB, file system), lógica que no requiere interactividad, componentes sin estado. Client Component ("use client"): cuando necesitas useState, useEffect, event handlers (onClick, onChange), acceso a APIs del browser (localStorage, window), o librerías que usan hooks. La regla práctica: empieza con Server Component, agrega "use client" solo cuando el compilador o la lógica lo requieran.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es ISR y cuándo lo usarías?',
        answer:
          'ISR (Incremental Static Regeneration) genera páginas estáticas en build time y las regenera en background cada N segundos (revalidate). El primer usuario después de expirar el TTL recibe la página stale mientras Next.js regenera en background; el siguiente usuario ya recibe la nueva versión. Ideal para: páginas de producto con precios que cambian poco frecuentemente, artículos de blog, páginas de categorías. No usar para datos en tiempo real o personalizados por usuario.',
        difficulty: 'medium',
      },
      {
        question: '¿Cuál es la diferencia entre revalidatePath y revalidateTag?',
        answer:
          'revalidatePath("/products") invalida el cache de una ruta específica. revalidateTag("products") invalida todos los fetch() que tienen ese tag: fetch(url, { next: { tags: ["products"] } }). revalidateTag es más granular y eficiente: puedes invalidar un conjunto de datos relacionados desde múltiples rutas sin revalidar rutas completas. Ambos se usan en Server Actions o Route Handlers para invalidación manual del cache.',
        difficulty: 'hard',
      },
      {
        question: '¿Qué problema resuelve el layout.tsx y cómo funciona?',
        answer:
          'Los layouts son componentes que envuelven páginas y persisten entre navegaciones (no se re-renderizan al navegar entre rutas que comparten el layout). Resuelven el problema de mantener estado en sidebars, headers o tabs de navegación al cambiar de ruta. El RootLayout (obligatorio en app/) envuelve toda la app. Los layouts pueden ser anidados y reciben children como prop.',
        difficulty: 'easy',
      },
      {
        question: '¿Qué son los Server Actions y qué ventajas tienen sobre los API routes?',
        answer:
          'Las Server Actions son funciones async marcadas con "use server" que se ejecutan en el servidor y se invocan desde el cliente. Ventajas sobre API routes: menos boilerplate (no necesitas fetch + endpoint), integración nativa con formularios HTML, type safety end-to-end (TypeScript sabe los tipos de los argumentos y el retorno), y acceso directo a la base de datos. Desventaja: no son un endpoint HTTP estándar (no se pueden llamar desde servicios externos).',
        difficulty: 'medium',
      },
      {
        question: '¿Cómo funciona el caching en Next.js App Router?',
        answer:
          'Next.js tiene 4 capas de cache: 1) Request Memoization: deduplicación de fetch() con la misma URL dentro del mismo árbol de render. 2) Data Cache: persiste resultados de fetch() entre requests y deployments (invalidar con revalidate o revalidateTag). 3) Full Route Cache: caché del HTML/RSC payload de rutas estáticas en el servidor. 4) Router Cache: caché en el cliente de los segmentos visitados para navegación instantánea.',
        difficulty: 'hard',
      },
    ],
    codeExamples: [
      {
        title: 'Server Component con fetch y Suspense',
        description: 'Fetching de datos en Server Component con loading state usando Suspense.',
        language: 'typescript',
        code: `// app/products/page.tsx — Server Component (default)
import { Suspense } from 'react';
import { ProductList } from './product-list';
import { ProductListSkeleton } from './product-list-skeleton';

export default function ProductsPage() {
  return (
    <main>
      <h1>Products</h1>
      {/* Suspense muestra el skeleton mientras ProductList fetcha datos */}
      <Suspense fallback={<ProductListSkeleton />}>
        <ProductList />
      </Suspense>
    </main>
  );
}

// app/products/product-list.tsx — Server Component con fetch
async function getProducts() {
  const res = await fetch('https://api.example.com/products', {
    next: { revalidate: 60, tags: ['products'] }, // ISR: revalida cada 60s
  });
  if (!res.ok) throw new Error('Failed to fetch products');
  return res.json() as Promise<Product[]>;
}

export async function ProductList() {
  const products = await getProducts(); // fetch directo en el componente

  return (
    <ul>
      {products.map(product => (
        <li key={product.id}>
          <h2>{product.name}</h2>
          <p>\${product.price}</p>
        </li>
      ))}
    </ul>
  );
}`,
      },
      {
        title: 'Server Action para formulario',
        description: 'Mutación de datos con Server Action integrada en un formulario HTML.',
        language: 'typescript',
        code: `// app/products/actions.ts
'use server';

import { revalidateTag } from 'next/cache';
import { redirect } from 'next/navigation';
import { z } from 'zod';
import { prisma } from '@/lib/prisma';

const CreateProductSchema = z.object({
  name: z.string().min(2),
  price: z.coerce.number().positive(),
  description: z.string().optional(),
});

export async function createProduct(formData: FormData) {
  const raw = {
    name: formData.get('name'),
    price: formData.get('price'),
    description: formData.get('description'),
  };

  const parsed = CreateProductSchema.safeParse(raw);
  if (!parsed.success) {
    return { error: parsed.error.flatten().fieldErrors };
  }

  await prisma.product.create({ data: parsed.data });
  revalidateTag('products'); // Invalida el cache de productos
  redirect('/products');
}

// app/products/new/page.tsx
import { createProduct } from '../actions';

export default function NewProductPage() {
  return (
    <form action={createProduct}>
      <input name="name" placeholder="Product name" required />
      <input name="price" type="number" placeholder="Price" required />
      <textarea name="description" placeholder="Description" />
      <button type="submit">Create Product</button>
    </form>
  );
}`,
      },
    ],
    quiz: [
      {
        question: '¿Dónde se ejecuta el middleware.ts de Next.js?',
        options: [
          'En el browser del cliente',
          'En el servidor Node.js con acceso completo a Node APIs',
          'En el Edge Runtime (V8 isolate, sin acceso a Node.js APIs)',
          'En el servidor solo durante el build',
        ],
        correctIndex: 2,
        explanation:
          'El middleware de Next.js se ejecuta en el Edge Runtime: un entorno de V8 isolates ultra-rápido pero con APIs limitadas (sin acceso a Node.js, sin fs, sin crypto completo). Esto permite que se ejecute en CDN edges cerca del usuario. Si necesitas Node.js APIs en el middleware, debes usar runtime: "nodejs" explícitamente (más lento).',
      },
      {
        question: '¿Qué hace "use client" en Next.js?',
        options: [
          'Ejecuta el componente solo en el navegador, nunca en el servidor',
          'Marca el componente como Client Component: se pre-renderiza en servidor pero se hidrata en el cliente',
          'Deshabilita el SSR para ese componente',
          'Permite importar módulos de Node.js',
        ],
        correctIndex: 1,
        explanation:
          '"use client" marca una frontera: el componente y todos sus hijos se incluyen en el JavaScript bundle del cliente. Importante: los Client Components siguen siendo pre-renderizados en el servidor (SSR) para el HTML inicial; "use client" no deshabilita el SSR. Solo significa que el componente también se hidrata e interactúa en el cliente.',
      },
      {
        question: '¿Cuál es el propósito del archivo loading.tsx en App Router?',
        options: [
          'Muestra un spinner global durante la carga inicial de la app',
          'Crea automáticamente un Suspense boundary alrededor de la página, mostrando su contenido mientras la página carga',
          'Maneja errores de carga de red',
          'Solo funciona con imágenes next/image',
        ],
        correctIndex: 1,
        explanation:
          'loading.tsx crea automáticamente un Suspense boundary alrededor de page.tsx. Mientras la página (un async Server Component) está esperando datos, se muestra el contenido de loading.tsx (generalmente un skeleton). Esto habilita streaming: el layout se muestra inmediatamente y el contenido llega cuando está listo.',
      },
      {
        question: '¿Cuándo usarías generateStaticParams en Next.js?',
        options: [
          'Para definir los query params válidos de una página',
          'Para pre-generar rutas dinámicas en build time (SSG)',
          'Para validar params en el servidor',
          'Para generar metadata de la página',
        ],
        correctIndex: 1,
        explanation:
          'generateStaticParams() retorna los parámetros de las rutas dinámicas que deben pre-generarse en build time. Equivale a getStaticPaths del Pages Router. Por ejemplo, para /blog/[slug], retorna [{ slug: "post-1" }, { slug: "post-2" }]. Next.js genera el HTML de cada combinación en el build.',
      },
      {
        question: '¿Qué ventaja tiene next/font sobre importar fuentes de Google Fonts directamente?',
        options: [
          'next/font es más rápido de escribir',
          'Elimina peticiones externas a Google, evita layout shift, y las fuentes se sirven junto con los assets de la app',
          'Soporta más fuentes que Google Fonts',
          'Permite usar fuentes de variable font',
        ],
        correctIndex: 1,
        explanation:
          'next/font descarga las fuentes en build time y las sirve como assets estáticos de la app, eliminando requests externas a Google en runtime (privacidad + performance). Automáticamente aplica font-display: swap y size-adjust para evitar Cumulative Layout Shift (CLS). No hay ninguna petición a fonts.googleapis.com en producción.',
      },
    ],
    exercises: [
      {
        title: 'Autenticación con NextAuth.js y middleware',
        difficulty: 'hard',
        description:
          'Implementa autenticación completa con NextAuth.js v5 (Auth.js): providers de Google y credentials (email/password), sesión con JWT, middleware que protege todas las rutas bajo /dashboard, página de login con error handling, y callback para guardar el user en DB en el primer login. El middleware debe redirigir a /login si no hay sesión y a /dashboard si ya está autenticado y visita /login.',
        hints: [
          'auth.config.ts para la configuración base (compatible con Edge Runtime)',
          'auth.ts importa auth.config y añade el adapter de Prisma (que necesita Node.js)',
          'El middleware importa solo auth.config para mantenerse en Edge Runtime',
          'Usa el callback signIn para crear el usuario en DB si no existe',
        ],
        tags: ['Auth', 'Middleware', 'NextAuth'],
      },
      {
        title: 'Dashboard con streaming y Suspense',
        difficulty: 'medium',
        description:
          'Crea un dashboard que muestre 4 métricas (revenue, orders, customers, conversion rate) cargadas desde DB. Cada métrica debe cargarse de forma independiente con Suspense y su propio skeleton, para que la UI sea visible inmediatamente y las métricas aparezcan conforme llegan. Usa Promise.all para las que pueden cargarse en paralelo y carga secuencial para las que dependen entre sí.',
        hints: [
          'Cada MetricCard es un Server Component async que fetchea sus propios datos',
          'Envuelve cada MetricCard en <Suspense fallback={<MetricSkeleton />}>',
          'Los Suspense boundaries independientes permiten que cada card aparezca cuando está lista',
          'Para métricas con la misma fuente de datos, usa unstable_cache para no duplicar queries',
        ],
        tags: ['Suspense', 'Streaming', 'Performance'],
      },
    ],
  },
  {
    slug: 'aws',
    title: 'AWS',
    shortDescription: 'Plataforma cloud líder con más de 200 servicios para compute, storage, networking, AI y más.',
    icon: 'cloud',
    color: 'text-orange-400',
    category: 'cloud',
    concepts: [
      {
        title: 'IAM: Identidad y control de acceso',
        description:
          'IAM (Identity and Access Management) controla quién puede hacer qué en AWS. Usuarios, grupos, roles y políticas (documentos JSON con Effect/Action/Resource). El principio de mínimo privilegio: dar solo los permisos necesarios. Los roles son preferibles a los usuarios para servicios AWS (una Lambda o EC2 asumen un rol, no usan credenciales). Las políticas managed by AWS vs managed by customer. STS para credenciales temporales.',
        tags: ['Seguridad', 'Fundamentos'],
      },
      {
        title: 'EC2: Compute escalable',
        description:
          'EC2 provee VMs (instancias) con diferentes familias (compute-optimized C, memory-optimized R, general purpose M/T). Instance types definen CPU/RAM/red. AMIs son imágenes del sistema operativo. Security Groups actúan como firewall virtual. Auto Scaling Groups ajustan el número de instancias según carga. Elastic Load Balancer distribuye el tráfico. Spot instances reducen costos hasta 90% para cargas interrumpibles.',
        tags: ['Compute', 'Escalabilidad'],
      },
      {
        title: 'S3: Object Storage',
        description:
          'S3 almacena objetos (archivos) en buckets con durabilidad 99.999999999% (11 nines). Las políticas de bucket y ACLs controlan el acceso. Versionado, lifecycles para mover objetos a clases más baratas (S3 IA, Glacier). Presigned URLs para acceso temporal sin exponer credenciales. S3 puede servir sitios estáticos. Event notifications para disparar Lambdas al subir archivos. Multipart upload para archivos grandes.',
        tags: ['Storage', 'Objetos'],
      },
      {
        title: 'Lambda: Funciones serverless',
        description:
          'Lambda ejecuta funciones en respuesta a eventos sin gestionar servidores. Escala automáticamente a cero. Límites: 15min de ejecución, 10GB de memoria, 512MB-10GB de /tmp. Cold starts son el principal problema de latencia (usa Provisioned Concurrency para mitigarlo). Triggers: API Gateway, S3, DynamoDB Streams, EventBridge, SQS. El pricing es por invocación + GB-second. Layers para compartir dependencias.',
        tags: ['Serverless', 'Compute'],
      },
      {
        title: 'RDS y Aurora: Bases de datos relacionales',
        description:
          'RDS provee PostgreSQL, MySQL, MariaDB, Oracle, SQL Server gestionados: backups automáticos, patching, Multi-AZ para alta disponibilidad. Aurora es compatible con MySQL/PostgreSQL pero hasta 5x más rápido, con storage que crece automáticamente. Aurora Serverless escala compute automáticamente. RDS Proxy gestiona el connection pooling (crítico para Lambda que puede abrir miles de conexiones).',
        tags: ['Databases', 'SQL'],
      },
      {
        title: 'VPC: Networking',
        description:
          'VPC (Virtual Private Cloud) es tu red privada en AWS. Subnets públicas (con acceso a internet via Internet Gateway) y privadas (aisladas). Route Tables controlan el routing. Security Groups (stateful, nivel de instancia) vs NACLs (stateless, nivel de subnet). NAT Gateway para que instancias privadas accedan a internet sin exponerse. VPC Peering y Transit Gateway para conectar VPCs. Las Lambdas dentro de VPC pierden acceso a internet a menos que uses NAT Gateway.',
        tags: ['Networking', 'Seguridad'],
      },
      {
        title: 'CloudFormation e IaC',
        description:
          'CloudFormation define infraestructura como código en YAML/JSON. Stacks son grupos de recursos. Los cambios se hacen via changesets (previsualización antes de aplicar). AWS CDK (Cloud Development Kit) permite definir infraestructura en TypeScript/Python/Java y genera CloudFormation. Terraform es la alternativa multi-cloud más popular. IaC permite: versionar la infraestructura, reproducir entornos, y hacer code review de cambios.',
        tags: ['IaC', 'DevOps'],
      },
      {
        title: 'SQS, SNS y EventBridge',
        description:
          'SQS: cola de mensajes para desacoplamiento. Standard (at-least-once, mejor esfuerzo en orden) vs FIFO (exactly-once, orden garantizado). DLQ para mensajes fallidos. SNS: pub/sub para fan-out a múltiples subscribers (Lambda, SQS, HTTP). EventBridge: event bus serverless con reglas de routing basadas en el contenido del evento. El patrón: productores publican eventos, consumidores los procesan de forma independiente.',
        tags: ['Mensajería', 'Async'],
      },
    ],
    useCases: [
      {
        title: 'Hosting de aplicaciones web y APIs',
        description:
          'EC2 + ALB + Auto Scaling para aplicaciones tradicionales. ECS/EKS para contenedores. Lambda + API Gateway para APIs serverless. S3 + CloudFront para frontends estáticos con CDN global. Amplify para full-stack simplificado. La elección depende del control necesario vs la operativa que quieres gestionar.',
      },
      {
        title: 'Almacenamiento y procesamiento de datos',
        description:
          'S3 como data lake. Glue para ETL. Athena para queries SQL sobre S3 sin mover datos. Redshift para data warehouse. RDS/Aurora para OLTP. DynamoDB para cargas de trabajo NoSQL a gran escala con latencia predecible. Kinesis para streaming de datos en tiempo real.',
      },
      {
        title: 'Pipelines CI/CD',
        description:
          'CodePipeline orquesta el pipeline. CodeBuild ejecuta builds y tests. CodeDeploy para deployments blue/green. ECR para el registry de imágenes Docker. Muchos equipos combinan CodeBuild con GitHub Actions o prefieren herramientas externas como CircleCI o GitLab CI conectadas a AWS.',
      },
      {
        title: 'Arquitecturas event-driven',
        description:
          'Lambda + SQS + SNS + EventBridge para sistemas desacoplados y resilientes. Los eventos fluyen entre servicios sin acoplamiento directo. Cada servicio escala independientemente. DLQ captura mensajes fallidos para re-procesar. Step Functions para flujos de trabajo complejos con estados y reintentos.',
      },
    ],
    interviewQA: [
      {
        question: '¿Cuál es la diferencia entre Security Groups y NACLs en AWS?',
        answer:
          'Security Groups: stateful (si permites entrada, la salida relacionada se permite automáticamente), operan a nivel de instancia/ENI, solo reglas de allow (no deny). NACLs: stateless (debes definir reglas de entrada Y salida explícitamente), operan a nivel de subnet, soportan reglas de allow y deny, se evalúan en orden numérico. En la práctica, Security Groups son suficientes para la mayoría de casos; NACLs se usan para bloquear IPs específicas a nivel de subnet.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es un cold start en Lambda y cómo lo mitigas?',
        answer:
          'Un cold start ocurre cuando Lambda debe inicializar un nuevo execution environment: descarga el código, arranca el runtime, ejecuta el código de inicialización (fuera del handler). Puede tomar desde ms hasta segundos para runtimes como Java/C#. Mitigaciones: 1) Provisioned Concurrency (pre-calienta instancias, tiene costo), 2) usar runtimes más rápidos (Node.js, Python), 3) mantener el código de inicialización fuera del handler al mínimo, 4) SnapStart para Java (guarda el snapshot del estado inicializado).',
        difficulty: 'medium',
      },
      {
        question: '¿Cuándo usarías DynamoDB en lugar de RDS?',
        answer:
          'DynamoDB cuando: necesitas latencia single-digit millisecond a cualquier escala, el acceso es predecible (accedes por la partition key), tienes cargas de trabajo con picos extremos (DynamoDB escala en segundos), o necesitas una tabla global multi-región. RDS/Aurora cuando: necesitas queries SQL complejas con JOINs, transacciones ACID complejas, el modelo de datos es relacional y no conoces todos los patrones de acceso de antemano.',
        difficulty: 'hard',
      },
      {
        question: '¿Qué es el principio de mínimo privilegio en IAM?',
        answer:
          'Dar a cada usuario, servicio o rol solo los permisos mínimos necesarios para realizar su función. En la práctica: las Lambdas tienen roles específicos con solo los permisos que necesitan (no AdministratorAccess), los desarrolladores tienen acceso read-only en producción, los pipelines de CI/CD tienen permisos limitados a los servicios que deployan. Esto limita el blast radius si hay una brecha de seguridad.',
        difficulty: 'easy',
      },
      {
        question: '¿Cuál es la diferencia entre SQS y SNS?',
        answer:
          'SQS es una cola de mensajes: un productor envía mensajes y uno o más consumidores los procesan (cada mensaje es procesado por UN consumidor). Útil para desacoplar componentes y procesar trabajo en background. SNS es pub/sub: un publisher envía un mensaje a un topic y TODOS los subscribers lo reciben simultáneamente (fan-out). Un patrón común: SNS → múltiples SQS queues para que diferentes servicios procesen el mismo evento de forma independiente.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es el Multi-AZ en RDS y cuándo es necesario?',
        answer:
          'Multi-AZ mantiene una réplica sincrónica de la DB en otra Availability Zone. Si la instancia primaria falla, RDS hace failover automático a la réplica (típicamente 1-2 minutos). El failover es transparente para la aplicación (mismo endpoint). Multi-AZ es para alta disponibilidad (HA), no para performance (la réplica no sirve reads). Para reads, usa Read Replicas. Multi-AZ es necesario en cualquier sistema de producción que requiera uptime alto.',
        difficulty: 'medium',
      },
    ],
    codeExamples: [
      {
        title: 'Lambda con TypeScript y API Gateway',
        description: 'Función Lambda con tipos, manejo de errores y response helpers.',
        language: 'typescript',
        code: `import { APIGatewayProxyEvent, APIGatewayProxyResult } from 'aws-lambda';

interface CreateUserBody {
  name: string;
  email: string;
}

function response(statusCode: number, body: unknown): APIGatewayProxyResult {
  return {
    statusCode,
    headers: { 'Content-Type': 'application/json', 'Access-Control-Allow-Origin': '*' },
    body: JSON.stringify(body),
  };
}

export const handler = async (
  event: APIGatewayProxyEvent,
): Promise<APIGatewayProxyResult> => {
  try {
    if (!event.body) {
      return response(400, { error: 'Body is required' });
    }

    const body = JSON.parse(event.body) as CreateUserBody;

    if (!body.name || !body.email) {
      return response(400, { error: 'name and email are required' });
    }

    // Lógica de negocio
    const user = await createUser(body);

    return response(201, { data: user });
  } catch (error) {
    console.error('Unhandled error:', error);
    return response(500, { error: 'Internal server error' });
  }
};

async function createUser(data: CreateUserBody) {
  // Interacción con DynamoDB, RDS, etc.
  return { id: crypto.randomUUID(), ...data, createdAt: new Date().toISOString() };
}`,
      },
      {
        title: 'Infraestructura con AWS CDK (TypeScript)',
        description: 'Stack CDK con Lambda, API Gateway y DynamoDB.',
        language: 'typescript',
        code: `import * as cdk from 'aws-cdk-lib';
import * as lambda from 'aws-cdk-lib/aws-lambda';
import * as apigateway from 'aws-cdk-lib/aws-apigateway';
import * as dynamodb from 'aws-cdk-lib/aws-dynamodb';
import * as nodejs from 'aws-cdk-lib/aws-lambda-nodejs';
import { Construct } from 'constructs';

export class ApiStack extends cdk.Stack {
  constructor(scope: Construct, id: string, props?: cdk.StackProps) {
    super(scope, id, props);

    // DynamoDB table
    const table = new dynamodb.Table(this, 'UsersTable', {
      partitionKey: { name: 'pk', type: dynamodb.AttributeType.STRING },
      sortKey: { name: 'sk', type: dynamodb.AttributeType.STRING },
      billingMode: dynamodb.BillingMode.PAY_PER_REQUEST,
      removalPolicy: cdk.RemovalPolicy.DESTROY, // Solo para dev
    });

    // Lambda function
    const usersLambda = new nodejs.NodejsFunction(this, 'UsersHandler', {
      entry: 'src/handlers/users.ts',
      runtime: lambda.Runtime.NODEJS_20_X,
      environment: { TABLE_NAME: table.tableName },
      timeout: cdk.Duration.seconds(10),
      memorySize: 256,
    });

    // Dar permisos a Lambda para leer/escribir en DynamoDB
    table.grantReadWriteData(usersLambda);

    // API Gateway
    const api = new apigateway.RestApi(this, 'UsersApi', {
      restApiName: 'Users Service',
      defaultCorsPreflightOptions: { allowOrigins: apigateway.Cors.ALL_ORIGINS },
    });

    const users = api.root.addResource('users');
    users.addMethod('GET', new apigateway.LambdaIntegration(usersLambda));
    users.addMethod('POST', new apigateway.LambdaIntegration(usersLambda));

    new cdk.CfnOutput(this, 'ApiUrl', { value: api.url });
  }
}`,
      },
    ],
    quiz: [
      {
        question: '¿Cuál es el límite de tiempo máximo de ejecución de una Lambda?',
        options: ['1 minuto', '5 minutos', '15 minutos', '1 hora'],
        correctIndex: 2,
        explanation:
          'El timeout máximo de una función Lambda es 15 minutos (900 segundos). El default es 3 segundos. Para procesos más largos, considera Step Functions, ECS Fargate tasks, o EC2. Lambda es ideal para operaciones cortas y event-driven.',
      },
      {
        question: '¿Qué tipo de durabilidad ofrece S3 Standard?',
        options: ['99.9%', '99.99%', '99.999999999% (11 nines)', '100%'],
        correctIndex: 2,
        explanation:
          'S3 Standard ofrece 99.999999999% (11 nines) de durabilidad, lo que significa que si almacenas 10 millones de objetos, puedes esperar perder un objeto cada 10,000 años en promedio. Logra esto almacenando datos redundantemente en al menos 3 AZs.',
      },
      {
        question: '¿Cuándo deberías usar una Presigned URL de S3?',
        options: [
          'Para hacer un bucket público temporalmente',
          'Para dar acceso temporal a un objeto específico sin exponer credenciales AWS',
          'Para acelerar la subida de archivos grandes',
          'Para encriptar objetos en S3',
        ],
        correctIndex: 1,
        explanation:
          'Las Presigned URLs permiten acceso temporal (con expiración) a un objeto S3 específico sin exponer credenciales AWS. El servidor genera la URL firmada y la entrega al cliente, quien puede descargar o subir directamente a S3. Ideal para: permitir que usuarios descarguen archivos privados o suban archivos directamente a S3 desde el browser (evitando pasar por el servidor).',
      },
      {
        question: '¿Qué es una Availability Zone en AWS?',
        options: [
          'Un datacenter individual de AWS',
          'Una región geográfica de AWS',
          'Uno o más datacenters físicamente separados dentro de una región, con conectividad de alta velocidad entre ellos',
          'Un edge location de CloudFront',
        ],
        correctIndex: 2,
        explanation:
          'Una AZ es uno o más datacenters físicamente separados (con energía, refrigeración y networking independientes) dentro de la misma región. La separación física protege contra fallos de una instalación. Para alta disponibilidad, distribuye recursos en múltiples AZs dentro de la misma región.',
      },
      {
        question: '¿Qué hace RDS Proxy y por qué es importante para Lambda?',
        options: [
          'Actúa como CDN para queries SQL',
          'Gestiona un pool de conexiones a RDS, evitando que Lambda abra miles de conexiones directas',
          'Encripta las conexiones a la base de datos',
          'Replica automáticamente la base de datos entre regiones',
        ],
        correctIndex: 1,
        explanation:
          'Lambda puede escalar a miles de instancias simultáneas, y cada una intentaría abrir una conexión a RDS. Las bases de datos tienen límite de conexiones (PostgreSQL por ejemplo). RDS Proxy mantiene un pool de conexiones persistentes a RDS y las multiplexea entre las invocaciones de Lambda, reduciendo el número de conexiones reales a la DB de miles a decenas.',
      },
    ],
    exercises: [
      {
        title: 'Pipeline serverless de procesamiento de imágenes',
        difficulty: 'hard',
        description:
          'Diseña e implementa (con CDK) un pipeline que: 1) Recibe la subida de una imagen a S3 via Presigned URL, 2) El evento S3 dispara una Lambda, 3) La Lambda genera thumbnails de 3 tamaños con Sharp, los guarda en S3, y registra el resultado en DynamoDB, 4) Si falla, el mensaje va a una DLQ y una segunda Lambda lo notifica vía SNS. Usa TypeScript y maneja errores correctamente.',
        hints: [
          'Usa s3.addEventNotification para conectar S3 → Lambda',
          'Sharp en Lambda requiere una Lambda Layer o incluirlo en el bundle con bundling options',
          'La Lambda debe tener IAM permissions para leer del bucket origen y escribir en el de thumbnails',
          'Usa una DLQ en la configuración del Event Source Mapping de S3',
        ],
        tags: ['Lambda', 'S3', 'CDK', 'Serverless'],
      },
      {
        title: 'API REST con autenticación usando Cognito',
        difficulty: 'medium',
        description:
          'Crea una API con Lambda + API Gateway protegida con AWS Cognito User Pool. Los usuarios se registran, verifican email, y hacen login para obtener JWT. La API verifica el token con un Cognito Authorizer. Implementa endpoints: POST /auth/register, POST /auth/login, GET /profile (protegido). Usa CDK para toda la infraestructura.',
        hints: [
          'CognitoUserPoolAuthorizer en API Gateway valida los JWT automáticamente',
          'Usa el Cognito Admin SDK desde Lambda para crear usuarios programáticamente',
          'El token JWT de Cognito tiene claims como sub, email, cognito:groups',
          'Para login, usa InitiateAuth con USER_PASSWORD_AUTH flow',
        ],
        tags: ['Cognito', 'Auth', 'API Gateway', 'CDK'],
      },
    ],
  },
  {
    slug: 'software-architecture',
    title: 'Arquitectura de Software',
    shortDescription: 'Principios y patrones para diseñar sistemas escalables, mantenibles y resilientes.',
    icon: 'architecture',
    color: 'text-purple-400',
    category: 'architecture',
    concepts: [
      {
        title: 'Monolito vs Microservicios',
        description:
          '¿Por qué importa? Elegir el estilo arquitectural equivocado tiene consecuencias que duran años. Un monolito es simple al inicio: un solo despliegue, sin latencia de red entre módulos, transacciones ACID directas. Pero con el tiempo, si el equipo crece y el dominio es complejo, el monolito se vuelve frágil: cualquier cambio puede romper otras partes, los deploys se vuelven lentos y riesgosos. Los microservicios resuelven eso dividiendo el sistema en servicios autónomos con ciclos de vida independientes. El costo: complejidad operativa (observabilidad, red, consistencia eventual). La regla de Martin Fowler: empieza con un monolito bien estructurado; migra a microservicios cuando el monolito duela.',
        tags: ['Estilos', 'Trade-offs'],
      },
      {
        title: 'SOLID principles',
        description:
          '¿Por qué importa? Los principios SOLID son la diferencia entre código que escala con el equipo y código que se convierte en un problema de mantenimiento. S (Single Responsibility): cada clase tiene una razón para cambiar; evita clases-dios que hacen todo. O (Open/Closed): abierto a extensión, cerrado a modificación; evita tocar código que funciona para agregar features. L (Liskov Substitution): las subclases deben ser sustituibles por su base; evita herencias que rompen contratos. I (Interface Segregation): interfaces pequeñas y específicas; evita depender de métodos que no usas. D (Dependency Inversion): depende de abstracciones, no de implementaciones; permite mockear y testear.',
        tags: ['Principios', 'OOP'],
      },
      {
        title: 'Domain-Driven Design (DDD)',
        description:
          '¿Por qué importa? La mayoría de los bugs no son errores de código sino errores de comprensión del dominio. DDD pone el modelo del negocio en el centro: el código debe hablar el mismo idioma que los expertos del dominio (Ubiquitous Language). Los Bounded Contexts delimitan dónde aplica cada modelo. Los Aggregates garantizan la consistencia de negocio. Los Domain Events capturan lo que ocurrió. Es especialmente valioso en sistemas complejos donde el dominio tiene reglas de negocio ricas.',
        tags: ['DDD', 'Dominio'],
      },
      {
        title: 'Event-Driven Architecture',
        description:
          '¿Por qué importa? En sistemas distribuidos, el acoplamiento sincrónico crea fragilidad: si el servicio B está caído, el servicio A no puede funcionar. La arquitectura event-driven desacopla productores y consumidores: el productor emite un evento ("OrderPlaced") sin saber quién lo procesará. Los consumidores reaccionan de forma independiente y pueden fallar sin afectar al productor. El costo es la complejidad: consistencia eventual, debugging más difícil, duplicación de eventos. Úsala cuando el desacoplamiento y la resiliencia valen más que la simplicidad.',
        tags: ['Eventos', 'Desacoplamiento'],
      },
      {
        title: 'CAP Theorem',
        description:
          '¿Por qué importa? En sistemas distribuidos, no puedes tener todo. El teorema CAP dice que en presencia de una partición de red solo puedes elegir entre Consistencia (todos los nodos ven el mismo dato) o Disponibilidad (el sistema siempre responde). PostgreSQL elige CP: en una partición, el sistema puede volverse indisponible para garantizar que no sirves datos stale. DynamoDB elige AP: siempre responde, pero puede servir datos eventualmente consistentes. Entender este trade-off es clave para diseñar sistemas que fallen de forma predecible.',
        tags: ['Distributed Systems', 'Trade-offs'],
      },
      {
        title: 'CQRS y Event Sourcing',
        description:
          '¿Por qué importa? En muchos sistemas, los patrones de lectura y escritura son muy diferentes: las escrituras son complejas (validaciones, reglas de negocio) pero las lecturas necesitan ser ultra-rápidas y en formatos específicos para cada pantalla. CQRS (Command Query Responsibility Segregation) separa el modelo de escritura del de lectura, permitiendo optimizar cada uno independientemente. Event Sourcing va más lejos: en lugar de guardar el estado actual, guarda todos los eventos que llevaron a ese estado. Esto da audit trail completo y la capacidad de reconstruir el estado en cualquier punto del tiempo. El costo es la complejidad de implementación.',
        tags: ['CQRS', 'Event Sourcing'],
      },
      {
        title: 'API Design: REST, GraphQL y gRPC',
        description:
          '¿Por qué importa? El API es el contrato entre tu sistema y sus consumidores; cambiarlo tiene un costo alto. REST es el estándar para APIs públicas: familiar, cacheable, stateless. GraphQL resuelve el over/under-fetching de REST en sistemas donde el cliente necesita datos de múltiples recursos en formatos variables (BFF, apps móviles con diferentes necesidades). gRPC es más eficiente para comunicación interna entre microservicios: tipado fuerte con Protobuf, bi-directional streaming, pero más complejo de consumir desde el browser.',
        tags: ['API', 'Contratos'],
      },
      {
        title: 'Observabilidad: Logs, Metrics y Traces',
        description:
          '¿Por qué importa? Un sistema que no puedes observar no puedes operarlo. Los tres pilares: Logs (eventos que ocurrieron, con contexto suficiente para debuggear), Metrics (tendencias numéricas a lo largo del tiempo: latencia p99, error rate, throughput), Traces (seguimiento de una request a través de múltiples servicios). La diferencia entre un sistema que se cae a las 2am y tardas 4 horas en entender qué pasó vs 10 minutos es la calidad de la observabilidad. Correlation IDs permiten rastrear una request a través de todos los logs.',
        tags: ['Operaciones', 'Resiliencia'],
      },
    ],
    useCases: [
      {
        title: 'Diseño de sistemas a gran escala',
        description:
          'Las entrevistas de system design y el trabajo real requieren tomar decisiones arquitecturales: qué base de datos elegir, cómo escalar horizontalmente, cómo manejar picos de tráfico, cómo garantizar disponibilidad. Entender los trade-offs de cada decisión es más valioso que conocer una arquitectura específica.',
      },
      {
        title: 'Refactoring de monolitos legacy',
        description:
          'La mayoría de los proyectos no nacen perfectos. Aplicar principios SOLID, identificar Bounded Contexts y extraer módulos o microservicios de forma incremental (Strangler Fig Pattern) permite mejorar la arquitectura sin reescribir todo.',
      },
      {
        title: 'Diseño de APIs y contratos',
        description:
          'Decidir entre REST, GraphQL o gRPC según los consumidores, manejar versioning de APIs, diseñar para la evolución (backward compatibility), documentar contratos con OpenAPI/Protobuf.',
      },
    ],
    interviewQA: [
      {
        question: '¿Cuándo elegiría microservicios sobre un monolito?',
        answer:
          'Los microservicios tienen sentido cuando: el equipo es grande (más de ~10 ingenieros) y los equipos deben deployar de forma independiente; el dominio tiene subdominios claramente separados con diferentes requerimientos de escala; diferentes partes del sistema necesitan diferentes tecnologías o ciclos de deployment. No elegir microservicios si: el equipo es pequeño, el dominio no está bien entendido todavía, o la complejidad operativa no está justificada. El error más común es adoptar microservicios demasiado pronto.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es la consistencia eventual y cuándo es aceptable?',
        answer:
          'La consistencia eventual significa que el sistema eventualmente llegará a un estado consistente, pero puede haber un período donde diferentes nodos ven diferentes valores. Es aceptable cuando: la baja latencia importa más que la consistencia fuerte (feeds de redes sociales, likes, contadores), o cuando la disponibilidad es crítica y las particiones de red son probables. No es aceptable cuando: operas con dinero (transferencias bancarias), inventario en tiempo real, o cualquier caso donde servir datos stale causa daño real al negocio.',
        difficulty: 'hard',
      },
      {
        question: '¿Qué es el Strangler Fig Pattern?',
        answer:
          'Es una estrategia para migrar un sistema legacy de forma incremental. En lugar de reescribir todo, colocas un proxy/facade frente al monolito y vas extrayendo funcionalidades una a una al nuevo sistema. El nuevo sistema "estrangula" al viejo gradualmente. Ventajas: reduces el riesgo de una reescritura big-bang, puedes hacer rollback fácilmente, el sistema sigue funcionando durante la migración. Aplica el principio de "no cambiar lo que funciona".',
        difficulty: 'hard',
      },
      {
        question: '¿Cuál es la diferencia entre escalado horizontal y vertical?',
        answer:
          'Escalado vertical (scale up): más CPU/RAM/disco a la misma máquina. Simple, sin cambios de código, pero tiene límites físicos y es costoso. Un solo punto de fallo. Escalado horizontal (scale out): más instancias de la misma máquina. Sin límite teórico, más resiliente (no hay single point of failure), pero requiere que la aplicación sea stateless o que el estado esté externalizado (DB, Redis). Los sistemas modernos diseñan para escalar horizontalmente desde el inicio.',
        difficulty: 'easy',
      },
      {
        question: '¿Qué hace un API Gateway en una arquitectura de microservicios?',
        answer:
          'El API Gateway es el punto de entrada único para todos los clientes. Responsabilidades: routing (dirige requests al microservicio correcto), autenticación/autorización (centraliza la verificación de JWT), rate limiting, SSL termination, request/response transformation, aggregation (combina respuestas de múltiples servicios). Ventaja: los clientes no necesitan conocer la topología interna. Riesgo: puede convertirse en un cuello de botella o en un monolito si contiene demasiada lógica de negocio.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es el Circuit Breaker pattern y por qué es necesario?',
        answer:
          'En sistemas distribuidos, cuando un servicio falla, las llamadas que esperan su respuesta acumulan threads/conexiones y pueden cascadear la falla a otros servicios. El Circuit Breaker monitorea los fallos: en estado "closed" deja pasar las llamadas normalmente; si los fallos superan un umbral, se "abre" y rechaza inmediatamente las llamadas (fail fast) sin esperar timeout; tras un tiempo, pasa a "half-open" para probar si el servicio se recuperó. Evita el efecto cascada y mejora la resiliencia del sistema.',
        difficulty: 'hard',
      },
    ],
    codeExamples: [
      {
        title: 'Repository pattern con abstracción',
        description: 'Separación entre lógica de negocio y acceso a datos para facilitar testing y cambios.',
        language: 'typescript',
        code: `// La abstracción (puerto) — lógica de negocio depende de esto, no de Prisma
interface UserRepository {
  findById(id: string): Promise<User | null>;
  findByEmail(email: string): Promise<User | null>;
  save(user: User): Promise<User>;
  delete(id: string): Promise<void>;
}

// La implementación concreta (adaptador) — puede cambiarse sin tocar la lógica
class PrismaUserRepository implements UserRepository {
  constructor(private prisma: PrismaClient) {}

  async findById(id: string) {
    return this.prisma.user.findUnique({ where: { id } });
  }

  async findByEmail(email: string) {
    return this.prisma.user.findUnique({ where: { email } });
  }

  async save(user: User) {
    return this.prisma.user.upsert({
      where: { id: user.id },
      create: user,
      update: user,
    });
  }

  async delete(id: string) {
    await this.prisma.user.delete({ where: { id } });
  }
}

// Lógica de negocio — no sabe nada de Prisma
class UserService {
  constructor(private repo: UserRepository) {} // Depende de la abstracción

  async registerUser(email: string, password: string): Promise<User> {
    const existing = await this.repo.findByEmail(email);
    if (existing) throw new ConflictError('Email already registered');

    const user = User.create({ email, password: await hash(password) });
    return this.repo.save(user);
  }
}

// En tests: inyectar un mock sin base de datos real
const mockRepo: UserRepository = {
  findByEmail: jest.fn().mockResolvedValue(null),
  save: jest.fn().mockImplementation(u => u),
  findById: jest.fn(),
  delete: jest.fn(),
};
const service = new UserService(mockRepo);`,
      },
      {
        title: 'Circuit Breaker implementation',
        description: 'Implementación básica del patrón Circuit Breaker para llamadas a servicios externos.',
        language: 'typescript',
        code: `type CircuitState = 'closed' | 'open' | 'half-open';

interface CircuitBreakerOptions {
  failureThreshold: number;   // Fallos para abrir el circuito
  successThreshold: number;   // Éxitos para cerrar desde half-open
  timeout: number;            // Ms que espera antes de pasar a half-open
}

class CircuitBreaker {
  private state: CircuitState = 'closed';
  private failureCount = 0;
  private successCount = 0;
  private nextAttempt = Date.now();

  constructor(private options: CircuitBreakerOptions) {}

  async call<T>(fn: () => Promise<T>): Promise<T> {
    if (this.state === 'open') {
      if (Date.now() < this.nextAttempt) {
        throw new Error('Circuit is OPEN — service unavailable');
      }
      this.state = 'half-open';
      console.log('Circuit entering HALF-OPEN state');
    }

    try {
      const result = await fn();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure();
      throw error;
    }
  }

  private onSuccess() {
    this.failureCount = 0;
    if (this.state === 'half-open') {
      this.successCount++;
      if (this.successCount >= this.options.successThreshold) {
        this.state = 'closed';
        this.successCount = 0;
        console.log('Circuit CLOSED — service recovered');
      }
    }
  }

  private onFailure() {
    this.failureCount++;
    if (this.failureCount >= this.options.failureThreshold) {
      this.state = 'open';
      this.nextAttempt = Date.now() + this.options.timeout;
      console.log(\`Circuit OPEN — will retry at \${new Date(this.nextAttempt)}\`);
    }
  }
}

// Uso
const paymentBreaker = new CircuitBreaker({ failureThreshold: 5, successThreshold: 2, timeout: 30_000 });

async function chargeCard(amount: number) {
  return paymentBreaker.call(() => paymentGateway.charge(amount));
}`,
      },
    ],
    quiz: [
      {
        question: '¿Por qué el principio Open/Closed es importante en equipos grandes?',
        options: [
          'Porque hace el código más rápido',
          'Porque permite agregar nueva funcionalidad sin modificar código existente que ya funciona y está testeado, reduciendo el riesgo de regresiones',
          'Porque elimina la necesidad de tests',
          'Porque reduce el número de archivos en el proyecto',
        ],
        correctIndex: 1,
        explanation:
          'En equipos grandes, el código existente y testeado es valioso. El principio Open/Closed protege ese código: extiendes el comportamiento con nuevas clases/módulos en lugar de modificar los existentes. Esto reduce el riesgo de introducir bugs en funcionalidad que ya funciona. Frameworks como NestJS y Angular están diseñados siguiendo este principio (puedes agregar Guards, Pipes e Interceptors sin modificar el core).',
      },
      {
        question: '¿Cuál es el principal beneficio del patrón Repository?',
        options: [
          'Hace las queries SQL más rápidas',
          'Desacopla la lógica de negocio del mecanismo de persistencia, facilitando testing y la posibilidad de cambiar la base de datos',
          'Elimina la necesidad de un ORM',
          'Mejora el performance de la base de datos',
        ],
        correctIndex: 1,
        explanation:
          'El Repository desacopla tu lógica de negocio (que no debería saber si usas Prisma, TypeORM o una API REST) del mecanismo concreto de persistencia. Esto permite: testear la lógica de negocio con mocks sin una DB real, cambiar de Prisma a TypeORM sin tocar los Services, y mantener el código de negocio limpio y readable.',
      },
      {
        question: '¿Por qué se dice que debes "diseñar para el fallo" en sistemas distribuidos?',
        options: [
          'Porque es imposible escribir código sin bugs',
          'Porque en sistemas distribuidos los fallos parciales son inevitables: la red puede fallar, los servicios pueden estar lentos o caídos. Diseñar para el fallo significa que el sistema se degrada graciosamente en lugar de fallar completamente',
          'Para tener un pretexto cuando algo falla en producción',
          'Porque los tests siempre van a fallar',
        ],
        correctIndex: 1,
        explanation:
          'En sistemas distribuidos, la pregunta no es si algo va a fallar, sino cuándo. La red es no confiable, los servicios tienen downtime, los timeouts ocurren. Diseñar para el fallo implica: timeouts en todas las llamadas externas, circuit breakers, reintentos con backoff, fallbacks, degradación gradual. Un sistema bien diseñado bajo el principio de "design for failure" sigue funcionando (parcialmente) aunque algunos componentes fallen.',
      },
      {
        question: '¿Cuándo es apropiado usar Event Sourcing?',
        options: [
          'En todas las aplicaciones para tener audit trail',
          'Cuando el historial completo de cambios es un requerimiento de negocio (finanzas, legal), cuando necesitas reconstruir el estado en cualquier punto del tiempo, o cuando el modelo de eventos enriquece el dominio',
          'Solo en sistemas con más de 1 millón de usuarios',
          'Siempre que uses microservicios',
        ],
        correctIndex: 1,
        explanation:
          'Event Sourcing tiene un costo real: mayor complejidad, eventual consistency, queries más complejas. Vale la pena cuando: el negocio necesita auditoría completa (¿quién cambió qué y cuándo?), cuando necesitas "time travel" (reconstruir el estado en el pasado), o en dominios financieros donde cada transacción debe ser registrada. No lo uses por defecto — agrega complejidad sin beneficio si el dominio no lo necesita.',
      },
      {
        question: '¿Qué problema resuelve el patrón Saga en microservicios?',
        options: [
          'La autenticación entre servicios',
          'Las transacciones distribuidas: coordinar una operación que abarca múltiples servicios garantizando consistencia eventual con compensación si algo falla',
          'El balanceo de carga entre instancias',
          'El versionado de APIs',
        ],
        correctIndex: 1,
        explanation:
          'Las transacciones ACID no funcionan entre microservicios. La Saga coordina una secuencia de transacciones locales: cada paso publica un evento que dispara el siguiente. Si un paso falla, se ejecutan transacciones compensatorias para deshacer los pasos anteriores (ej: si el pago falla, se libera el inventario reservado). Hay dos variantes: Choreography (los servicios reaccionan a eventos) y Orchestration (un orchestrador coordina los pasos).',
      },
    ],
    exercises: [
      {
        title: 'Diseño de sistema: URL Shortener',
        difficulty: 'medium',
        description:
          'Diseña un servicio de acortamiento de URLs similar a bit.ly. Requerimientos: 100M de URLs por día (escritura), 10x más reads que writes, las URLs deben ser únicas de 6-8 caracteres, latencia de redirect < 10ms p99. Documenta: elección de base de datos (justificando por qué), estrategia de generación de IDs cortos, capas de caché, cómo escalar. Identifica los cuellos de botella y cómo los resolverías.',
        hints: [
          'Calcula: 100M writes/día = ~1200 writes/segundo. ¿Qué DB aguanta esto?',
          'El redirect es la operación crítica: considera un caché de Redis con TTL',
          'Para IDs únicos: hash de la URL (MD5/SHA-256) + tomar los primeros 7 chars vs counter-based (Base62 de un ID autoincremental)',
          'La lectura es 10x más frecuente que la escritura: optimiza el read path',
        ],
        tags: ['System Design', 'Caching', 'Escalabilidad'],
      },
      {
        title: 'Implementar un sistema de pedidos con DDD',
        difficulty: 'hard',
        description:
          'Modela un sistema de pedidos online usando DDD. Identifica los Bounded Contexts (Orders, Inventory, Payments, Notifications). Implementa el Aggregate Order con sus reglas de negocio (no se puede cancelar un pedido ya enviado, el total debe ser consistente con los items). Usa Domain Events para comunicación entre contextos (OrderPlaced dispara la reserva de inventario y el inicio del pago). Implementa en TypeScript con el Repository pattern.',
        hints: [
          'El Aggregate Order es la raíz: Order contiene OrderItems, no al revés',
          'Los Domain Events se emiten dentro del Aggregate pero se dispatched fuera (por el Application Service)',
          'Cada Bounded Context tiene su propio modelo: Order en el contexto Orders tiene precio; en Shipping solo tiene dirección',
          'Usa Value Objects para conceptos como Money, Address, OrderStatus',
        ],
        tags: ['DDD', 'Domain Events', 'Aggregates'],
      },
    ],
  },
  {
    slug: 'ai-engineer',
    title: 'AI Engineer',
    shortDescription: 'Fundamentos para construir aplicaciones con LLMs, RAG, embeddings y agentes de IA.',
    icon: 'psychology',
    color: 'text-violet-400',
    category: 'ai',
    concepts: [
      {
        title: 'LLMs y cómo funcionan',
        description:
          '¿Por qué importa? Entender qué puede y qué no puede hacer un LLM evita el 80% de las frustraciones al construir con ellos. Los LLMs son transformers entrenados para predecir el siguiente token. No razonan como humanos: generan texto estadísticamente plausible. Esto explica las "alucinaciones": el modelo no sabe lo que no sabe, genera lo más probable. El context window es la memoria de trabajo del modelo. Los modelos tienen un knowledge cutoff. Saber esto permite diseñar sistemas que compensan las limitaciones: RAG para datos actuales, prompts estructurados para reducir alucinaciones, verificación de outputs.',
        tags: ['LLMs', 'Fundamentos'],
      },
      {
        title: 'Prompt Engineering',
        description:
          '¿Por qué importa? El 70% de los problemas con LLMs en producción se resuelven con mejores prompts. Técnicas: zero-shot (solo instrucción), few-shot (ejemplos en el prompt), chain-of-thought (pedir al modelo que razone paso a paso antes de responder). System prompt para definir el rol y las restricciones. Structured outputs (JSON mode) para salidas predecibles. Temperature controla aleatoriedad (0 para respuestas deterministas, >0.7 para creatividad). El prompt es código: versioánalo, testealo, itéralo.',
        tags: ['Prompting', 'LLMs'],
      },
      {
        title: 'Embeddings y búsqueda semántica',
        description:
          '¿Por qué importa? Los embeddings son la base de casi toda aplicación de AI útil con datos propios. Un embedding es una representación numérica (vector) de texto que captura el significado semántico: textos similares tienen vectores similares. La similitud se mide con cosine similarity o dot product. Los vector databases (Pinecone, Qdrant, pgvector) permiten búsqueda aproximada eficiente (ANN) sobre millones de vectores. Esto habilita: búsqueda semántica ("busco artículos sobre calentamiento global" encuentra texto que no contiene esa frase exacta), recomendaciones, y RAG.',
        tags: ['Embeddings', 'Vectores'],
      },
      {
        title: 'RAG: Retrieval Augmented Generation',
        description:
          '¿Por qué importa? Los LLMs no conocen tus datos internos y su conocimiento está desactualizado. RAG es el patrón que resuelve esto: en lugar de hacer fine-tuning (costoso), buscas los documentos relevantes en tiempo real y los incluyes en el contexto del prompt. Pipeline: 1) Indexación: chunking del documento, embeddings de cada chunk, almacenamiento en vector DB. 2) Query: embedding de la pregunta, búsqueda de los K chunks más similares, incluirlos en el prompt. 3) Generation: el LLM genera la respuesta basándose en los chunks recuperados. La calidad del RAG depende críticamente del chunking y de la retrieval.',
        tags: ['RAG', 'Arquitectura'],
      },
      {
        title: 'Agentes y herramientas (Tool Use)',
        description:
          '¿Por qué importa? Los LLMs por sí solos no pueden ejecutar código, buscar en la web, ni tomar acciones. Los agentes les dan esa capacidad. Un agente es un LLM equipado con herramientas (funciones que puede llamar) que itera en un loop: razona → decide qué herramienta usar → ejecuta → observa el resultado → razona de nuevo. El loop más conocido es ReAct (Reasoning + Acting). Los riesgos: los agentes pueden tomar acciones irreversibles, consumir muchos tokens, y son difíciles de predecir. Diseña con el principio de mínimo privilegio: solo dale al agente las herramientas que necesita.',
        tags: ['Agentes', 'Tool Use'],
      },
      {
        title: 'Evaluación de sistemas LLM',
        description:
          '¿Por qué importa? Sin evaluación, no puedes mejorar ni confiar en tu sistema en producción. Las métricas varían según el caso: para RAG, mide faithfulness (la respuesta está soportada por los documentos recuperados?), relevance (los documentos recuperados son relevantes?), answer correctness. Para generación libre, LLM-as-a-judge (usar otro LLM para evaluar la calidad). Frameworks: RAGAS para RAG, LangSmith para tracing. El golden dataset (preguntas + respuestas esperadas) es tu test suite de LLM.',
        tags: ['Evaluación', 'Calidad'],
      },
      {
        title: 'Fine-tuning vs RAG vs Prompting',
        description:
          '¿Por qué importa? Elegir la técnica incorrecta desperdicia recursos. Prompting: siempre primero. Sorprendentemente efectivo con few-shot examples. Costo: solo tokens. RAG: cuando el modelo necesita acceso a datos específicos, actualizados o voluminosos que no caben en el contexto. Fine-tuning: cuando necesitas cambiar el comportamiento base del modelo (tono, formato, dominio muy especializado) y tienes suficientes datos de ejemplo de alta calidad. No usar para "enseñarle conocimiento" — para eso usa RAG. Fine-tuning es caro y requiere mantenimiento.',
        tags: ['Fine-tuning', 'RAG', 'Trade-offs'],
      },
      {
        title: 'Streaming y latencia en aplicaciones LLM',
        description:
          '¿Por qué importa? Los LLMs son lentos: una respuesta de 500 tokens puede tardar 5-10 segundos. Sin streaming, el usuario espera en blanco. Con streaming, los tokens aparecen conforme se generan (como ChatGPT). En la API: stream: true retorna un stream de Server-Sent Events. El cliente muestra los tokens incrementalmente. La latencia percibida baja drásticamente aunque el tiempo total sea el mismo. Time-to-first-token (TTFT) es la métrica clave: cuánto tarda en aparecer el primer token.',
        tags: ['Streaming', 'UX', 'Performance'],
      },
    ],
    useCases: [
      {
        title: 'Chatbots y asistentes sobre documentos propios',
        description:
          'El caso de uso más común: "pregúntale a tu documentación". RAG sobre PDFs, bases de conocimiento, documentación técnica. Los usuarios hacen preguntas en lenguaje natural y obtienen respuestas contextualizadas con referencias a los documentos fuente.',
      },
      {
        title: 'Extracción y estructuración de datos',
        description:
          'Los LLMs son excelentes para extraer información no estructurada: parsear facturas, extraer entidades de texto libre, clasificar emails, normalizar datos de diferentes formatos. Structured outputs (JSON mode) garantiza una respuesta parseable. Mucho más flexible que regex o reglas manuales.',
      },
      {
        title: 'Generación de código y asistentes de desarrollo',
        description:
          'Code completion, explicación de código, generación de tests, revisión automática de PRs, generación de documentación. Los modelos de código (GPT-4o, Claude, Gemini) son especialmente buenos en este dominio. El contexto del repo mejora significativamente la calidad.',
      },
      {
        title: 'Pipelines de procesamiento de contenido',
        description:
          'Resumen automático de artículos, traducción, moderación de contenido, generación de metadata (tags, descripciones), A/B testing de copy. Procesamiento batch sobre grandes volúmenes de texto con costos predecibles usando APIs de LLMs.',
      },
    ],
    interviewQA: [
      {
        question: '¿Qué es RAG y cuándo lo usarías en lugar de fine-tuning?',
        answer:
          'RAG (Retrieval Augmented Generation) recupera documentos relevantes y los incluye en el contexto del prompt para que el LLM pueda responder con información específica de tus datos. Usa RAG cuando: los datos cambian frecuentemente (no puedes re-entrenar constantemente), el volumen de conocimiento es grande, necesitas trazabilidad (qué documento usó el modelo para responder), o el tiempo/costo de fine-tuning no está justificado. Fine-tuning cuando: necesitas cambiar el estilo o comportamiento base del modelo, tienes un dominio muy especializado con vocabulario propio, o el modelo necesita "aprender" formatos de respuesta específicos.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué son los embeddings y para qué se usan?',
        answer:
          'Los embeddings son representaciones vectoriales de texto en un espacio de alta dimensión donde la proximidad refleja similitud semántica. El texto "gato" estará cerca de "felino" y lejos de "automóvil". Se usan para: búsqueda semántica (encontrar documentos conceptualmente similares a una query), clustering (agrupar textos por tema), clasificación, y como base del retrieval en RAG. La calidad del embedding model afecta directamente la calidad del RAG.',
        difficulty: 'easy',
      },
      {
        question: '¿Qué es el "chunking" en RAG y por qué es importante?',
        answer:
          'Chunking es dividir documentos grandes en fragmentos más pequeños para indexar y recuperar. La estrategia afecta directamente la calidad del RAG: chunks muy pequeños pierden contexto (una oración sin su párrafo), chunks muy grandes superan el contexto del retrieval y añaden ruido. Estrategias: fixed-size (cada N tokens con overlap), semántico (dividir en párrafos o secciones), recursivo (jerarquía de chunks grandes → pequeños). El overlap entre chunks evita perder información en los bordes.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es la temperatura en un LLM?',
        answer:
          'La temperatura controla la aleatoriedad del sampling del siguiente token. Temperature = 0: el modelo siempre elige el token más probable (determinista, reproducible). Temperature alta (>1): el modelo toma más riesgos, elige tokens menos probables (más creativo pero más impreciso). Para casos de uso donde la precisión importa (extracción de datos, código, respuestas factuales): temperatura baja (0-0.3). Para generación creativa: temperatura media-alta (0.7-1.0).',
        difficulty: 'easy',
      },
      {
        question: '¿Cómo reducirías las alucinaciones de un LLM en producción?',
        answer:
          'Estrategias: 1) RAG: basa las respuestas en documentos concretos y pide al modelo que cite la fuente. 2) Prompt engineering: instruye explícitamente "si no sabes la respuesta, di que no la sabes". 3) Temperature baja para respuestas factuales. 4) Structured outputs: si el modelo solo puede responder en un schema JSON definido, es más difícil que alucine. 5) Verificación programática: valida la respuesta contra datos conocidos. 6) LLM-as-judge: otro LLM verifica la respuesta. 7) Human-in-the-loop para casos de alto riesgo.',
        difficulty: 'medium',
      },
      {
        question: '¿Qué es el context window y cuáles son sus implicaciones?',
        answer:
          'El context window es la cantidad máxima de tokens que el modelo puede procesar en una llamada (prompt + respuesta). GPT-4o: 128K tokens. Claude: hasta 200K. Implicaciones: no puedes pasar documentos ilimitados (necesitas RAG o summarización). Los modelos degradan su rendimiento en contextos muy largos (lost in the middle: ignoran información en el medio del contexto). Más tokens = mayor costo y latencia. El diseño del prompt es un ejercicio de gestión de contexto: incluir exactamente lo necesario.',
        difficulty: 'medium',
      },
    ],
    codeExamples: [
      {
        title: 'RAG pipeline con Anthropic y Qdrant',
        description: 'Pipeline completo de indexación y consulta RAG usando Claude y Qdrant.',
        language: 'typescript',
        code: `import Anthropic from '@anthropic-ai/sdk';
import { QdrantClient } from '@qdrant/js-client-rest';

const client = new Anthropic();
const qdrant = new QdrantClient({ host: 'localhost', port: 6333 });
const COLLECTION = 'docs';

// 1. Indexación: chunking + embeddings + almacenamiento
async function indexDocuments(documents: { id: string; text: string }[]) {
  for (const doc of documents) {
    // Chunking simple por párrafos
    const chunks = doc.text.split('\\n\\n').filter(c => c.length > 50);

    for (let i = 0; i < chunks.length; i++) {
      // Generar embedding con el modelo de embeddings de Anthropic/OpenAI
      const embeddingRes = await client.messages.create({
        model: 'claude-opus-4-6',
        max_tokens: 1,
        messages: [{ role: 'user', content: chunks[i] }],
        // En producción usarías un modelo de embeddings real
      });

      // Almacenar en Qdrant
      await qdrant.upsert(COLLECTION, {
        points: [{
          id: \`\${doc.id}-\${i}\`,
          vector: generateEmbedding(chunks[i]), // tu función de embedding
          payload: { text: chunks[i], docId: doc.id, chunkIndex: i },
        }],
      });
    }
  }
}

// 2. Query: retrieval + generation
async function queryRAG(question: string): Promise<string> {
  // Retrieval: buscar los chunks más relevantes
  const questionEmbedding = generateEmbedding(question);
  const results = await qdrant.search(COLLECTION, {
    vector: questionEmbedding,
    limit: 5,
    with_payload: true,
  });

  // Construir contexto con los chunks recuperados
  const context = results
    .map((r, i) => \`[Fuente \${i + 1}]: \${r.payload?.text}\`)
    .join('\\n\\n');

  // Generation: Claude responde basándose en el contexto
  const response = await client.messages.create({
    model: 'claude-opus-4-6',
    max_tokens: 1024,
    system: \`Eres un asistente que responde preguntas basándose únicamente en el contexto proporcionado.
Si la información no está en el contexto, di explícitamente que no lo sabes.
Cita las fuentes cuando sea posible.\`,
    messages: [{
      role: 'user',
      content: \`Contexto:\\n\${context}\\n\\nPregunta: \${question}\`,
    }],
  });

  return response.content[0].type === 'text' ? response.content[0].text : '';
}

function generateEmbedding(text: string): number[] {
  // Placeholder — usa text-embedding-3-small de OpenAI o similar
  return Array(1536).fill(0).map(() => Math.random());
}`,
      },
      {
        title: 'Streaming de respuestas LLM',
        description: 'Streaming de tokens en tiempo real con Anthropic SDK.',
        language: 'typescript',
        code: `import Anthropic from '@anthropic-ai/sdk';

const client = new Anthropic();

// Streaming en el servidor (Node.js / NestJS)
async function streamChatResponse(userMessage: string, res: Response) {
  res.setHeader('Content-Type', 'text/event-stream');
  res.setHeader('Cache-Control', 'no-cache');
  res.setHeader('Connection', 'keep-alive');

  const stream = await client.messages.stream({
    model: 'claude-opus-4-6',
    max_tokens: 1024,
    messages: [{ role: 'user', content: userMessage }],
  });

  for await (const chunk of stream) {
    if (
      chunk.type === 'content_block_delta' &&
      chunk.delta.type === 'text_delta'
    ) {
      // Enviar cada token al cliente via SSE
      res.write(\`data: \${JSON.stringify({ text: chunk.delta.text })}\\n\\n\`);
    }
  }

  res.write('data: [DONE]\\n\\n');
  res.end();
}

// En el cliente (React)
async function streamChat(message: string, onToken: (text: string) => void) {
  const response = await fetch('/api/chat', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ message }),
  });

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const lines = decoder.decode(value).split('\\n');
    for (const line of lines) {
      if (line.startsWith('data: ') && line !== 'data: [DONE]') {
        const { text } = JSON.parse(line.slice(6));
        onToken(text);
      }
    }
  }
}`,
      },
    ],
    quiz: [
      {
        question: '¿Qué problema principal resuelve RAG?',
        options: [
          'Hace los LLMs más rápidos',
          'Permite que el LLM responda con información actualizada y específica que no está en su entrenamiento',
          'Reduce el costo de las llamadas a la API',
          'Elimina las alucinaciones completamente',
        ],
        correctIndex: 1,
        explanation:
          'RAG resuelve la limitación fundamental de los LLMs: su conocimiento está congelado en el momento del entrenamiento y no incluye tus datos privados. Al recuperar documentos relevantes en tiempo real e incluirlos en el contexto, el LLM puede responder con información actualizada y específica de tu dominio sin necesidad de reentrenamiento.',
      },
      {
        question: '¿Qué es la "temperatura" en los LLMs?',
        options: [
          'El número de tokens procesados por segundo',
          'El calor generado por el servidor durante la inferencia',
          'Un parámetro que controla la aleatoriedad: temperatura baja = respuestas más deterministas, temperatura alta = más creativo',
          'El tamaño del modelo en parámetros',
        ],
        correctIndex: 2,
        explanation:
          'La temperatura modifica la distribución de probabilidad del siguiente token. Con temperatura 0, el modelo siempre elige el token más probable (reproducible y conservador). Con temperatura alta, tokens menos probables tienen más chance de ser elegidos, generando respuestas más variadas y creativas, pero también más propensas a errores.',
      },
      {
        question: '¿Por qué el chunking es crítico en un sistema RAG?',
        options: [
          'Los LLMs solo pueden procesar texto de menos de 100 tokens',
          'Porque afecta directamente qué información se recupera: chunks mal diseñados pierden contexto o añaden ruido, degradando la calidad de las respuestas',
          'Para reducir el costo de almacenamiento en la vector database',
          'Solo importa para documentos en idiomas distintos al inglés',
        ],
        correctIndex: 1,
        explanation:
          'El chunking determina la granularidad de lo que se indexa y recupera. Si los chunks son demasiado pequeños, pierden contexto (una oración sin su párrafo puede ser ambigua). Si son demasiado grandes, incluyen información irrelevante que confunde al LLM. La estrategia de chunking (tamaño, overlap, método) es una de las variables más impactantes en la calidad de un sistema RAG.',
      },
      {
        question: '¿Cuál es la diferencia entre few-shot y zero-shot prompting?',
        options: [
          'Few-shot usa ejemplos en el prompt; zero-shot solo da la instrucción sin ejemplos',
          'Few-shot hace pocos intentos; zero-shot no intenta si no está seguro',
          'Zero-shot es más caro que few-shot',
          'Son términos equivalentes',
        ],
        correctIndex: 0,
        explanation:
          'Zero-shot: solo le das la instrucción al modelo sin ejemplos ("clasifica este texto como positivo o negativo"). Few-shot: incluyes ejemplos de input → output en el prompt ("texto: Gran película → positivo. texto: Malísima → negativo. texto: X → ?"). Few-shot mejora significativamente la calidad cuando el modelo no entiende bien la tarea o el formato esperado.',
      },
      {
        question: '¿Por qué se necesitan vector databases para RAG a escala?',
        options: [
          'Las bases de datos relacionales no pueden almacenar vectores',
          'Porque la búsqueda por similitud en millones de vectores requiere algoritmos ANN (Approximate Nearest Neighbor) optimizados que las DBs tradicionales no tienen',
          'Por razones de seguridad',
          'Solo se necesitan para más de 1 billón de documentos',
        ],
        correctIndex: 1,
        explanation:
          'Buscar los K vectores más similares a una query entre millones de vectores con búsqueda exacta es O(n) y prohibitivamente lento. Los vector databases implementan algoritmos ANN (HNSW, IVF) que encuentran los vecinos más cercanos de forma aproximada pero en O(log n), con un trade-off controlable entre velocidad y precisión. pgvector permite hacer esto en PostgreSQL para volúmenes menores.',
      },
    ],
    exercises: [
      {
        title: 'Chatbot RAG sobre documentación propia',
        difficulty: 'medium',
        description:
          'Construye un chatbot que responda preguntas sobre la documentación de un framework (usa los docs de Next.js o NestJS descargados localmente). Implementa: 1) Script de indexación que chunca los docs, genera embeddings y los almacena en Qdrant. 2) API de chat con streaming que implementa RAG. 3) UI simple con React que muestra la respuesta en streaming y las fuentes usadas. Evalúa la calidad con 10 preguntas de prueba.',
        hints: [
          'Usa @anthropic-ai/sdk para Claude y text-embedding-3-small de OpenAI para embeddings',
          'Chunking: divide por secciones (## headings) con overlap de 1 párrafo',
          'Incluye en el payload del vector: url, sección, texto completo del chunk',
          'Muestra al usuario las fuentes como referencias clicables al final de la respuesta',
        ],
        tags: ['RAG', 'Claude API', 'Streaming'],
      },
      {
        title: 'Agente con herramientas (Tool Use)',
        difficulty: 'hard',
        description:
          'Implementa un agente de AI con Claude que pueda: buscar en Wikipedia, hacer cálculos matemáticos, y consultar el clima actual de una ciudad. El agente debe razonar qué herramienta usar, ejecutarla, y usar el resultado para responder. Implementa el loop agentic completo: LLM decide → ejecuta herramienta → observa resultado → LLM decide nuevamente. Limita el máximo de iteraciones y maneja errores de herramientas.',
        hints: [
          'Define las herramientas con el formato de tool_use de la API de Anthropic',
          'El loop: mientras la respuesta tenga tool_use blocks, ejecuta las herramientas y envía los tool_result de vuelta',
          'Usa stop_reason === "end_turn" para detectar cuando el agente terminó',
          'Implementa un máximo de 5 iteraciones para evitar loops infinitos',
        ],
        tags: ['Agentes', 'Tool Use', 'Claude API'],
      },
    ],
  },
  {
    slug: 'design-patterns',
    title: 'Patrones de Diseño',
    shortDescription: 'Soluciones reutilizables a problemas recurrentes en diseño de software orientado a objetos.',
    icon: 'pattern',
    color: 'text-amber-400',
    category: 'architecture',
    concepts: [
      {
        title: 'Singleton',
        description:
          '¿Por qué se usa? Algunos recursos deben existir una sola vez en toda la aplicación: la conexión a la base de datos, el logger, el registry de configuración. Si cada módulo creara su propia instancia, tendríamos múltiples conexiones innecesarias, configuraciones divergentes, y consumo excesivo de recursos. Singleton garantiza que hay exactamente una instancia y provee un punto de acceso global. El problema real de Singleton: hace el código difícil de testear (no puedes reemplazarlo fácilmente por un mock) y puede ser un anti-pattern disfrazado de global. Alternativa moderna: usar DI (como en NestJS) que gestiona el ciclo de vida de singletons por ti.',
        tags: ['Creacional', 'Instancias'],
      },
      {
        title: 'Factory Method',
        description:
          '¿Por qué se usa? A veces no sabes en tiempo de compilación qué tipo exacto de objeto necesitas crear, o quieres que las subclases decidan qué instanciar. Ejemplo real: un sistema de pagos que necesita crear diferentes procesadores (Stripe, PayPal, MercadoPago) según la región del usuario. Sin Factory, el código de creación está hardcodeado con if/else en múltiples lugares. Con Factory Method, la lógica de creación está encapsulada y agregar un nuevo proveedor solo requiere crear una nueva clase y registrarla, sin tocar el código existente (Open/Closed principle).',
        tags: ['Creacional', 'Extensibilidad'],
      },
      {
        title: 'Observer',
        description:
          '¿Por qué se usa? Cuando múltiples partes del sistema necesitan reaccionar a un evento pero no queremos acoplarlas directamente. Ejemplo real: cuando un usuario completa una compra, necesitas: enviar email, actualizar el inventario, registrar en analíticas, y notificar al vendedor. Si el OrderService llama directamente a EmailService, InventoryService, etc., están acoplados: cambiar uno afecta al otro. Observer desacopla: OrderService emite un evento "OrderCompleted" y los otros servicios se suscriben. Agregar una nueva reacción solo requiere crear un nuevo subscriber, sin tocar OrderService.',
        tags: ['Comportamiento', 'Eventos', 'Desacoplamiento'],
      },
      {
        title: 'Strategy',
        description:
          '¿Por qué se usa? Cuando una operación puede hacerse de múltiples maneras y quieres poder cambiar el algoritmo en runtime o configurarlo sin cambiar el código del cliente. Ejemplo real: un sistema de sorting que necesita diferentes algoritmos según el tamaño del dataset; un compresor que puede usar gzip, brotli, o zstd; un router de pagos que usa diferentes gateways. Sin Strategy, el código está lleno de if/else que crecen con cada nueva variante. Con Strategy, cada algoritmo es una clase separada intercambiable. Nuevo algoritmo = nueva clase, sin modificar el código existente.',
        tags: ['Comportamiento', 'Algoritmos'],
      },
      {
        title: 'Decorator',
        description:
          '¿Por qué se usa? La herencia tiene un problema: agrega comportamiento en tiempo de compilación y genera una explosión combinatoria de subclases. Si quieres una conexión con logging, otra con caché, y otra con ambas, necesitarías 3 subclases. Decorator añade responsabilidades en runtime: envuelves el objeto con capas que añaden funcionalidad. Ejemplo real: middleware en Express/NestJS es exactamente el patrón Decorator, las Promises con `.then()` también. Los streams de Node.js (Transform) son otro ejemplo. Úsalo cuando quieres funcionalidad composable y configurable sin explosión de subclases.',
        tags: ['Estructural', 'Composición'],
      },
      {
        title: 'Repository',
        description:
          '¿Por qué se usa? El acceso a datos (SQL, NoSQL, APIs) no debería estar mezclado con la lógica de negocio. Si tu Service llama directamente a `prisma.user.findMany()`, dos problemas: la lógica de negocio está acoplada a Prisma (cambiar de ORM requiere tocar el Service), y no puedes testear el Service sin una base de datos real. Repository crea una abstracción: el Service habla con `UserRepository` (una interfaz), y la implementación concreta con Prisma es intercambiable. Los tests pueden usar una implementación in-memory. La lógica de queries SQL está encapsulada en un solo lugar.',
        tags: ['Arquitectural', 'Testing', 'Data Access'],
      },
      {
        title: 'Adapter',
        description:
          '¿Por qué se usa? Frecuentemente necesitas integrar librerías de terceros o APIs externas que tienen interfaces incompatibles con tu código. Si llamas directamente a la API de Stripe en 20 lugares, cuando Stripe cambia su API o quieres cambiar a Braintree, debes modificar los 20 lugares. El Adapter envuelve la interfaz incompatible detrás de una interfaz que controlas. Tu código usa `PaymentGateway`, el Adapter traduce entre tu interfaz y la de Stripe. Cambiar de proveedor = cambiar el adapter, no el código de negocio. También permite testear con un adapter fake.',
        tags: ['Estructural', 'Integración', 'Testing'],
      },
      {
        title: 'Command',
        description:
          '¿Por qué se usa? Cuando necesitas encapsular una operación como un objeto, lo que habilita: undo/redo (guardar los comandos ejecutados y revertirlos), queues de operaciones (poner comandos en una cola para ejecutar más tarde), logging de operaciones, y transacciones. Ejemplo real: en un editor de texto, cada acción (escribir, borrar, formatear) es un Command con execute() y undo(). En una arquitectura de microservicios, un Command puede encolarse en RabbitMQ y ejecutarse asincrónicamente. También es la base de las arquitecturas CQRS.',
        tags: ['Comportamiento', 'Undo/Redo', 'Queues'],
      },
    ],
    useCases: [
      {
        title: 'Extensibilidad sin romper código existente',
        description:
          'Factory Method, Strategy y Observer permiten agregar nueva funcionalidad (nuevos tipos, nuevos algoritmos, nuevos handlers de eventos) sin modificar el código existente (Open/Closed Principle). Esto es crítico en sistemas en producción donde cambiar código existente tiene riesgo de regresiones.',
      },
      {
        title: 'Testabilidad e inyección de dependencias',
        description:
          'Repository, Adapter y Strategy permiten reemplazar implementaciones reales por mocks en tests. Si tu código depende de abstracciones (interfaces) en lugar de implementaciones concretas, los tests pueden inyectar versiones in-memory o mock sin necesitar bases de datos reales ni APIs externas.',
      },
      {
        title: 'Arquitecturas event-driven y desacoplamiento',
        description:
          'Observer y Command son la base de arquitecturas event-driven. Los sistemas que procesan pedidos, notificaciones, y flujos de trabajo complejos usan estos patrones para desacoplar productores de consumidores y hacer el sistema más flexible y resiliente.',
      },
    ],
    interviewQA: [
      {
        question: '¿Por qué usarías el patrón Strategy en lugar de if/else?',
        answer:
          'El problema con if/else para seleccionar comportamiento es que el código crece indefinidamente al agregar variantes, viola Open/Closed (debes modificar el bloque if para agregar una opción), y es difícil de testear (debes testear todos los branches juntos). Strategy encapsula cada algoritmo en una clase separada con la misma interfaz. Agregar un nuevo algoritmo es crear una clase nueva sin tocar las existentes. Cada estrategia es testeable en aislamiento. El contexto que las usa no necesita conocer la implementación específica.',
        difficulty: 'medium',
      },
      {
        question: '¿Cuál es la diferencia entre Decorator y Herencia?',
        answer:
          'La herencia añade comportamiento en tiempo de compilación y genera jerarquías rígidas. Si quieres un Coffee con Milk, otro con Sugar, y otro con ambos, necesitas 3 subclases (explosión combinatoria). Decorator añade comportamiento en runtime mediante composición: CoffeeWithMilk = new MilkDecorator(new Coffee()); CoffeeWithBoth = new SugarDecorator(new MilkDecorator(new Coffee())). La regla: "Favorecer composición sobre herencia". Decorator es composición en runtime, es más flexible que la herencia.',
        difficulty: 'medium',
      },
      {
        question: '¿Cuándo usarías el patrón Observer y cuál es su principal riesgo?',
        answer:
          'Observer cuando múltiples partes del sistema necesitan reaccionar a cambios en un objeto sin acoplamiento directo. Casos típicos: eventos de UI, propagación de cambios de estado, notificaciones post-proceso. El principal riesgo es la dificultad para razonar sobre el flujo: cuando un evento dispara observers que a su vez disparan otros eventos, el orden de ejecución puede ser difícil de predecir y debuggear. Otro riesgo: memory leaks si los observers no se unsuscriben correctamente (especialmente en componentes de UI que se destruyen).',
        difficulty: 'medium',
      },
      {
        question: '¿Por qué el Singleton es considerado a veces un anti-pattern?',
        answer:
          'El Singleton crea estado global implícito, lo que hace el código difícil de testear (los tests se afectan entre sí si comparten el singleton), viola el principio de Single Responsibility (la clase gestiona su propia instanciación además de su lógica), y crea acoplamiento oculto. La alternativa moderna es DI (Dependency Injection): el contenedor IoC gestiona el ciclo de vida y garantiza una sola instancia, pero esa instancia puede ser reemplazada en tests. En frameworks como NestJS, los providers son singletons gestionados por el contenedor, sin los problemas del Singleton clásico.',
        difficulty: 'hard',
      },
      {
        question: '¿Cómo el patrón Adapter mejora la mantenibilidad a largo plazo?',
        answer:
          'El Adapter aísla las dependencias externas detrás de una interfaz que controlas. Los beneficios a largo plazo: 1) Puedes cambiar de proveedor (Stripe → Braintree) modificando solo el Adapter, sin tocar el código de negocio. 2) Las APIs externas cambian frecuentemente; el Adapter localiza esos cambios en un solo lugar. 3) Los tests pueden usar un Adapter fake/mock sin hacer llamadas HTTP reales. 4) El código de negocio no sabe nada del proveedor externo, está protegido de los detalles de implementación.',
        difficulty: 'medium',
      },
      {
        question: '¿Cuál es la diferencia entre Factory Method y Abstract Factory?',
        answer:
          'Factory Method define un método en la clase base que las subclases sobreescriben para crear un tipo de objeto. Abstract Factory crea familias de objetos relacionados: en lugar de un método, es una interfaz con múltiples métodos de creación (createButton(), createCheckbox()) que garantizan que los objetos creados son compatibles entre sí. Ejemplo: un Abstract Factory para UI themes (DarkTheme crea botones y checkboxes oscuros; LightTheme crea los mismos componentes en claro). Úsas Abstract Factory cuando los objetos deben ser usados juntos y deben ser del mismo "familia".',
        difficulty: 'hard',
      },
    ],
    codeExamples: [
      {
        title: 'Strategy: sistema de pagos extensible',
        description: 'Cómo usar Strategy para agregar nuevos métodos de pago sin modificar código existente.',
        language: 'typescript',
        code: `// La abstracción (Strategy interface)
interface PaymentStrategy {
  charge(amount: number, currency: string): Promise<{ transactionId: string }>;
  refund(transactionId: string): Promise<void>;
}

// Estrategias concretas — cada una encapsula una implementación
class StripePayment implements PaymentStrategy {
  constructor(private apiKey: string) {}

  async charge(amount: number, currency: string) {
    console.log(\`Charging \${amount} \${currency} via Stripe\`);
    // Lógica específica de Stripe...
    return { transactionId: 'stripe_' + Date.now() };
  }

  async refund(transactionId: string) {
    console.log(\`Refunding \${transactionId} via Stripe\`);
  }
}

class PayPalPayment implements PaymentStrategy {
  async charge(amount: number, currency: string) {
    console.log(\`Charging \${amount} \${currency} via PayPal\`);
    return { transactionId: 'paypal_' + Date.now() };
  }

  async refund(transactionId: string) {
    console.log(\`Refunding \${transactionId} via PayPal\`);
  }
}

// El contexto — no sabe qué estrategia usa internamente
class OrderProcessor {
  constructor(private paymentStrategy: PaymentStrategy) {}

  // Cambiar la estrategia en runtime si es necesario
  setPaymentStrategy(strategy: PaymentStrategy) {
    this.paymentStrategy = strategy;
  }

  async processOrder(orderId: string, amount: number) {
    console.log(\`Processing order \${orderId}\`);
    const result = await this.paymentStrategy.charge(amount, 'USD');
    console.log(\`Payment successful: \${result.transactionId}\`);
    return result;
  }
}

// Agregar MercadoPago = solo nueva clase, sin tocar OrderProcessor
class MercadoPagoPayment implements PaymentStrategy {
  async charge(amount: number, currency: string) {
    return { transactionId: 'mp_' + Date.now() };
  }
  async refund(transactionId: string) {}
}

// Factory para seleccionar la estrategia según la región
function getPaymentStrategy(region: string): PaymentStrategy {
  const strategies: Record<string, PaymentStrategy> = {
    US: new StripePayment(process.env.STRIPE_KEY!),
    EU: new PayPalPayment(),
    LATAM: new MercadoPagoPayment(),
  };
  return strategies[region] ?? strategies['US'];
}`,
      },
      {
        title: 'Observer: sistema de eventos de dominio',
        description: 'Observer tipado para propagar eventos de dominio sin acoplamiento directo.',
        language: 'typescript',
        code: `// Eventos de dominio
interface DomainEvent {
  readonly occurredAt: Date;
  readonly type: string;
}

class OrderPlacedEvent implements DomainEvent {
  readonly occurredAt = new Date();
  readonly type = 'OrderPlaced';
  constructor(
    public readonly orderId: string,
    public readonly userId: string,
    public readonly total: number,
  ) {}
}

// Observer interface
interface DomainEventHandler<T extends DomainEvent> {
  handle(event: T): Promise<void>;
}

// Event Bus (puede ser sincrónico o publicar a una cola)
class DomainEventBus {
  private handlers = new Map<string, DomainEventHandler<any>[]>();

  subscribe<T extends DomainEvent>(
    eventType: string,
    handler: DomainEventHandler<T>,
  ) {
    const existing = this.handlers.get(eventType) ?? [];
    this.handlers.set(eventType, [...existing, handler]);
  }

  async publish(event: DomainEvent): Promise<void> {
    const handlers = this.handlers.get(event.type) ?? [];
    // Ejecutar todos los handlers en paralelo
    await Promise.all(handlers.map(h => h.handle(event)));
  }
}

// Handlers concretos — totalmente independientes entre sí
class SendOrderConfirmationEmail implements DomainEventHandler<OrderPlacedEvent> {
  async handle(event: OrderPlacedEvent) {
    console.log(\`Sending confirmation email for order \${event.orderId}\`);
  }
}

class UpdateInventoryHandler implements DomainEventHandler<OrderPlacedEvent> {
  async handle(event: OrderPlacedEvent) {
    console.log(\`Updating inventory for order \${event.orderId}\`);
  }
}

// Uso: OrderService no sabe quién reacciona a sus eventos
const eventBus = new DomainEventBus();
eventBus.subscribe('OrderPlaced', new SendOrderConfirmationEmail());
eventBus.subscribe('OrderPlaced', new UpdateInventoryHandler());
// Agregar analytics: solo una línea, sin tocar OrderService

async function placeOrder(userId: string, items: any[]) {
  // Lógica de negocio...
  const order = { id: 'order_123', total: 99.99 };

  // Emitir el evento — no sabe quién lo maneja
  await eventBus.publish(new OrderPlacedEvent(order.id, userId, order.total));
  return order;
}`,
      },
    ],
    quiz: [
      {
        question: '¿Por qué el patrón Strategy es preferible a un switch/case largo?',
        options: [
          'Porque es más rápido en runtime',
          'Porque encapsula cada variante en su propia clase, cumpliendo Open/Closed: agregar una nueva opción es crear una clase nueva sin modificar el switch existente',
          'Porque usa menos memoria',
          'Solo es mejor si hay más de 10 casos',
        ],
        correctIndex: 1,
        explanation:
          'El switch/case viola Open/Closed: para agregar un nuevo algoritmo debes modificar el bloque switch, arriesgando romper los casos existentes. Strategy encapsula cada variante en una clase separada con la misma interfaz. Agregar un algoritmo = crear una clase nueva. El código que usa las estrategias no cambia. Además, cada estrategia es testeable en aislamiento.',
      },
      {
        question: '¿Qué problema específico resuelve el patrón Adapter?',
        options: [
          'Reduce el número de instancias creadas',
          'Permite que interfaces incompatibles trabajen juntas, aislando el código de dependencias externas',
          'Mejora el performance de las llamadas a APIs externas',
          'Garantiza que solo exista una instancia de un servicio externo',
        ],
        correctIndex: 1,
        explanation:
          'El Adapter resuelve la incompatibilidad entre interfaces: tu código espera una interfaz (PaymentGateway) pero la librería externa tiene otra (stripe.charges.create()). El Adapter traduce entre ambas, aislando tu código de los detalles de la implementación externa. El beneficio real: puedes cambiar de proveedor o mockear en tests sin tocar el código de negocio.',
      },
      {
        question: '¿Cuál es la principal ventaja del patrón Observer sobre llamadas directas?',
        options: [
          'Es más rápido que llamar métodos directamente',
          'Desacopla al productor de los consumidores: el productor no sabe quién reacciona a sus eventos, lo que permite agregar comportamiento sin modificarlo',
          'Garantiza que los eventos se procesen en orden',
          'Reduce el número de clases necesarias',
        ],
        correctIndex: 1,
        explanation:
          'Sin Observer, cuando ocurre algo importante debes llamar directamente a todos los servicios que necesitan saber: emailService.send(), inventoryService.update(), analytics.track(). Están todos acoplados. Con Observer, emites un evento y los interesados se suscriben. Agregar un nuevo comportamiento (ej: analytics) = nuevo subscriber, sin tocar el productor.',
      },
      {
        question: '¿Cuándo el patrón Decorator es preferible a la herencia?',
        options: [
          'Siempre, la herencia nunca debería usarse',
          'Cuando necesitas combinar comportamientos en runtime o tendrías una explosión combinatoria de subclases para representar todas las combinaciones posibles',
          'Solo cuando trabajas con I/O streams',
          'Cuando el lenguaje no soporta herencia múltiple',
        ],
        correctIndex: 1,
        explanation:
          'Si tienes Coffee y quieres versiones con Milk, Sugar, y Vanilla en todas las combinaciones, la herencia requiere 7 subclases (2³-1). Decorator permite: new Vanilla(new Sugar(new Milk(new Coffee()))). Cada feature es una clase que envuelve a otra. Las combinaciones son ilimitadas sin explosión de clases. Regla general: usa herencia para relaciones "es-un" y Decorator para "tiene-un comportamiento adicional".',
      },
      {
        question: '¿Qué hace que el patrón Repository sea esencial para el testing?',
        options: [
          'Hace las queries SQL más rápidas',
          'Abstrae el acceso a datos detrás de una interfaz, permitiendo reemplazar la implementación real por un mock in-memory en tests sin necesitar una base de datos real',
          'Automatiza la creación de tests',
          'Solo es útil en aplicaciones muy grandes',
        ],
        correctIndex: 1,
        explanation:
          'Si tu lógica de negocio llama directamente a Prisma, los tests necesitan una DB real. Con Repository, la lógica depende de una interfaz (UserRepository), y en tests inyectas una implementación in-memory que no necesita DB. Los tests son más rápidos, no requieren setup de DB, y son más confiables (no fallan por problemas de conectividad). Es un ejemplo directo del principio D de SOLID (Dependency Inversion).',
      },
    ],
    exercises: [
      {
        title: 'Sistema de notificaciones con Observer y Strategy',
        difficulty: 'medium',
        description:
          'Implementa un sistema de notificaciones donde: los eventos de dominio (UserRegistered, OrderPlaced, PaymentFailed) usan Observer para notificar a múltiples canales; los canales (Email, SMS, Push, Slack) implementan Strategy para que sean intercambiables; un NotificationPreference por usuario decide qué canales recibe para cada tipo de evento. La solución debe ser extensible: agregar un nuevo canal o un nuevo tipo de evento no debe requerir modificar el código existente.',
        hints: [
          'DomainEventBus despacha eventos; NotificationService se suscribe a los eventos relevantes',
          'NotificationChannel es la Strategy interface: send(recipient, message)',
          'UserNotificationPreferences mapea event types a arrays de canales preferidos',
          'Usa @Injectable y DI de NestJS o un contenedor simple para inyectar los canales',
        ],
        tags: ['Observer', 'Strategy', 'Extensibilidad'],
      },
      {
        title: 'Query Builder con Builder Pattern',
        difficulty: 'hard',
        description:
          'Implementa un Query Builder type-safe para SQL en TypeScript usando el patrón Builder. Debe soportar: select() con columnas específicas, from(), where() con condiciones, orderBy(), limit(), offset(). El Builder debe ser fluent (cada método retorna this). Al llamar build() genera el SQL string y los parámetros. Importante: usa TypeScript generics para que el tipo de retorno de select() sea type-safe según las columnas elegidas.',
        hints: [
          'class QueryBuilder<T extends Record<string, unknown>> con métodos que retornan this',
          'Acumula las partes del query en arrays internos',
          'build() concatena las partes en orden correcto: SELECT ... FROM ... WHERE ... ORDER BY ... LIMIT ...',
          'Para type-safety en select: select<K extends keyof T>(...cols: K[]) retorna QueryBuilder<Pick<T, K>>',
        ],
        tags: ['Builder', 'TypeScript', 'SQL'],
      },
    ],
  },
];
